/* vim:set expandtab ff=unix fenc=utf-8 ts=4 */
/*!
 * lib.js
 */
/* global console, window */

(function (w) {
  'use strict'

  const $ = require('jquery')
  const BigDecimal = require('big.js')
  const d = w.document
  // const debug = lo.noop;
  const debug = require('debug')('client')
  const lo = require('lodash')

  if (lo.isUndefined(lo.isDefined)) {
    lo.isDefined = function (v) {
      return (typeof v) !== 'undefined'
    }
  }

  /**
   * @typedef {object} DeviceIncrementDuration
   * @property {number} duration
   * @property {number} increment
   * @property {number} price
   */

  /**
   * @typedef {object} IncrementPrice
   * @property {number} increment
   * @property {number} price
   */

  /**
   */
  class DeviceEditDryer {
    /**
     * @param {HTMLFormElement} fm
     * @throws {TypeError} Throws `TypeError` if `fm` is not an
     *     `HTMLFormElement`, or when it does not contain `duration`,
     *     `increment` or `price` fields.
     */
    constructor (fm) {
      if (!(fm instanceof HTMLFormElement)) {
        throw TypeError('`fm` is not a form')
      }

      /* Initialize fields */
      this.duration =
        fm.querySelector('.sebco-deviceedit-device-form-cycle-duration')

      this.increment = fm.querySelector('.sebco-deviceedit-device-form-coin-1')
      this.price = fm.querySelector('.sebco-deviceedit-device-form-price')

      this.topOff =
        fm.querySelector('.sebco-deviceedit-device-form-extended-cycle-price');

      ['duration', 'increment', 'price', 'topOff'].forEach(k => {
        if (!this[k]) {
          throw new TypeError('`' + k + '` is not a form field', k)
        }
      })

      /* Default field values */
      Object.keys(DeviceEditDryer.defaultValue)
        .forEach(fieldname => {
          const field = this[fieldname]
          let v

          try {
            v = parseFloat(field.value)
          } catch (ignore) {
          }

          if (!Number.isFinite(v)) {
            field.value = DeviceEditDryer.defaultValue[fieldname]
          }

          field.dataset.previousValue = field.value
        })

      /* Field constraints */
      this.constraint = {}

      Object.keys(DeviceEditDryer.defaultConstraint)
        .forEach(fieldname => {
          const constraint = this.constraint[fieldname] = {}
          const defaults = DeviceEditDryer.defaultConstraint[fieldname]
          const field = this[fieldname]

          Object.keys(defaults)
            .forEach(prop => {
              const n = parseFloat(field[prop])

              constraint[prop] = (Number.isFinite(n))
                ? n
                : defaults[prop]

              field[prop] = constraint[prop]
            })
        })

      this.price.step = this.constraint.price.step = this.increment.value

      /* Field event handlers */
      this.duration.addEventListener(
        'input',
        lo.debounce(this.handleDurationInput.bind(this), 100)
      )

      this.increment
        .addEventListener('input', this.handleIncrementInput.bind(this))

      this.price.addEventListener(
        'input',
        lo.debounce(this.handlePriceInput.bind(this), 100)
      )
    }

    /**
     * @params {number} value
     * @params {number} max
     * @private
     * @returns {number}
     */
    clampMax (value, max) {
      if (value > max) {
        return max
      }

      return value
    }

    /**
     * @params {number} value
     * @params {number} min
     * @private
     * @returns {number}
     */
    clampMin (value, min) {
      if (value < min) {
        return min
      }

      return value
    }

    /**
     * @params {number} value
     * @params {number} min
     * @params {number} max
     * @private
     * @returns {number}
     */
    clampToRange (value, min, max) {
      return this.clampMin(this.clampMax(value, max), min)
    }

    /**
     * @params {number} low
     * @params {number} high
     * @private
     * @returns {number}
     */
    computeDivisor (low, high) {
      if (low <= 0) {
        return 1
      }

      const divisor = new BigDecimal(high).div(low).round().toNumber()

      return (divisor < 1)
        ? 1
        : divisor
    }

    /**
     * @param {number} divisor
     * @param {number} duration
     * @private
     * @returns {boolean}
     */
    decrementDuration (divisor, duration) {
      const constraint = this.constraint
      let minutesPerCoin = parseInt(duration / divisor, 10)
      let computedDuration = minutesPerCoin * divisor

      if (computedDuration > duration) {
        debug('computedDuration: ' + computedDuration + ', duration: ' +
          duration)
        minutesPerCoin = parseInt(duration / divisor, 10) - 1
        computedDuration = minutesPerCoin * divisor
      }

      debug('computedDuration: ' + computedDuration + ', duration: ' + duration)

      if (computedDuration >= constraint.duration.min) {
        debug('computedDuration >= constraint.duration.min')
        this.duration.dataset.minutesPerCoin = minutesPerCoin
        this.duration.dataset.previousValue = computedDuration
        this.duration.value = computedDuration
        return true
      }

      return false
    }

    /**
     * @param {Event} ev
     * @private
     */
    handleDurationInput (ev) {
      const el = (!ev.currentTarget)
        ? ev.target
        : ev.currentTarget

      const o = {
        duration: parseInt(el.value, 10),
        increment: parseFloat(this.increment.value),
        price: parseFloat(this.price.value)
      }

      Object.keys(o)
        .filter(k => !Number.isFinite(o[k]))
        .forEach(k => {
          o[k] = 0
        })

      this.updateByDuration(o)
    }

    /**
     * @param {Event} ev
     * @private
     */
    handleIncrementInput (ev) {
      const el = (!ev.currentTarget)
        ? ev.target
        : ev.currentTarget

      this.topOff.value =
        this.price.step =
        this.constraint.price.step =
        parseFloat(el.value)

      this.price.dispatchEvent(new Event('input'))
    }

    /**
     * @param {Event} ev
     * @private
     */
    handlePriceInput (ev) {
      debug('handlePriceInput fired')

      const el = (!ev.currentTarget)
        ? ev.target
        : ev.currentTarget

      debug(el.value)
      const v = parseFloat(el.value)

      if (v > this.constraint.price.max) {
        el.value = this.constraint.price.max
        return this.handlePriceInput(ev)
      }

      if (v < this.constraint.price.min) {
        el.value = this.constraint.price.min
        return this.handlePriceInput(ev)
      }

      const o = {
        duration: parseInt(this.duration.value, 10),
        increment: parseFloat(this.increment.value),
        price: parseFloat(el.value)
      }

      Object.keys(o)
        .filter(k => !Number.isFinite(o[k]))
        .forEach(k => {
          o[k] = 0
        })

      this.updateByPrice(o)
    }

    /**
     * @param {number} divisor
     * @param {number} duration
     * @private
     * @returns {boolean}
     */
    incrementDuration (divisor, duration) {
      const constraint = this.constraint
      let minutesPerCoin = parseInt(duration / divisor, 10) + 1
      let computedDuration = minutesPerCoin * divisor

      debug('computedDuration: ' + computedDuration + ', duration: ' + duration)

      if (computedDuration <= constraint.duration.max) {
        debug('computedDuration <= constraint.duration.max')
        this.duration.dataset.minutesPerCoin = minutesPerCoin
        this.duration.dataset.previousValue = computedDuration
        this.duration.value = computedDuration
        return true
      }

      minutesPerCoin = parseInt(duration / divisor, 10)
      computedDuration = minutesPerCoin * divisor

      if (
        computedDuration === parseInt(this.duration.dataset.previousValue, 10)
      ) {
        debug('computedDuration === this.duration.dataset.previousValue')
        this.duration.dataset.minutesPerCoin = minutesPerCoin
        this.duration.dataset.previousValue = computedDuration
        this.duration.value = computedDuration
        return true
      }

      return false
    }

    /**
     * @param {DeviceIncrementDuration} o
     * @private
     */
    updateByDuration (o) {
      debug('updateByDuration fired')

      const { constraint } = this
      const duration = this.clampMin(o.duration, constraint.duration.min)
      const { increment, price } = o
      const divisor = this.computeDivisor(increment, price)
      const minutesPerCoin = parseInt(duration / divisor, 10)

      debug('minutesPerCoin: ' + minutesPerCoin)

      const computedDuration = minutesPerCoin * divisor

      if (computedDuration === duration) {
        debug('computedDuration === duration')
        this.duration.dataset.minutesPerCoin = minutesPerCoin
        this.duration.dataset.previousValue = computedDuration
        return
      }

      if (computedDuration > duration) {
        debug('computedDuration > duration')

        if (this.decrementDuration(divisor, duration)) {
          return
        }

        if (this.incrementDuration(divisor, duration)) {
          return
        }
      }

      if (computedDuration < duration) {
        debug('computedDuration < duration')

        const increase =
          duration > parseInt(this.duration.dataset.previousValue, 10)

        debug(increase)

        if (increase) {
          if (this.incrementDuration(divisor, duration)) {
            return
          }

          this.decrementDuration(divisor, duration)
        } else {
          if (this.decrementDuration(divisor, duration)) {
            return
          }

          this.incrementDuration(divisor, duration)
        }
      }
    }

    /**
     * @param {DeviceIncrementDuration} o
     * @private
     */
    updateByPrice (o) {
      const { constraint: straint } = this

      const duration = this.clampToRange(
        o.duration,
        straint.duration.min,
        straint.duration.max
      )

      const { increment } = o

      const price =
        this.clampToRange(o.price, straint.price.min, straint.price.max)

      if (price === 0) {
        return
      }

      const divisor = this.computeDivisor(increment, price)

      if (divisor > straint.duration.max) {
        o.price = price - increment
        return this.updateByPrice(o)
      }

      const computedPrice = new BigDecimal(increment)
        .times(divisor)
        .round(2)
        .toNumber()

      const minutesPerCoin = new BigDecimal(duration)
        .div(divisor)
        .round()
        .toNumber()

      if (minutesPerCoin < 1) {
        o.duration = divisor
        return this.updateByPrice(o)
      }

      const computedDuration = minutesPerCoin * divisor

      if (
        this.validate({
          duration: computedDuration,
          increment,
          price: computedPrice
        })
      ) {
        this.price.value = parseFloat(computedPrice)
        this.duration.value = computedDuration
        return
      }

      if (computedPrice > straint.price.max) {
        o.price = computedPrice - increment
        return this.updateByPrice(o)
      }

      if (computedPrice < straint.price.min) {
        o.price = computedPrice + increment
        return this.updateByPrice(o)
      }

      if (computedDuration > straint.duration.max) {
        o.price = computedPrice - increment
        return this.updateByPrice(o)
      }

      if (computedDuration < straint.duration.min) {
        o.price = computedPrice + increment
        return this.updateByPrice(o)
      }

      this.price.value = parseFloat(computedPrice)
      this.duration.value = computedDuration
    }

    /**
     * @param {DeviceIncrementDuration} o
     * @private
     * @returns {boolean}
     */
    validate (o) {
      if (!lo.isObjectLike(o)) {
        return false
      }

      const { constraint } = this
      const durationConstraint = constraint.duration
      const priceConstraint = constraint.price

      return (
        Number.isFinite(o.duration) &&
        Number.isFinite(o.price) &&
        (o.duration > durationConstraint.min) &&
        (o.duration < durationConstraint.max) &&
        (o.price > priceConstraint.min) &&
        (o.price < priceConstraint.max)
      )
    }
  }

  /**
   * @const
   * @private
   * @static
   */
  DeviceEditDryer.defaultConstraint = Object.freeze({
    duration: {
      max: 75,
      min: 5,
      step: 1
    },
    price: {
      max: 9.95,
      min: 0,
      step: 0.25
    }
  })

  /**
   * @const
   * @private
   * @static
   */
  DeviceEditDryer.defaultValue = Object.freeze({
    duration: 8,
    increment: 0.25,
    price: 0.25
  })

  /**
   */
  class DeviceEditWasher {
    /**
     * @param {HTMLFormElement} fm
     * @throws {TypeError} Throws `TypeError` if `fm` is not an
     *     `HTMLFormElement`, or when it does not contain `duration`,
     *     `increment` or `price` fields.
     */
    constructor (fm) {
      if (!(fm instanceof HTMLFormElement)) {
        throw TypeError('`fm` is not a form')
      }

      /* Initialize fields */
      this.increment = fm.querySelector('.sebco-deviceedit-device-form-coin-1')
      this.price = fm.querySelector('.sebco-deviceedit-device-form-price')

      this.topOff =
        fm.querySelector('.sebco-deviceedit-device-form-extended-cycle-price');

      ['increment', 'price', 'topOff'].forEach(k => {
        if (!this[k]) {
          throw new TypeError('`' + k + '` is not a form field')
        }
      })

      /* Default field values */
      Object.keys(DeviceEditWasher.defaultValue)
        .forEach(fieldname => {
          const field = this[fieldname]
          let v

          try {
            v = parseFloat(field.value)
          } catch (ignore) {
          }

          if (!Number.isFinite(v)) {
            field.value = DeviceEditWasher.defaultValue[fieldname]
          }

          field.dataset.previousValue = field.value
        })

      /* Field constraints */
      this.constraint = {}

      Object.keys(DeviceEditWasher.defaultConstraint)
        .forEach(fieldname => {
          const constraint = this.constraint[fieldname] = {}
          const defaults = DeviceEditWasher.defaultConstraint[fieldname]
          const field = this[fieldname]

          Object.keys(defaults)
            .forEach(prop => {
              const n = parseFloat(field[prop])

              constraint[prop] = (Number.isFinite(n))
                ? n
                : defaults[prop]

              field[prop] = constraint[prop]
            })
        })

      this.topOff.step =
        this.constraint.topOff.step =
        this.price.step =
        this.constraint.price.step =
        this.increment.value

      /* Field event handlers */
      this.increment
        .addEventListener('input', this.handleIncrementInput.bind(this))

      this.price.addEventListener(
        'input',
        lo.debounce(this.handlePriceInput.bind(this), 200)
      )

      this.topOff.addEventListener(
        'input',
        lo.debounce(this.handleTopOffInput.bind(this), 200)
      )
    }

    /**
     * @params {number} value
     * @params {number} max
     * @private
     * @returns {number}
     */
    clampMax (value, max) {
      if (value > max) {
        return max
      }

      return value
    }

    /**
     * @params {number} value
     * @params {number} min
     * @private
     * @returns {number}
     */
    clampMin (value, min) {
      if (value < min) {
        return min
      }

      return value
    }

    /**
     * @params {number} value
     * @params {number} min
     * @params {number} max
     * @private
     * @returns {number}
     */
    clampToRange (value, min, max) {
      return this.clampMin(this.clampMax(value, max), min)
    }

    /**
     * @params {number} low
     * @params {number} high
     * @private
     * @returns {number}
     */
    computeDivisor (low, high) {
      if (low <= 0) {
        return 1
      }

      const divisor = new BigDecimal(high).div(low).round().toNumber()

      return (divisor < 1)
        ? 1
        : divisor
    }

    /**
     * @param {Event} ev
     * @private
     */
    handleIncrementInput (ev) {
      const el = (!ev.currentTarget)
        ? ev.target
        : ev.currentTarget

      this.topOff.step =
        this.price.step =
        this.constraint.price.step =
        parseFloat(el.value)

      this.topOff.dispatchEvent(new Event('input'))
      this.price.dispatchEvent(new Event('input'))
    }

    /**
     * @param {Event} ev
     * @private
     */
    handlePriceInput (ev) {
      debug('handlePriceInput fired')

      const el = (!ev.currentTarget)
        ? ev.target
        : ev.currentTarget

      debug(el.value)

      const v = parseFloat(el.value)

      if (v > this.constraint.price.max) {
        el.value = this.constraint.price.max
        return this.handlePriceInput(ev)
      }

      if (v < this.constraint.price.min) {
        el.value = this.constraint.price.min
        return this.handlePriceInput(ev)
      }

      const o = {
        increment: parseFloat(this.increment.value),
        price: parseFloat(el.value)
      }

      Object.keys(o)
        .filter(k => !Number.isFinite(o[k]))
        .forEach(k => {
          o[k] = 0
        })

      this.updateByPrice(o)
    }

    /**
     * @param {Event} ev
     * @private
     */
    handleTopOffInput (ev) {
      debug('handleTopOffInput fired')

      const el = (!ev.currentTarget)
        ? ev.target
        : ev.currentTarget

      const s = new BigDecimal(el.step).round(2).toNumber()

      if (s <= 0) {
        return
      }

      const v = new BigDecimal(el.value).round(2).toNumber()

      if (v <= 0) {
        return
      }

      if (v < s) {
        el.value = el.step
        return
      }

      let divisor = this.computeDivisor(s, v)

      console.log('divisor: ' + divisor)

      let computedTopOff = new BigDecimal(s).times(divisor).round(2).toNumber()

      console.log('step times divisor: ' + computedTopOff)
      console.log('current value: ' + v)

      if (computedTopOff === v) {
        return
      }

      if (computedTopOff > v) {
        if (computedTopOff > this.constraint.topOff.max) {
          divisor = this.computeDivisor(s, this.constraint.topOff.max)
          computedTopOff = new BigDecimal(s).times(divisor).round(2).toNumber()
        }

        el.value = computedTopOff

        return
      }

      const r = v - computedTopOff

      if (new BigDecimal(r).gt(new BigDecimal(s).div(2))) {
        computedTopOff = new BigDecimal(computedTopOff)
          .plus(el.step)
          .toNumber()
      }

      if (computedTopOff > this.constraint.topOff.max) {
        divisor = this.computeDivisor(s, this.constraint.topOff.max)
        computedTopOff = new BigDecimal(s).times(divisor).round(2).toNumber()
      }

      el.value = computedTopOff
    }

    /**
     * @param {IncrementPrice} o
     * @private
     */
    updateByPrice (o) {
      const { constraint: straint } = this

      const price =
        this.clampToRange(o.price, straint.price.min, straint.price.max)

      if (price === 0) {
        return
      }

      const { increment } = o
      const divisor = this.computeDivisor(increment, price)

      const computedPrice = new BigDecimal(increment)
        .times(divisor)
        .round(2)
        .toNumber()

      if (
        this.validate({
          increment,
          price: computedPrice
        })
      ) {
        this.price.value = parseFloat(computedPrice)
        return
      }

      if (computedPrice > straint.price.max) {
        o.price = computedPrice - increment
        return this.updateByPrice(o)
      }

      if (computedPrice < straint.price.min) {
        o.price = computedPrice + increment
        return this.updateByPrice(o)
      }

      this.price.value = parseFloat(computedPrice)
    }

    /**
     * @param {IncrementPrice} o
     * @private
     * @returns {boolean}
     */
    validate (o) {
      if (!lo.isObjectLike(o)) {
        return false
      }

      const constraint = this.constraint
      const priceConstraint = constraint.price

      return (
        Number.isFinite(o.price) &&
        (o.price > priceConstraint.min) &&
        (o.price < priceConstraint.max)
      )
    }
  }

  /**
   * @const
   * @private
   * @static
   */
  DeviceEditWasher.defaultConstraint = Object.freeze({
    price: {
      max: 9.95,
      min: 0,
      step: 0.25
    },
    topOff: {
      max: 9.95,
      min: 0,
      step: 0.25
    }
  })

  /**
   * @const
   * @private
   * @static
   */
  DeviceEditWasher.defaultValue = Object.freeze({
    increment: 0.25,
    price: 0.25,
    topOff: 0.25
  })

  /** @private */
  function SpinSpeedType () {
  }

  /**
   * @const
   * @private
   */
  SpinSpeedType.OFF = 0

  /**
   * @const
   * @private
   */
  SpinSpeedType.SLOW = 1

  /**
   * @const
   * @private
   */
  SpinSpeedType.MEDIUM = 2

  /**
   * @const
   * @private
   */
  SpinSpeedType.FAST = 3

  /**
   * @const
   * @private
   */
  SpinSpeedType.EXTRA_FAST = 4

  /**
   */
  class WCloudDeviceEditFormDryer52 {
    /**
     * @param {HTMLFormElement} form
     * @throws {ReferenceError | TypeError}
     */
    constructor (form) {
      if ((typeof form) === 'undefined') {
        throw new ReferenceError('`form` is undefined')
      }

      if (!(form instanceof HTMLFormElement)) {
        throw new TypeError('`form` is not an `HTMLFormElement`')
      }

      this.form = form
      this.handleInputDuration = this.handleInputDuration.bind(this)
      this.handleInputPrice = this.handleInputPrice.bind(this)

      form.querySelectorAll('.mmp-cycle')
        .forEach(cycle => {
          if ((typeof cycle) === 'undefined') {
            throw new ReferenceError('`cycle` is undefined')
          }

          if (!(cycle instanceof HTMLElement)) {
            throw new TypeError('`cycle` is not an `HTMLElement`')
          }

          const duration = cycle.querySelector('.mmp-duration')
          const price = cycle.querySelector('.mmp-price')

          duration.addEventListener(
            'input',
            lo.debounce(this.handleInputDuration, 600)
          )

          price.addEventListener(
            'input',
            lo.debounce(this.handleInputPrice, 600)
          )
        })
    }

    /**
     * @param {InputEvent} ev
     */
    handleInputDuration (ev) {
      const el = (ev.currentTarget)
        ? ev.currentTarget
        : ev.target

      if (!(el instanceof HTMLElement)) {
        throw new TypeError('`el` is not an `HTMLElement`')
      }

      this.update(el.closest('.mmp-cycle'))
    }

    /**
     * @param {InputEvent} ev
     */
    handleInputPrice (ev) {
      const el = (ev.currentTarget)
        ? ev.currentTarget
        : ev.target

      if (!(el instanceof HTMLElement)) {
        throw new TypeError('`el` is not an `HTMLElement`')
      }

      this.update(el.closest('.mmp-cycle'))
    }

    update (cycle) {
      const pel = cycle.querySelector('.mmp-price')
      const del = cycle.querySelector('.mmp-duration')

      if (!(pel instanceof HTMLInputElement)) {
        throw new TypeError('`pel` is not an `HTMLInputElement`')
      }

      if (!(del instanceof HTMLInputElement)) {
        throw new TypeError('`del` is not an `HTMLInputElement`')
      }

      if (pel.type !== 'number') {
        throw new TypeError('`pel.type` is not `number`')
      }

      if (del.type !== 'number') {
        throw new TypeError('`del.type` is not `number`')
      }

      const step = parseFloat(pel.step)
      const durationMax = parseInt(del.max, 10)
      const durationMin = parseInt(del.min, 10)
      const priceMax = parseFloat(pel.max)
      const priceMin = parseFloat(pel.min)
      let duration = parseInt(del.value, 10)
      let price = parseFloat(pel.value)

      if (Number.isFinite(priceMax) && (price > priceMax)) {
        price = priceMax
      }

      if (Number.isFinite(priceMin) && (price < priceMin)) {
        price = priceMin
      }

      if (Number.isFinite(durationMax) && (duration > durationMax)) {
        duration = durationMax
      }

      if (Number.isFinite(durationMin) && (duration < durationMin)) {
        duration = durationMin
      }

      const multiplier = new BigDecimal(price)
        .div(step)
        .round()
        .toNumber()

      price = new BigDecimal(step).times(multiplier).toNumber()

      if (duration < multiplier) {
        duration = multiplier
      }

      const minutesPerCoin = new BigDecimal(duration)
        .div(multiplier)
        .round()
        .toNumber()

      duration = minutesPerCoin * multiplier

      if (duration > durationMax) {
        pel.value = (price - step).toFixed(2)
        this.update(cycle)
        return
      }

      del.value = duration
      pel.value = price.toFixed(2)

      Array.from(this.form.querySelectorAll('.mmp-cycle'))
        .filter(c => c !== cycle)
        .forEach(c => {
          const d = c.querySelector('.mmp-duration')
          const p = c.querySelector('.mmp-price')

          if (d) {
            d.value = del.value
          }

          if (p) {
            p.value = pel.value
          }
        })
    }
  }

  /**
   */
  class WCloudDeviceSelectionFormDryer52 {
    /**
     * @param {HTMLFormElement} form
     * @throws {ReferenceError | TypeError}
     */
    constructor (form) {
      this.initialize(form)
    }

    /**
     * @param {number} duration
     * @param {number} extendedCyclePrice
     * @param {number} price
     * @private
     * @returns {number}
     */
    calculateExtendedCycleDuration (
      duration,
      extendedCyclePrice,
      price
    ) {
      if ((duration <= 0) || (price <= 0)) {
        return 0
      }

      const multiplier = extendedCyclePrice / price

      return Math.floor(duration * multiplier)
    }

    /**
     * @param {number} duration
     * @param {number} extendedCyclePrice
     * @param {number} maximumCycleDuration
     * @param {number} price
     * @private
     * @returns {number}
     */
    calculateMaximumDryerTopoffs (
      duration,
      extendedCycleDuration,
      maximumCycleDuration
    ) {
      if (extendedCycleDuration <= 0) {
        return 0
      }

      return Math.floor(
        (maximumCycleDuration - duration) / extendedCycleDuration
      )
    }

    /**
     * @param {Element} o
     * @private
     * @return {?Element}
     */
    findLabel (o) {
      if ((typeof o) === 'undefined') {
        throw new ReferenceError('`o` is undefined')
      }

      if (!(o instanceof Element)) {
        throw new TypeError('`o` is not an `Element`')
      }

      return (o.id)
        ? this.element.form.querySelector(`label[for="${o.id}"]`)
        : null
    }

    /**
     * @param {number} n
     * @private
     * @return {string}
     * @throws {ReferenceError | TypeError}
     */
    formatCurrency (n) {
      if ((typeof n) === 'undefined') {
        throw new ReferenceError('`n` is undefined')
      }

      if (!Number.isFinite(n)) {
        throw new TypeError('`n` is not a `number`')
      }

      if (this.formatter) {
        this.formatter.format(n)
      }

      return `$${n.toFixed(2)}`
    }

    /**
     * @param {Event} ev
     * @private
     */
    handleCycleInput (ev) {
      const el = ev.target
      const { dataset, value } = el
      const duration = this.duration = parseInt(dataset.duration, 10)

      this.cycle = parseInt(value, 10)
      this.extendedCycleCount = 0
      this.extendedCyclePrice = 0
      this.price = parseFloat(dataset.price)

      if (this.extendedCycle) {
        this.extendedCyclePrice = parseFloat(dataset.extendedCyclePrice)

        this.extendedCycleDuration = this.calculateExtendedCycleDuration(
          duration,
          this.extendedCyclePrice,
          this.price
        )

        this.maximumExtendedCycleCount = this.calculateMaximumDryerTopoffs(
          duration,
          this.extendedCycleDuration,
          this.maximumCycleDuration
        )

        console.warn({
          extendedCycleDuration: this.extendedCycleDuration,
          extendedCyclePrice: this.extendedCyclePrice,
          maximumExtendedCycleCount: this.maximumExtendedCycleCount
        })
      }

      this.selectedCycle = el
      this.updateState()
    }

    /**
     * @param {MouseEvent} ev
     * @private
     */
    handleTopOffClick (ev) {
      if (!(ev instanceof MouseEvent)) {
        throw new TypeError('ev is not a MouseEvent')
      }

      ev.preventDefault()

      switch (ev.currentTarget?.name) {
        case 'add':
          if (this.extendedCycleCount < this.maximumExtendedCycleCount) {
            this.extendedCycleCount += 1
          }
          break
        case 'subtract':
          if (this.extendedCycleCount > 0) {
            this.extendedCycleCount -= 1
          }
          break
        default:
          return
      }

      this.element.extendedCycleCount.value = this.extendedCycleCount
      this.updateState()
    }

    /**
     * @param {HTMLFormElement} form
     * @private
     * @throws {ReferenceError | TypeError}
     */
    initialize (form) {
      if ((typeof form) === 'undefined') {
        throw new TypeError('`form` is undefined')
      }

      if (!(form instanceof HTMLFormElement)) {
        throw new TypeError('`form` is not an `HTMLFormElement`')
      }

      const { dataset: formDataset } = form

      this.cycle = -1
      this.duration = 0
      this.extendedCycle = formDataset.extendedCycle === 'true'
      this.extendedCycleCount = 0
      this.extendedCycleDuration = 0
      this.extendedCyclePrice = 0

      this.formatter = (Intl)
        ? new Intl.NumberFormat('en-US', { currency: 'USD', style: 'currency' })
        : null

      this.maximumCycleDuration = parseInt(formDataset.maximumCycleDuration, 10)
      this.maximumExtendedCycleCount = 0
      this.price = 0
      this.selectedCycle = null

      const element = this.element = {
        cycle: Array.from(form.querySelectorAll('input[name="cycle"]')),
        extendedCycleCount: form.querySelector('.sebco-extended-cycle-count'),
        extendedCycleDuration:
          form.querySelector('.sebco-display-extended-cycle-duration'),
        extendedCyclePrice:
          form.querySelector('.sebco-display-extended-cycle-price'),
        duration: form.querySelector('.sebco-duration'),
        form,
        price: form.querySelector('.sebco-price'),
        submit: Array.from(form.querySelectorAll('[type="submit"]')),
        topOff: Array.from(
          form.querySelectorAll('button[name="add"], button[name="subtract"]')
        )
      }

      element.cycle
        .forEach(v => {
          v.addEventListener('input', this.handleCycleInput.bind(this))
        })

      if (this.extendedCycle) {
        element.topOff
          .forEach(v => {
            v.addEventListener('click', this.handleTopOffClick.bind(this))
          })
      }

      const t = element.cycle.find(v => (v.checked === true))

      if (t) {
        t.dispatchEvent(new CustomEvent('input'))
      }
    }

    /**
     * @private
     */
    updateState () {
      const {
        element,
        extendedCycle,
        extendedCycleCount,
        extendedCycleDuration,
        extendedCyclePrice,
        selectedCycle
      } = this

      if (!selectedCycle) {
        return
      }

      const selectedCycleDataset = selectedCycle.dataset
      const u = element.form.querySelector('.upgrades')

      if (u) {
        u.disabled = false
      }

      element.cycle
        .forEach(v => {
          const x = this.findLabel(v)

          if (x) {
            if (v.checked) {
              x.classList.add('selected')
            } else {
              x.classList.remove('selected')
            }
          }
        })

      element.submit
        .filter(v => v.disabled)
        .forEach(v => {
          v.disabled = false
        })

      let price = parseFloat(selectedCycleDataset.price)
      let duration = parseInt(selectedCycleDataset.duration, 10)

      if (extendedCycle) {
        price += (extendedCycleCount * extendedCyclePrice)
        duration += (extendedCycleCount * extendedCycleDuration)
      }

      element.price.innerText = this.formatCurrency(price)
      element.duration.innerText = duration
      element.extendedCycleDuration.innerText = extendedCycleDuration

      element.extendedCyclePrice.innerText =
        this.formatCurrency(extendedCyclePrice)
    }
  }

  /**
   */
  class WCloudDeviceSelectionFormWasher52 {
    /**
     * @param {HTMLFormElement} form
     * @throws {ReferenceError | TypeError}
     */
    constructor (form) {
      this.initialize(form)
    }

    /**
     * @param {Element} o
     * @private
     * @return {?Element}
     */
    findLabel (o) {
      if ((typeof o) === 'undefined') {
        throw new ReferenceError('`o` is undefined')
      }

      if (!(o instanceof Element)) {
        throw new TypeError('`o` is not an `Element`')
      }

      return (o.id)
        ? this.element.form.querySelector(`label[for="${o.id}"]`)
        : null
    }

    /**
     * @param {number} n
     * @private
     * @return {string}
     * @throws {ReferenceError | TypeError}
     */
    formatCurrency (n) {
      if ((typeof n) === 'undefined') {
        throw new ReferenceError('`n` is undefined')
      }

      if (!Number.isFinite(n)) {
        throw new TypeError('`n` is not a `number`')
      }

      if (this.formatter) {
        this.formatter.format(n)
      }

      return `$${n.toFixed(2)}`
    }

    /**
     * @param {Event} ev
     * @private
     */
    handleCycleInput (ev) {
      const el = ev.target
      const t = parseInt(el.dataset.extraRinse, 10)

      this.cycle = parseInt(el.value, 10)
      this.extraRinse = (Number.isFinite(t) && (t > 0))
      this.price = parseFloat(el.dataset.price)
      this.selectedCycle = el
      this.soilLevel = parseInt(el.dataset.soilLevel, 10)
      this.spinSpeed = parseInt(el.dataset.spinSpeed, 10)
      this.temperature = parseInt(el.dataset.temperature, 10)

      const { element } = this

      element.temperature
        .forEach(v => {
          if (this.temperature === parseInt(v.value, 10)) {
            v.checked = true
          }
        })

      /*
       * 2021-03-23 special case: it is currently not possible to select the
       * slow (low) spin speed option with the "normal" base cycle. This
       * behavior is true when interacting with the physical machine, and when
       * programming remotely via W-cloud. Disabling the low spin speed option
       * here. Assumes that the "normal" cycle is at the default (zero-ith)
       * position.
       */
      element.spinSpeed
        .forEach(v => {
          if (
            (this.cycle === 0) &&
            (SpinSpeedType.SLOW === parseInt(v.value, 10))
          ) {
            v.disabled = true

            const l = this.findLabel(v)

            if (l) {
              l.classList.add('disabled')
            }
          } else if (v.disabled) {
            v.disabled = false

            const l = this.findLabel(v)

            if (l) {
              l.classList.remove('disabled')
            }
          }
        })

      this.updateState()
    }

    /**
     * @param {Event} ev
     * @private
     */
    handleExtraRinseInput (ev) {
      this.extraRinse = ev.target.checked
      this.updateState()
    }

    /**
     * @param {Event} ev
     * @private
     */
    handleSoilLevelInput (ev) {
      const el = ev.target

      if (el.checked) {
        this.soilLevel = parseInt(el.value, 10)
      } else if (this.selectedCycle) {
        this.soilLevel = parseInt(this.selectedCycle.dataset.soilLevel, 10)
      } else {
        this.soilLevel = 0
      }

      this.updateState()
    }

    /**
     * @param {Event} ev
     * @private
     */
    handleSpinSpeedInput (ev) {
      const el = ev.target

      if (el.checked) {
        this.spinSpeed = parseInt(el.value, 10)
      } else if (this.selectedCycle) {
        this.spinSpeed = parseInt(this.selectedCycle.dataset.spinSpeed, 10)
      } else {
        this.spinSpeed = SpinSpeedType.MEDIUM
      }

      this.updateState()
    }

    /**
     * @param {Event} ev
     * @private
     */
    handleTemperatureInput (ev) {
      this.temperature = parseInt(ev.target.value, 10)
      this.updateState()
    }

    /**
     * @param {HTMLFormElement} form
     * @private
     * @throws {ReferenceError | TypeError}
     */
    initialize (form) {
      if ((typeof form) === 'undefined') {
        throw new TypeError('`form` is undefined')
      }

      if (!(form instanceof HTMLFormElement)) {
        throw new TypeError('`form` is not an `HTMLFormElement`')
      }

      this.cycle = -1
      this.extraRinse = false

      this.formatter = (Intl)
        ? new Intl.NumberFormat('en-US', { currency: 'USD', style: 'currency' })
        : null

      this.modelCode = parseInt(form.dataset.modelCode, 10)
      this.price = 0
      this.selectedCycle = null
      this.soilLevel = 0
      this.temperature = 0

      const element = this.element = {
        cycle: Array.from(form.querySelectorAll('input[name="cycle"]')),
        extraRinse: form.querySelector('input[name="extra_rinse"]'),
        form,
        price: form.querySelector('.sebco-price'),
        soilLevel: Array.from(form.querySelectorAll('input[name="soil_level"]')),
        spinSpeed: Array.from(form.querySelectorAll('input[name="spin_speed"]')),
        submit: Array.from(form.querySelectorAll('[type="submit"]')),
        temperature: Array
          .from(form.querySelectorAll('input[name="temperature"]'))
      }

      element.cycle
        .forEach(v => {
          v.addEventListener('input', this.handleCycleInput.bind(this))
        })

      if (element.extraRinse) {
        element.extraRinse
          .addEventListener('input', this.handleExtraRinseInput.bind(this))
      }

      element.soilLevel
        .forEach(v => {
          v.addEventListener('input', this.handleSoilLevelInput.bind(this))
        })

      element.spinSpeed
        .forEach(v => {
          v.addEventListener('input', this.handleSpinSpeedInput.bind(this))
        })

      element.temperature
        .forEach(v => {
          v.addEventListener('input', this.handleTemperatureInput.bind(this))
        })

      const t = element.cycle.find(v => (v.checked === true))

      if (t) {
        t.dispatchEvent(new CustomEvent('input'))
      }
    }

    /**
     * @private
     */
    updateState () {
      const { element, selectedCycle } = this

      if (!selectedCycle) {
        return
      }

      const selectedCycleDataset = selectedCycle.dataset

      element.form.querySelector('.upgrades').disabled = false

      element.cycle
        .forEach(v => {
          const x = this.findLabel(v)

          if (x) {
            if (v.checked) {
              x.classList.add('selected')
            } else {
              x.classList.remove('selected')
            }
          }
        })

      element.submit
        .filter(v => v.disabled)
        .forEach(v => {
          v.disabled = false
        })

      let base
      let price = parseFloat(selectedCycleDataset.price)
      let t = element.extraRinse
      let u

      if (t) {
        t.checked = (this.extraRinse)
        u = this.findLabel(t)

        if (t.checked) {
          u.classList.add('selected')
          base = (parseInt(selectedCycleDataset.extraRinse, 10) > 0)

          if (!base) {
            price += parseFloat(t.dataset.price)
          }
        } else {
          u.classList.remove('selected')
        }
      }

      t = 0
      base = parseInt(selectedCycleDataset.soilLevel, 10)

      element.soilLevel
        .forEach(v => {
          const soilLevel = parseInt(v.value, 10)
          const u = this.findLabel(v)

          v.checked = (this.soilLevel === soilLevel)

          if (v.checked) {
            if (soilLevel > base) {
              t = parseFloat(v.dataset.price)
            }

            if (u) {
              u.classList.add('selected')
            }
          } else {
            if (u) {
              u.classList.remove('selected')
            }
          }
        })

      price += t
      t = 0
      base = parseInt(selectedCycleDataset.spinSpeed, 10)

      element.spinSpeed
        .forEach(v => {
          const spinSpeed = parseInt(v.value, 10)
          const u = this.findLabel(v)

          v.checked = (this.spinSpeed === spinSpeed)

          if (v.checked) {
            if (spinSpeed > base) {
              t = parseFloat(v.dataset.price)
            }

            if (u) {
              u.classList.add('selected')
            }
          } else {
            if (u) {
              u.classList.remove('selected')
            }
          }
        })

      price += t
      base = parseInt(selectedCycleDataset.temperature, 10)
      t = 0

      element.temperature
        .forEach(v => {
          const u = this.findLabel(v)
          const x = parseInt(v.value, 10)

          if (this.temperature === x) {
            v.checked = true

            if (x > base) {
              t = parseFloat(v.dataset.price)
            }

            if (u) {
              u.classList.add('selected')
            }
          } else {
            v.checked = false

            if (u) {
              u.classList.remove('selected')
            }
          }
        })

      price += t
      this.price = price
      element.price.innerText = this.formatCurrency(this.price)
    }
  }

  w.jQuery = $
  require('./migrate-mute.js')
  require('jquery-migrate')
  require('velocity-animate')
  require('../../node_modules/jquery-ui-dist/jquery-ui')
  require('./jquery.creditCardValidator')
  require('virtual-keyboard')
  require('../../node_modules/bootstrap/js/dropdown')
  w.Handlebars = require('../../node_modules/handlebars/dist/handlebars')

  /*
   * Top-level namespace for custom methods/data.
   */
  class A {
    static ajaxPromise (o) {
      var def = $.Deferred()
      var opts = { dataType: 'json' }

      if (lo.isObjectLike(o)) {
        $.extend(opts, o)
      }

      opts.error = function (x, t, e) {
        var err = new Error(e)

        err.jqXHR = x
        err.textStatus = t
        err.errorThrown = e
        def.reject(err)
      }

      opts.success = function (data) {
        def.resolve(data)
      }

      $.ajax(opts)

      return def.promise()
    }

    static ajaxPromiseScript (url) {
      var def = $.Deferred()
      var opts = { dataType: 'script' }

      if (lo.isString(url)) {
        opts.url = url
      }

      opts.complete = function () {
        def.resolve()
      }

      $.ajax(opts)

      return def.promise()
    }

    /**
     * @param {string} message
     * @param {string | null} title
     * @param {string | null} extraClasses
     * @returns {JQuery<HTMLElement | null>}
     */
    static buildFlash (
      message,
      title = null,
      extraClasses = null
    ) {
      const tmpl = d.getElementById('template-sebco-flash-data')

      if (tmpl) {
        return $(
          w.Handlebars.compile(tmpl.innerHTML)({
            extraClasses,
            message,
            title
          })
        )
      }

      return null
    }

    static centerVertical (el) {
      var jq = $(el)
      var offset

      offset = A.parseInt((A.win.height() - jq.height()) / 2)

      if (isNaN(offset) || offset < 1) {
        return
      }

      offset -= (2 * offset)
      // jq.animate('scroll', {offset: offset});

      $('html, body')
        .animate({
          scrollTop: A.parseInt(jq.offset().top + offset) + 'px'
        })
    }

    /*
     * Creates a new DIV.sebco-screen-saver production and returns it as a
     * jQuery instance. If the body[use-screen-saver] is not (boolean) true,
     * this method returns null. If the template does not exist in the current
     * page, returns null. If the screen saver production does not contain any
     * .sebco-slide productions, returns null.
     */
    static createScreenSaver () {
      var body = $('body').first()
      var tmpl
      var screenSaver = null
      var useScreenSaver = body.data('useScreenSaver')

      if (!lo.isBoolean(useScreenSaver) || !useScreenSaver) {
        /* RETURNS */
        return screenSaver
      }

      tmpl = $('#template-sebco-screen-saver')

      if (tmpl.length > 0) {
        screenSaver = $(tmpl.html())

        screenSaver
          .hide()
          .find('.sebco-slide')
          .each(A.createScreenSaverSlide)
          .end()
          .data('isVisible', false)
          .data('timerId', null)
          .on('click', function () {
            A.hideScreenSaver()
            A.queueScreenSaver()
          })
      }

      if (screenSaver !== null &&
          screenSaver.find('.sebco-slide').length < 1) {
        screenSaver = null
      }

      return screenSaver
    }

    static createScreenSaverSlide (ignore, el) {
      var con = $(el)
      var t
      var type = con.data('type')
      var url = con.data('url')
      var v

      if (!lo.isString(url) || !lo.isString(type)) {
        con.remove()

        /* RETURNS */
        return
      }

      if (type.toLowerCase().indexOf('image') === 0) {
        /*
         * Pre-load image.
         */
        t = new Image()
        t.src = A.sanitizeUrl(url)

        con
          .addClass('sebco-slide-image')
          .css({ backgroundImage: 'url(' + A.sanitizeUrl(url) + ')' })
      } else if (type.toLowerCase().indexOf('video') === 0) {
        v = $(d.createElement('video'))
          .attr({
            height: A.parseInt(A.win.outerHeight()),
            preload: 'auto',
            src: A.sanitizeUrl(url),
            width: A.parseInt(A.win.outerWidth())
          })

        con
          .addClass('sebco-slide-video')
          .append(v)
      } else {
        con.remove()
      }
    }

    static currentTimeAutoUpdate () {
      var cte = A.generateCurrentTimeElement()
      var existing = $('.sebco-current-time-auto-update').get()

      $.each(existing, function (ignore, v) {
        var hour,
          old
          // replacement;

        old = $(v)

        if (cte.attr('datetime') === old.attr('datetime')) {
          return false
        }

        hour = cte.data('hour')

        if (hour !== old.data('hour')) {
          if (hour < 12) {
            $('.sebco-greeting-morning')
              .css({ display: 'block' })
              .prop('hidden', false)

            $('.sebco-greeting-afternoon')
              .css({ display: 'none' })
              .prop('hidden', false)

            $('.sebco-greeting-evening')
              .css({ display: 'none' })
              .prop('hidden', false)
          } else if (hour < 18) {
            $('.sebco-greeting-morning')
              .css({ display: 'none' })
              .prop('hidden', false)

            $('.sebco-greeting-afternoon')
              .css({ display: 'block' })
              .prop('hidden', false)

            $('.sebco-greeting-evening')
              .css({ display: 'none' })
              .prop('hidden', false)
          } else {
            $('.sebco-greeting-morning')
              .css({ display: 'none' })
              .prop('hidden', false)

            $('.sebco-greeting-afternoon')
              .css({ display: 'none' })
              .prop('hidden', false)

            $('.sebco-greeting-evening')
              .css({ display: 'block' })
              .prop('hidden', false)
          }
        }

        if (old.hasClass('sebco-transparent')) {
          cte.addClass('sebco-transparent')
        }

        old.replaceWith(cte)
        cte.removeClass('sebco-transparent')
      })
    }

    /*
     * Takes a string representation of a cookie and returns it as an object
     * with "key" and "value" fields.  The values of both fields are assumed to
     * be URI-encoded in the input, and will be decoded to plain-text and have
     * any leading and trailing whitespace removed.
     */
    static decodeCookie (s) {
      var cook = {}
      var rep = function (tok) {
        return $.trim(tok).replace(/\+/g, ' ')
      }
      var toks

      if (lo.isString(s)) {
        toks = $.trim(s).split('=')

        if (toks.length > 1) {
          cook.key = decodeURIComponent(rep(toks[0]))
          cook.value = decodeURIComponent(rep(toks[1]))
        }
      }

      return cook
    }

    /*
     * Loops through all properties of devicesA, and compares the property
     * values to devicesB. If a property exists in both devicesA and devicesB,
     * but the property value do not match, returns false. Otherwise, returns
     * true.
     *
     * If either devicesA or devicesB is null or not an object, returns true.
     *
     * If a property exists in devicesA, but not in devicesB, the property value
     * will not be checked for equality.
     */
    static deviceStatusesEqual (devicesA, devicesB) {
      var stat = true

      if (lo.isObjectLike(devicesA) && lo.isObjectLike(devicesB)) {
        $.each(devicesA, function (k) {
          if (
            lo.isDefined(devicesB[k]) &&
            (devicesA[k] !== devicesB[k])
          ) {
            debug(
              k +
                ' ' +
                devicesA[k] +
                ' changed to ' +
                devicesB[k]
            )

            stat = false

            /* RETURNS */
            return false
          }
        })
      }

      return stat
    }

    static dismissAllDismissibles () {
      Array.from(d.querySelectorAll('.alert-dismissable, .alert-dismissible'))
        .map(alrt => alrt.querySelector('.close'))
        .filter(btn => !!btn)
        .forEach(btn => {
          btn.click()
        })
    }

    static fadeOutAndRemove (o) {
      return o
        .animate(A.animateParamsFadeOut, 'easeOutCirc')
        .promise()
        .then(function () {
          return o.remove()
        })
    }

    static fadeOutAndSlideUpAndRemove (o) {
      return o
        .animate(A.animateParamsFadeOut, 'easeOutCirc')
        .promise()
        .then(function () {
          return o
            .animate(A.animateParamsSlideUp, 'easeOutCirc')
            .promise()
        })
        .then(function () {
          return o.remove()
        })
    }

    static findPositionByAddress (endpoint, address) {
      return A
        .ajaxPromise({
          data: { address },
          type: 'GET',
          url: endpoint
        })
        .then(A.normalizeJsonResponse)
        .then(function (o) {
          if (o.meta.status === 200 && lo.isObjectLike(o.response)) {
            /* RETURNS */
            return o.response
          }

          return null
        })
    }

    static generateCallbackName () {
      var callbackName

      do {
        callbackName = 'sebcoCallback' +
          Math.floor(Math.random() * 0xFFFFFFFF).toString(16)
      } while (lo.isFunction(w[callbackName]))

      return callbackName
    }

    static generateCurrentTimeElement () {
      var datetime = ''
      var hour
      var meridiem = 'AM'
      var now = new Date()
      var text = ''

      datetime += A.zeroPad(now.getUTCFullYear(), 4) +
        '-' +
        A.zeroPad(now.getUTCMonth() + 1) +
        '-' +
        A.zeroPad(now.getUTCDate()) +
        'T' +
        A.zeroPad(now.getUTCHours()) +
        ':' +
        A.zeroPad(now.getUTCMinutes()) +
        'Z'

      hour = now.getHours()

      if (hour > 11) {
        hour -= 12
        meridiem = 'PM'
      }

      if ($('body:first').data('isKiosk') === true &&
          $('html:first').hasClass('sebco-landing')) {
        meridiem = ' ' + meridiem
      }

      text += (hour === 0)
        ? 12
        : hour

      text += ':' +
        A.zeroPad(now.getMinutes()) +
        meridiem

      return $(d.createElement('time'))
        .addClass('sebco-current-time-auto-update')
        .attr('datetime', datetime)
        .data('hour', now.getHours())
        .text(text)
    }

    static generateId () {
      var v = ''

      do {
        v = 'gid-' + Math
          .floor(Math.random() * Number.MAX_SAFE_INTEGER)
          .toString(16)
      } while (d.getElementById(v) !== null)

      return v
    }

    /*
     * Returns all cookies as an array of objects.  Each returned object will
     * have the fields "key" and "value".
     */
    static getCookies () {
      var cooks
      var i = 0
      var j

      cooks = d.cookie.split('; ')
      j = cooks.length

      while (i < j) {
        cooks[i] = A.decodeCookie(cooks[i])
        i += 1
      }

      return cooks
    }

    /*
     * Sets the cookie named in "key" to the value specified in "val".  If "val"
     * is undefined or null, any existing cookie matching "name" will be
     * deleted.  An optional "options" argument may also be provided.  If
     * "options" is used, it is expected to be an object with the following
     * (optional) fields:
     *
     *     expires  The date and time on which this cookie will expire.  If
     *              defined, "expires" must be an instance of the Date object,
     *              or a number containin a millisecond offset of Midnight, 01
     *              Jan 1970, GMT.  If the Date/number value is in the past will
     *              cause a pre-existing cookie matching "name" to be deleted.
     *              The default is undefined, which causes this to be a session
     *              cookie.
     *     path     The URL path to which this cookie applies.  The default is
     *              undefined, which causes this cookie to apply to the path of
     *              the current page.
     *     domain   The domain name to which this cookie applies.  The default
     *              is undefined, which causes this cookie to apply to the
     *              fully-qualified domain name of the current page.
     *     secure   If set to any value (including falsy values), the cookie
     *              will only be sent if the current connection is secure.  The
     *              default is undefined, which causes the cookie to be sent
     *              over secure and unsecure connections.
     */
    static setCookie (key, val, options) {
      var buf
      var opts = lo.isObjectLike(options)
        ? $.extend({}, options)
        : {}

      if (lo.isDefined(key)) {
        buf = encodeURIComponent(key) + '='

        if (lo.isDefined(val)) {
          buf += encodeURIComponent(val)
        } else {
          opts.expires = -1
        }

        if (lo.isDefined(opts.expires)) {
          if (typeof opts.expires === 'number') {
            opts.expires = new Date(opts.expires)
          }

          if (lo.isDate(opts.expires)) {
            buf += '; expires=' +
              encodeURIComponent(opts.expires.toUTCString())
          }
        }

        if (lo.isDefined(opts.path)) {
          buf += '; path=' + encodeURIComponent(opts.path)
        }

        if (lo.isDefined(opts.domain)) {
          buf += '; domain=' + encodeURIComponent(opts.domain)
        }

        if (lo.isDefined(opts.secure)) {
          buf += '; secure'
        }

        d.cookie = buf
      }
    }

    static handleActiveErrorReportEnabledChange (e) {
      var control = $(e.currentTarget)
      var fm
      var options

      if (control.length < 1) {
        /* RETURNS */
        return
      }

      fm = control.closest('form')

      if (fm.length < 1) {
        /* RETURNS */
        return
      }

      options = {
        data: {
          enabled: control.is(':checked')
            ? 1
            : 0,
          name: fm.find('input[name="emailId"]').val()
        },
        dataType: 'json',
        type: 'POST',
        url: fm.data('endpoint')
      }

      A.ajaxPromise(options)
        .then(function (v) {
          debug(v)
          $(
            '#sebco-table-active-error-report-con, ' +
              '#sebco-active-error-report-ids-con'
          ).trigger('datachange')
        })
    }

    static handleDeviceEditCoin1Change (e) {
      var form
      var machineType
      var sel
      var val

      if (!e || lo.isUndefined(e.currentTarget)) {
        return
      }

      sel = e.currentTarget

      if (lo.isUndefined(sel.value)) {
        return
      }

      val = parseFloat(sel.value)

      if (!Number.isFinite(val)) {
        return
      }

      form = sel.closest('form')
      machineType = form.getAttribute('data-machine-type')

      form
        .querySelectorAll(
          [
            '.sebco-deviceedit-device-form-detergent-price',
            '.sebco-deviceedit-device-form-extended-cycle-price',
            '.sebco-deviceedit-device-form-price'
          ].join(', ')
        )
        .forEach(function (v) {
          v.step = val

          if (machineType === 'washer') {
            return
          }

          /* Lock dryer top-off price to the value of coin1. */
          if (
            v.classList
              .contains('sebco-deviceedit-device-form-extended-cycle-price')
          ) {
            v.value = val
            v.disabled = true
          }
        })
    }

    static handleDeviceEditPaymentTypeChange (e) {
      var form
      var machineType
      var sel
      var t
      var val

      if (!e || lo.isUndefined(e.currentTarget)) {
        return
      }

      sel = e.currentTarget

      if (lo.isUndefined(sel.value)) {
        return
      }

      val = parseInt(e.currentTarget.value, 10)

      if (!Number.isFinite(val)) {
        return
      }

      form = e.currentTarget.closest('form')
      machineType = form.getAttribute('data-machine-type')

      if (
        (val !== A.PAYMENT_TYPE_DEBIT_ONLY) &&
        (val !== A.PAYMENT_TYPE_ENHANCED_DEBIT)
      ) {
        form.querySelectorAll('.sebco-deviceedit-device-form-coin-1')
          .forEach(function (v) {
            v.disabled = false

            if (machineType === 'washer') {
              return
            }

            /* Lock dryer top-off price to value of coin1. */
            form
              .querySelectorAll(
                '.sebco-deviceedit-device-form-extended-cycle-price'
              )
              .forEach(function (u) {
                u.setAttribute('data-wcloud-coin', 'true')
                u.value = v.value
                u.disabled = true
              })
          })

        return
      }

      form.querySelectorAll('.sebco-deviceedit-device-form-coin-1')
        .forEach(function (v) {
          Array.from(v.options)
            .some(function (u) {
              if (parseFloat(u.value) === 0.05) {
                u.selected = true
                return true
              }

              u.selected = false

              return false
            })

          v.disabled = true
        })

      /* Restore step to a nickel. */
      form
        .querySelectorAll(
          [
            '.sebco-deviceedit-device-form-detergent-price',
            '.sebco-deviceedit-device-form-extended-cycle-price',
            '.sebco-deviceedit-device-form-price'
          ].join(', ')
        )
        .forEach(function (v) {
          v.step = 0.05

          /* Remove locking of top-off price to value of coin1. */
          if (
            v.classList
              .contains('sebco-deviceedit-device-form-extended-cycle-price')
          ) {
            v.removeAttribute('data-wcloud-coin')
          }
        })

      /* Indirectly restore enabled/disabled state of top-off price field. */
      t = form
        .querySelector('.sebco-deviceedit-device-form-extended-cycle-style')

      if (t !== null) {
        t.dispatchEvent(new Event('change'))
      }
    }

    static handleDeviceEditPriceChange (e) {
      var coin1
      var duration
      var increment
      var els = { price: e.currentTarget }
      var price
      var fm
      var multiplier
      var t

      els.fm = els.price.closest('form')

      if (!els.fm) {
        return
      }

      els.coin1 = els.fm.querySelector('.sebco-deviceedit-device-form-coin-1')

      els.duration = els.fm
        .querySelector('.sebco-deviceedit-device-form-cycle-duration')

      if (!els.coin1 || !els.duration) {
        return
      }

      coin1 = parseFloat(els.coin1.value)
      duration = parseInt(els.duration.dataset.requestedDuration, 10)
      price = parseFloat(els.price.value)

      if (
        !Number.isFinite(coin1) ||
        !Number.isFinite(duration) ||
        !Number.isFinite(price)
      ) {
        return
      }

      els.price.setAttribute('step', coin1)
      multiplier = parseInt(price / coin1, 10)

      if (multiplier < 1) {
        multiplier = 1
      }

      t = (multiplier * coin1)

      if (t < price) {
        price = t + coin1
      }

      if (price < 0) {
        price = 0
      } else if (price > 9.95) {
        price = 9.95
      }

      els.price.value = price
      increment = parseInt(duration / multiplier, 10)

      if (increment < 1) {
        increment = 1
      }

      if ((increment * multiplier) < duration) {
        increment += 1
      }

      duration = increment * multiplier

      if (duration < 5) {
        duration += increment
      }

      if (duration > 75) {
        duration -= increment
      }

      els.duration.value = duration
      console.log([coin1, duration])
      console.log([typeof els.price.value, els.price.value])
      lo.noop(fm)
    }

    static handleChangeSubmitChange (e) {
      const f = e.currentTarget.closest('form')

      if (f) {
        f.submit()
      }
    }

    static handleDeviceEventDupeIndicatorClick (e) {
      var t = $(e.currentTarget)
      var u = t.closest('tr')
      var v

      if (lo.isDefined(u.data('dupeId'))) {
        v = $('#sebco-row-' + u.data('dupeId'))

        if (v.length > 0) {
          v.removeClass('sebco-dupe-collapse')

          A.handleDeviceEventDupeIndicatorClick({
            currentTarget: v.find('.sebco-dupe-indicator')
          })

          t.css({ cursor: 'auto', opacity: 0.5 })
        }
      }
    }

    static handleFlashDataCloseClick (e) {
      e.preventDefault()
      e.stopPropagation()

      A
        .fadeOutAndSlideUpAndRemove(
          $(e.currentTarget).closest('.sebco-flash-data')
        )
        .fail(debug)
    }

    static handleForgotPasswordLinkClick (e) {
      var el = e.currentTarget
      var email = $('input[type="email"]:first').val()
      var link = $(el)
      var pathname = link.data('originalUrl')
      var t

      if (lo.isString(email) &&
          $.trim(email) !== '' &&
          lo.isString(pathname) &&
          $.trim(pathname) !== '') {
        t = d.createElement('a')
        t.href = '/'
        t.pathname = A.sanitizeUrl(pathname)
        t.search = '?email=' + encodeURIComponent(email)
        link.attr('href', t.href)
      }
    }

    /**
     * @param {InputEvent} e
     * @returns {void}
     */
    static handleMmpCustomerTypeInput (e) {
      const { currentTarget } = e

      if (!(currentTarget instanceof HTMLSelectElement)) {
        return
      }

      const { formatInputId, locationInputId } = currentTarget.dataset
      const formatSel = d.getElementById(formatInputId)
      const locationSel = d.getElementById(locationInputId)

      if (!(formatSel instanceof HTMLSelectElement)) {
        return
      }

      const { value: type } = currentTarget.selectedOptions?.[0]

      if (!type) {
        return
      }

      let o = null

      if (locationSel instanceof HTMLSelectElement) {
        switch (type) {
          case 'usagebylocation':
          case 'usagebymachine':
            locationSel.selectedIndex = 0
            locationSel.disabled = true
            break
          default:
            locationSel.disabled = false
            break
        }
      }

      try {
        o = JSON.parse(formatSel?.dataset?.formatOptions)

        if (o[type] && lo.isObjectLike(o[type])) {
          while (formatSel.length > 0) {
            formatSel.remove(0)
          }

          Object.entries(o[type])
            .forEach(([k, v]) => formatSel.add(new Option(v, k)))

          formatSel.disabled = (formatSel.length < 2)
        }
      } catch (ignore) {}
    }

    /**
     * @param {SubmitEvent} ev
     */
    static handleMmpLoadingFormSubmit (ev) {
      const form = (ev.currentTarget) ? ev.currentTarget : ev.target

      if (form instanceof HTMLFormElement) {
        console.log(form)

        const btns = Array.from(
          form.querySelectorAll('button[type="submit"], input[type="submit"]')
        ).filter(btn => !btn.disabled)

        if (btns.length) {
          A.dismissAllDismissibles()

          btns.forEach(btn => {
            btn.disabled = true
          })

          A.waitPromise(60000)
            .then(() => {
              btns.forEach(btn => {
                btn.disabled = false
              })
            })
            .catch(console.error)
        }
      }
    }

    static handleMmpSelectAllTriggerInput (e) {
      if (!e.currentTarget) {
        return
      }

      const el = e.currentTarget
      const fm = el.closest('form')

      if (!fm) {
        return
      }

      const ins = fm.querySelectorAll('input[name="' + el.name + '"]')

      if (ins.length < 1) {
        return
      }

      /* If unchecked -> checked, select all */
      if (el.checked) {
        ins.forEach((v) => {
          if (!v.checked) {
            v.checked = true
          }
        })

        return
      }

      /* If checked -> unchecked, and all are selected, deselect all */
      const n = Array.from(ins)
        .reduce((u, v) => ((v.checked) ? (1 + u) : u), 0)

      if (n < ins.length) {
        ins.forEach((v) => {
          if (v.checked) {
            v.checked = false
          }
        })
      }
    }

    static handleMmpUserLookupFormSubmit (ev) {
      const id = Number.parseInt(
        d.getElementById(`${ev.currentTarget.id}-id`)?.value,
        10
      )

      console.warn(id)

      if (!Number.isFinite(id)) {
        ev.preventDefault()
      }
    }

    static handleGenericUrlChangerChange (e) {
      const sel = e.currentTarget
      const data = sel.dataset
      const re = new RegExp(`{{${data.property}}}`)
      const url = data.urlTemplate.trim().replace(re, sel.value.trim())

      if (/^\//.test(url)) {
        w.location.href = new URL(A.sanitizeUrl(url), w.location.href)
      }
    }

    static handlePaymentMethodFavorite (e) {
      var link

      e.preventDefault()
      link = $(e.currentTarget)
      $.ajax({
        url: '/paymentmethods/setfavorite',
        dataType: 'json',
        error: function (x, t, e) {
          debug([x, t, e])
        },
        success: function (data) {
          if (lo.isObjectLike(data) &&
              lo.isObjectLike(data.meta) &&
              Number.isFinite(data.meta.status) &&
              data.meta.status < 400) {
            $('.sebco-favorite-widget')
              .removeClass('sebco-favorite-widget-active')

            link.addClass('sebco-favorite-widget-active')
          }
        }
      })
    }

    /** @param {MouseEvent} e  */
    static handleSortableTableClick (e) {
      const { currentTarget } = e

      if (currentTarget instanceof HTMLElement) {
        const { direction, field } = currentTarget.dataset

        if (field) {
          const order = []

          debug([direction, typeof direction])
          switch (direction) {
            case '1':
              order.push([field, 0])
              break
            default:
              order.push([field, 1])
              break
          }

          const table = currentTarget.closest('.mmp-sortable-table')

          if (table instanceof HTMLElement) {
            try {
              const previous = JSON.parse(table.dataset.order)

              if (Array.isArray(previous)) {
                for (const p of previous) {
                  if (!p.field || p.field === field) {
                    continue
                  }
                  order.push([p.field, p.direction])
                }

                e.preventDefault()
                window.location.href = '?' + new URLSearchParams({
                  order: JSON.stringify(order)
                }).toString()
              }
            } catch (err) {
              this.warn(err)
            }
          }
        }
      }
    }

    static handleStaffAcknowledgeDispatchClose (e) {
      e.preventDefault()

      $(e.currentTarget)
        .closest('.sebco-acknowledge-dispatch-form')
        .dialog('close')
    }

    static handleStaffAcknowledgeDispatchTrigger (e) {
      var data,
        f,
        id,
        opts,
        q,
        t,
        u,
        win

      e.preventDefault()
      q = $(e.currentTarget)
      id = '#sebco-acknowledge-dispatch-' + q.data('id')
      f = $(id)

      if (f.length > 0) {
        /* RETURNS */
        return
      }

      t = w
        .Handlebars
        .compile(
          $('#template-sebco-acknowledge-dispatch-form').html()
        )

      data = {
        id: q.data('id'),
        inetAccount: q.data('inetAccount'),
        inetProvider: q.data('inetProvider'),
        label: q.data('label'),
        type: q.data('type')
      }

      u = q.data('inetMaintainer')

      data.inetMaintainer = (Number.isFinite(u) && u === 1)
        ? 'Building'
        : 'My Magic Pass'

      u = q.attr('data-contact-name-1')

      if (lo.isString(u) && u !== '') {
        data.contactName1 = u
      }

      u = q.attr('data-contact-name-2')

      if (lo.isString(u) && u !== '') {
        data.contactName2 = u
      }

      u = q.attr('data-contact-phone-1')

      if (lo.isString(u) && u !== '') {
        data.contactPhone1 = u
      }

      u = q.attr('data-contact-phone-2')

      if (lo.isString(u) && u !== '') {
        data.contactPhone2 = u
      }

      console.log(data)

      if (data.type === 'device') {
        data.device = true
      } else if (data.type === 'kiosk') {
        data.kiosk = true
      }

      f = $(t(data))
        .on(
          'click',
          '.sebco-close',
          A.handleStaffAcknowledgeDispatchClose
        )

      win = A.win

      opts = {
        appendTo: '.sebco-page-main:first',
        autoResize: true,
        close: function () {
          A.waitPromise(600)
            .then(function () {
              f.dialog('destroy')
              f.remove()
            })
        },
        dialogClass: 'sebco-staff-functional-dialog',
        hide: {
          delay: 0,
          duration: 400,
          easing: 'easeOutCirc',
          effect: 'fadeOut'
        },
        maxHeight: (win.outerHeight() - 30),
        minWidth: 300,
        modal: true,
        resizable: false,
        show: {
          delay: 0,
          duration: 200,
          easing: 'easeOutCirc',
          effect: 'fadeIn'
        },
        width: (win.outerWidth() >= 768)
          ? 738
          : (win.outerWidth() - 30)
      }

      f.hide()
      f.dialog(opts)
      A.initUserLookup(f.find('.sebco-staff-user-lookup'))
    }

    static handleStaffBountyAddFormTargetLookupDatachange (e) {
      var t = $(e.currentTarget)
      var u
      var x

      u = t.data('lookupType')

      if (u === 'device') {
        x = t.data('placeholderDevice')

        if (lo.isString(x)) {
          t.attr('placeholder', x.replace(/(<|&lt;)/gim, ''))
        }

        t
          .data('endpoint', t.data('endpointDevice'))
          .val('')

        $(t.data('lookupIdField')).val(null)
      } else if (u === 'kiosk') {
        x = t.data('placeholderKiosk')

        if (lo.isString(x)) {
          t.attr('placeholder', x.replace(/(<|&lt;)/gim, ''))
        }

        t
          .data('endpoint', t.data('endpointKiosk'))
          .val('')

        $(t.data('lookupIdField')).val(null)
      }
    }

    static handleStaffBountyAddFormTypeChange (e) {
      var sel = $(e.currentTarget)
      var t
      var v = sel.val()

      if (v !== 'device' && v !== 'kiosk') {
        /* RETURNS */
        return
      }

      t = sel.closest('form')

      if (t.length < 1) {
        /* RETURNS */
        return
      }

      t = t.find('.sebco-bounty-add-form-target-lookup')

      if (t.length < 1) {
        /* RETURNS */
        return
      }

      if (v !== t.data('lookupType')) {
        t.data('lookupType', v).trigger('datachange')
      }
    }

    static handleStaffCollectionReportSubmit (e) {
      var con
      var opts
      var fm
      var tmpl

      e.preventDefault()
      fm = e.currentTarget

      fm.querySelectorAll('button[type="submit"]')
        .forEach(function (v) {
          v.setAttribute('disabled', 'disabled')
        })

      opts = {
        data: {
          begin: fm.elements.begin.value,
          end: fm.elements.end.value,
          locationId: fm.elements.locationId.value
        },
        url: fm.getAttribute('data-endpoint')
      }

      con = d.querySelector('#' + fm.getAttribute('data-output-container-id'))

      tmpl = w.Handlebars.compile(
        d.querySelector('#' + fm.getAttribute('data-loader-template-id'))
          .innerText
      )

      con.innerHTML = tmpl({})

      tmpl = w.Handlebars.compile(
        d.querySelector('#' + fm.getAttribute('data-template-id')).innerText
      )

      A.ajaxPromise(opts)
        .then(A.normalizeJsonResponse)
        .then(function (o) {
          if (o.meta.status >= 400) {
            throw new Error(o.meta.message)
          }

          return A.waitPromise(1000)
            .then(function () {
              con.innerHTML = tmpl(o.response.location)

              fm.querySelectorAll('button[type="submit"]')
                .forEach(function (v) {
                  v.removeAttribute('disabled')
                })
            })
        })
        .catch(function (err) {
          con.innerHTML = tmpl({ error: err })

          fm.querySelectorAll('button[type="submit"]')
            .forEach(function (v) {
              v.removeAttribute('disabled')
            })
        })
    }

    static handleStaffReportDateChangerSubmit (e) {
      var con
      var opts
      var fm
      var tmpl

      e.preventDefault()
      fm = e.currentTarget

      fm.querySelectorAll('button[type="submit"]')
        .forEach(function (v) {
          v.setAttribute('disabled', 'disabled')
        })

      opts = {
        data: {},
        url: fm.getAttribute('data-endpoint')
      };

      ['begin', 'end', 'month', 'page', 'tz'].forEach(function (k) {
        if (fm.elements[k]) {
          opts.data[k] = fm.elements[k].value
        }
      })

      con = d.querySelector('#' + fm.getAttribute('data-output-container-id'))

      tmpl = w.Handlebars.compile(
        d.querySelector('#' + fm.getAttribute('data-loader-template-id'))
          .innerText
      )

      con.innerHTML = tmpl({})

      tmpl = w.Handlebars.compile(
        d.querySelector('#' + fm.getAttribute('data-template-id')).innerText
      )

      A.ajaxPromise(opts)
        .then(A.normalizeJsonResponse)
        .then(function (o) {
          if (o.meta.status >= 400) {
            throw new Error(o.meta.message)
          }

          return A.waitPromise(1000)
            .then(function () {
              con.innerHTML = tmpl(o.response)

              fm.querySelectorAll('button[type="submit"]')
                .forEach(function (v) {
                  v.removeAttribute('disabled')
                })
            })
        })
        .catch(function (err) {
          try {
            con.innerHTML = tmpl({ error: err })
          } catch (x) {
            console.error(x)
          }

          fm.querySelectorAll('button[type="submit"]')
            .forEach(function (v) {
              v.removeAttribute('disabled')
            })
        })
    }

    static handleUnifiedSearchActionPostClick (e) {
      e.preventDefault()
      $('#' + $(e.currentTarget).data('id')).trigger('submit')
    }

    static handleUnifiedSearchItemClick (e) {
      var link = $(e.currentTarget).find('.sebco-action').first()

      e.preventDefault()

      if (link.length > 0) {
        if (link.hasClass('sebco-action-post')) {
          e.currentTarget = link
          A.handleUnifiedSearchActionPostClick(e)
        } else {
          w.location.href = link.attr('href')
        }
      }
    }

    static handleUnifiedSearchLinkClick (e) {
      e.stopPropagation()
    }

    static handleUnifiedSearchRenderItem (ul, item) {
      var t,
        u,
        x

      if (!lo.isDefined(A.templateUnifiedSearchItem)) {
        A.templateUnifiedSearchItem = w
          .Handlebars
          .compile(
            $('#template-sebco-staff-unified-search-item').html()
          )
      }

      switch (item.type) {
        case 'device':
          item.badgeClass = 'btn sebco-btn-device'
          item.type = 'Device'
          break
        case 'kiosk':
          item.badgeClass = 'btn sebco-btn-kiosk'
          item.type = 'Kiosk'
          break
        case 'kiosk_hardware':
          item.badgeClass = 'btn sebco-btn-kiosk-hardware'
          item.type = 'Kiosk PC'
          break
        case 'location':
          item.badgeClass = 'btn sebco-btn-location'
          item.type = 'Location'
          break
        case 'room':
          item.badgeClass = 'btn sebco-btn-room'
          item.type = 'Room'
          break
        case 'user':
          item.badgeClass = 'btn sebco-btn-user'
          item.type = 'Customer'
          break
        default:
          item.badgeClass = 'btn'
          item.type = 'Unknown'
      }

      if ($.isArray(item.actions)) {
        $.each(item.actions, function (ignore, v) {
          if (v.post === true) {
            v.id = A.generateId()
          }

          if (lo.isString(v.url)) {
            v.url = A.sanitizeUrl(v.url)
          }
        })
      }

      t = $(A.templateUnifiedSearchItem(item))
        .data('ui-autocomplete-item', item)

      u = t.find('.sebco-name')
      x = u.text().split('\n')
      u.empty()

      $.each(x, function (i, v) {
        if (i > 0) {
          u.append(d.createElement('br'))
        }

        u.append(d.createTextNode(v))
      })

      return ul.append(t)
    }

    static hideScanningDialog (fn) {
      $('#sebco-dialog-scanning').dialog('close').dialog('destroy')

      if (lo.isFunction(fn)) {
        fn()
      }
    }

    /*
     * Hides any active screen saver currently displayed on the page. This
     * method also resets the screen saver to its first slide.
     *
     * Note: there is only meant to be a single screen saver production in a
     * page. If more than one is found in the page, the first is kept, but
     * additional screen savers are removed.
     */
    static hideScreenSaver () {
      Array.from(d.querySelectorAll('.sebco-screen-saver'))
        .forEach(function (v, i) {
          var screenSaver = $(v)
          var t = screenSaver.data('slideTimerId')

          w.clearTimeout(t)
          t = null

          screenSaver.removeAttr('data-slide-timer-id')
            .removeData('slideTimerId')

          if (i > 0) {
            screenSaver.remove()
          } else {
            screenSaver.data('currentIndex', 0)
              .data('isVisible', false)
              .find('video')
              .each(function (ignore, u) {
                if (lo.isFunction(u.pause)) {
                  u.pause()
                }
              })
              .end()
              .velocity('fadeOut', function () {
                screenSaver.find('video')
                  .each(function (ignore, u) {
                    u.currentTime = 0
                  })
              })
          }
        })

      $('#sebco-kiosk-login-scan-form-rfid').trigger('focus')
    }

    /*
     * Top-level initialization method.  All initialization should be fired off
     * from here.
     */
    static init () {
      debug('Initializing.')
      A.win = $(w)
      A.initKeyfobScanConstant()
      A.preCacheImages()
      A.initRegistrationForm()
      A.initOnScreenKeyboard()
      A.initNav()
      A.initWindowResizeEnd()
      A.initAddCardToggle()
      A.initAddKeyfob()
      /*
       * initAddUnassociatedKeyfob() must run after initAddKeyfob().
       */
      A.initAddUnassociatedKeyfob()
      A.initAutoRefreshDeviceStatuses()
      A.initDismissFlashData()
      A.initCurrentTimeAutoUpdate()
      A.initSuperCycleToggle()
      A.initTopOffControls()
      A.initDeviceDeselect()
      A.initDeviceServiceRequest()
      A.initFakeFinishPayLoading()
      A.initKeyfobScanTrigger()
      A.initAddFundsDropdown()
      A.initBackLinks()
      A.initResponsiveNavToggle()
      A.initCCValidation()
      A.initFormatterCCExpires()
      A.initTwisty()
      A.initKeyfobHousekeepingSubmit()
      A.initDetergentWidget()
      A.initSignoutForward()
      A.initSiteCode()
      A.initApartmentList()
      A.initScreenSaver()
      A.initAvatarChooser()
      A.initForgotPasswordLinks()
      A.initMonitorMessageTwisty()
      A.initPostFinishPayDelay()
      A.initEditDevice()
      A.initDevicehealthSelectLocation()
      A.initDevicehealthExposeMonitormessage()
      A.initPreventKioskImageDrag()
      A.initAnimateKioskTap()
      A.initUserLookups()
      A.initRoomLookup()
      A.initLocationLookup()
      A.initKioskLookup()
      A.initKioskHardwareLookups()
      A.initStaffAddFundsForm()
      A.initStaffCreateKioskForms()
      A.initStaffRebootKioskForm()
      A.initSendMobileAppLink()
      A.initStaffActivityHeatmap()
      A.initTenantReferralPromo()
      A.initStaffSetupRoomMode()
      A.initRequireConfirm()
      A.initFigureQuotePreview()
      A.initStaffStatistics()
      A.initStaffUnreliableDevices()
      A.initFullPageBackNav()
      A.initStaffMachineStartsBarChart()
      A.initStaffKioskDisconnects()
      A.initDemoToggleServiceRequest()
      A.initStaffUserStats()
      A.initZipLookup()
      A.initCloneFromField()
      A.initLatLongLookup()
      A.initSortableTable()
      A.initStaffSelectRoomLinks()
      A.initStaffFindLogViewerOutput()
      A.initStaffKioskConnectionStatus()
      A.initStaffMediaObjectUploadForms()
      A.initStaffDatePickers()
      A.initStaffLocationStats()
      A.initStaffLocationSelectForm()
      A.initStaffMicroEvents()
      A.initStaffDashboardPanels()
      A.initStaffResetCustomerPasswordFormTrigger()
      A.initStaffVirtualStackTriggers()
      A.initStaffLocationMap()
      A.initStaffKioskSoftwareUpdate()
      A.initStaffKioskSoftwareUpdateForm()
      A.initStaffDormantCustomerForm()
      A.initStaffRecalculateCustomerBalanceView()
      A.initStaffDeviceSetupMessageStatuses()
      A.initStaffCurrencyValidatorResetCountsForm()
      A.initStaffDeviceStatistics()
      A.initStaffServiceModeForm()
      A.initStaffCommServerStatus()
      A.initStaffRoomVideo()
      A.initPaymentMethodInputCleanup()
      A.initChangeSubmit()
      A.initCheckboxLarge()
      A.initStaffDeviceEventsTable()
      A.initStaffActiveErrorEmailIds()
      A.initStaffActiveErrorReports()
      A.initStaffKioskRebootCountRangeSelect()
      A.initStaffSupportedBillTypesCons()
      A.initDeviceFeedbackUploadForm()
      A.initStaffBountyAddForms()
      A.initStaffUnifiedSearches()
      A.initStaffAcknowledgeDispatches()
      A.initStaffCountBadges()
      A.initStaffZipBuildingMapCons()
      A.initStaffStreetViewCons()
      A.initStaffProblemDevicesView()
      A.initStaffDeviceeditSetstatusForm()
      A.initTimezones()
      A.initStaffCollectionReports()
      A.initStaffReportDateChanger()
      A.initMmpLoadingForms()
      A.initWCloudDeviceEditForms()
      A.initWCloudDeviceSelectionForms()
      A.initMmpCustomerReportForm()
      A.initMmpSelectAllTriggers()
      A.initMmpUserLookupForms()
      A.initGenericUrlChanger()
      // Do not enable yet
      // a.initPaymentMethodFavorite();

      w.Handlebars.registerHelper('hbCurrency', function (n) {
        var t = parseFloat(n)

        if (!Number.isFinite(t)) {
          return n
        }

        return '$' + t.toFixed(2)
      })

      w.Handlebars.registerHelper('hbDateSec', function (n) {
        var t = parseInt(n, 10)

        if (!Number.isFinite(t)) {
          return n
        }

        return (new Date(t * 1000)).toLocaleDateString()
      })

      w.Handlebars.registerHelper('hbIsoStringSec', function (n) {
        var t = parseInt(n, 10)

        if (!Number.isFinite(t)) {
          return n
        }

        return (new Date(t * 1000)).toISOString().replace(/\.000+/, '')
      })
    }

    static initAddCardToggle () {
      $('.sebco-add-card-toggle').each(function (ignore, v) {
        var trigger = $(v)
        var con = $('#' + trigger.data('targetId'))
        var focusField = con.find('.sebco-input-cc')

        con
          .hide()
          .removeAttr('hidden')
          .on(
            'click',
            'button[type="reset"], input[type="reset"]',
            function () {
              trigger.trigger('click')
            }
          )

        /*
         * If the active class present in the template, then display the form by
         * default.
         */
        if (con.hasClass('active')) {
          con
            .slideDown(
              'fast',
              function () {
                focusField.trigger('focus')
              }
            )
        }

        trigger.on('click', function (e) {
          e.preventDefault()

          if (con.hasClass('active')) {
            con
              .removeClass('active')
              .slideUp(
                'fast',
                function () {
                  trigger.trigger('focus')
                }
              )
          } else {
            con
              .addClass('active')
              .slideDown(
                'fast',
                function () {
                  focusField.trigger('focus')
                }
              )
          }
        })
      })
    }

    static initAddFundsDropdown () {
      var con = $('#sebco-add-funds-widget')
      var trigger

      if (!con.data('hasPaymentMethod')) {
        return false
      }

      trigger = $('#sebco-add-funds-widget-toggle')
      con.hide().prop('hidden', false)

      trigger.on('click', function (e) {
        e.stopPropagation()
        e.preventDefault()

        if (trigger.hasClass('active')) {
          trigger.removeClass('active')

          con.removeClass('active')
            .fadeOut({ duration: 100 })
        } else {
          trigger.addClass('active')

          con.addClass('active')
            .fadeIn({ duration: 100 })
        }
      })

      $(d)
        .on(
          'click',
          '#sebco-add-funds-widget .sebco-btn-close',
          function (e) {
            e.preventDefault()
            trigger.removeClass('active')

            con.removeClass('active')
              .fadeOut({ duration: 300 })
          }
        )
        .on('click', function (e) {
          if (
            trigger.hasClass('active') &&
            !trigger.is(e.target) &&
            !con.is(e.target) &&
            (con.has(e.target).length === 0)
          ) {
            trigger.trigger('click')
          }
        })
    }

    static initAddKeyfob () {
      if ($('body:first').data('isKioskLogin')) {
        /*
         * If the user is logged in to a kiosk, new adding a key fob should be
         * done via a scan.
         */
        A.initAddKeyfobScanForm()
      } else {
        /*
         * If the user is logged in via the web, new adding a key fob should be
         * done using manual form input.
         */
        A.initAddKeyfobInputForm()
      }
    }

    static initAddKeyfobScanForm () {
      var con = $('#sebco-add-keyfob-scan-form-con')
      var input = con.find('#sebco-add-keyfob-scan-form-rfid')
      var newKeyfobName
      var trigger = $('.sebco-add-keyfob-toggle')

      newKeyfobName = con.data('firstname') +
        "'s Key Fob #" +
        (1 + con.data('keyfobCount'))

      con.find('#sebco-add-keyfob-scan-form-name').val(newKeyfobName)

      trigger.on('click', function (e) {
        var t

        e.preventDefault()
        A.showScanningDialog()
        input.val('').trigger('focus')

        /*
         * Reset focus to trigger after 30 seconds.
         */
        t = w.setTimeout(
          function () {
            w.clearTimeout(t)
            t = null
            trigger.trigger('focus')
            A.hideScanningDialog()
          },
          30000
        )
      })
    }

    static initAddKeyfobInputForm () {
      $('.sebco-add-keyfob-toggle').each(function (ignore, v) {
        var trigger = $(v)
        var con = $('#' + trigger.data('targetId'))
        var focusField = con.find('.sebco-add-keyfob-form-rfid')

        con
          .hide()
          .removeAttr('hidden')
          .on(
            'click',
            'button[type="reset"], input[type="reset"]',
            function () {
              trigger.trigger('click')
            }
          )

        if (con.hasClass('active')) {
          /*
           * If the active class present in the template, then display the form
           * by default.
           */
          con
            .slideDown(
              'fast',
              function () {
                focusField.trigger('focus')
              }
            )
        }

        trigger.on('click', function (e) {
          e.preventDefault()

          if (con.hasClass('active')) {
            con
              .removeClass('active')
              .slideUp(
                'fast',
                function () {
                  trigger.trigger('focus')
                }
              )
          } else {
            con
              .addClass('active')
              .slideDown(
                'fast',
                function () {
                  focusField.trigger('focus')
                }
              )
          }
        })
      })
    }

    static initAddUnassociatedKeyfob () {
      var div = $('#sebco-unassociated-keyfob')

      if (div.length > 0 && div.data('hasUnassociatedKeyfob') === true) {
        $('.sebco-add-keyfob-toggle').trigger('click')
      }
    }

    static initAnimateKioskTap () {
      var offsetLeft,
        offsetTop,
        ref,
        tmpl

      /*
       * Only applies to kiosks.
       */
      if ($('body:first').data('isKiosk') !== true) {
        /* RETURNS */
        return
      }

      tmpl = $('#template-sebco-cursor-animation').html()
      ref = $(tmpl).css({ left: -16384, top: 0 })
      $('body:first').append(ref)
      offsetTop = A.parseInt(ref.outerHeight() / 2)
      offsetLeft = A.parseInt(ref.outerWidth() / 2)
      ref.remove()

      if (!Number.isFinite(offsetLeft)) {
        offsetLeft = 32
      }

      if (!Number.isFinite(offsetTop)) {
        offsetTop = offsetLeft
      }

      $(d)
        .on(
          'click',
          function (e) {
            var tmp

            if ((e.clientX === 0) && (e.clientY === 0)) {
              /* RETURNS */
              return
            }

            tmp = $(tmpl)

            tmp.css({
              left: e.clientX - offsetLeft,
              top: e.clientY - offsetTop
            })

            $('body:first').append(tmp)

            tmp
              .css({ opacity: 1 })
              .addClass('sebco-zoom-in-fade-out')

            setTimeout(function () {
              tmp.remove()
            }, 440)
          }
        )
    }

    static initApartmentList () {
      var apartmentId
      var apartment
      var registerForm = $('#sebco-register-form')
      var locationId

      if (registerForm.length < 1) {
        return
      }

      apartmentId = registerForm
        .find('#sebco-register-form-apartment-id')

      if (apartmentId.length < 1) {
        return
      }

      apartment = registerForm.find('#sebco-register-form-apartment')

      if (apartment.length < 1) {
        return
      }

      locationId = registerForm.find('#sebco-register-form-location-id')

      if (locationId.length < 1) {
        return
      }

      apartment.autocomplete({
        appendTo: apartment.parent(),
        minLength: 1,
        source: function (request, response) {
          var tmpLocationId = locationId.val()

          if (!lo.isString(tmpLocationId) ||
              $.trim(tmpLocationId) === '') {
            return
          }

          $.ajax({
            url: '/api/v1/apartments/' + tmpLocationId,
            data: { code: request.term },
            dataType: 'json',
            error: function (x, t, e) {
              debug([x, t, e])
              response([])
            },
            success: function (o) {
              var apartments = {}
              var apartmentNames = []

              if (
                lo.isDefined(o) &&
                lo.isDefined(o.response) &&
                Array.isArray(o.response)
              ) {
                $.each(o.response, function (ignore, v) {
                  if (lo.isDefined(v) && lo.isString(v.name)) {
                    apartments[v.name] = v.id
                    apartmentNames.push(v.name)
                  }
                })

                response(apartmentNames)
              }
            }
          })
        }
      })
    }

    static initAutoRefreshDeviceStatuses () {
      var con

      if ($('body:first').data('refreshDeviceStatusesV1') === false) {
        /* RETURNS */
        return
      }

      con = $('.sebco-initial-device-statuses')

      if (con.length > 0) {
        A.refreshDeviceStatuses(con)
      }
    }

    /**
     * Initializes the custom user avatar selection widget.
     */
    static initAvatarChooser () {
      var closeFn
      var con = $('#template-sebco-avatar-chooser')
      var dialogOptions
      var sel
      var updateFn

      if (con.length > 0) {
        closeFn = function (e) {
          e.preventDefault()
          $(e.currentTarget).closest('.sebco-dialog').dialog('close')
        }

        updateFn = function (e) {
          e.preventDefault()

          $('#sebco-avatar-chooser-form')
            .find('.sebco-avatar-chooser-form-avatar')
            .val($(e.currentTarget).data('avatar'))
            .end()
            .trigger('submit')
        }

        dialogOptions = {
          autoOpen: false,
          autoResize: true,
          dialogClass: 'sebco-dialog-con',
          minWidth: 300,
          modal: true,
          resizable: false
        }

        con = $(con.html())
          .hide()
          .removeAttr('hidden')
          .dialog(dialogOptions)
          .on('click', '.sebco-btn-close', closeFn)
          .on('click', '.sebco-avatar-choice', updateFn)

        /*
         * Open the selection widget when the user clicks the avatar. Insure
         * that the responsive nav is automatically closed (on phones).
         */
        sel = '.sebco-avatar .sebco-user-photo' +
          ', .sebco-staff-section-footer .sebco-user-photo'

        $(sel)
          .on(
            'click',
            function () {
              $('.sebco-top-row')
                .removeClass('sebco-show-responsive-nav')

              if (A.win.width() < 768) {
                con.dialog('option', { width: 300 })
              } else {
                con.dialog('option', { width: 500 })
              }

              con.dialog('open')
            }
          )
      }
    }

    static initBackLinks () {
      $(d)
        .on(
          'click',
          '.sebco-back-link[href]',
          function (e) {
            e.preventDefault()
            w.history.back()
          }
        )
    }

    static initCCValidation () {
      $('.sebco-input-cc').each(function (ignore, v) {
        var input = $(v)
        var defaultClassNames = input.attr('class')

        input.validateCreditCard(
          function (result) {
            var classNames = defaultClassNames

            if (result.card_type !== null) {
              classNames += ' sebco-input-cc-' +
                result.card_type.name
            }

            input.attr('class', classNames)
          }
        )
      })
    }

    static initChangeSubmit () {
      if (d.querySelectorAll('.sebco-change-submit').length < 1) {
        return
      }

      $(d).on('change', '.sebco-change-submit', A.handleChangeSubmitChange)
    }

    static initCheckboxLarge () {
      $('.sebco-checkbox-lg').each(function (ignore, v) {
        var con = $(v)
        var cb = con.find('input[type=checkbox]')
        var img = con.find('.sebco-checkbox-icon')

        if (cb.is(':checked')) {
          img.addClass('sebco-checkbox-icon-on')
        } else {
          img.removeClass('sebco-checkbox-icon-on')
        }

        cb.on('change', function () {
          if (cb.is(':checked')) {
            img.addClass('sebco-checkbox-icon-on')
          } else {
            img.removeClass('sebco-checkbox-icon-on')
          }
        })
      })
    }

    static initCloneFromField () {
      var targets = $('.sebco-clone-from-field')

      if (targets.length < 1) {
        /* RETURNS */
        return
      }

      targets.each(function (ignore, v) {
        var source,
          target

        target = $(v)
        source = $(target.data('cloneFromFieldId'))

        if (source.length > 0) {
          target.on('focus', function () {
            if ($.trim(target.val()) === '') {
              target.val(source.val())
            }
          })
        }
      })
    }

    static initStaffAcknowledgeDispatches () {
      $(d)
        .on(
          'click',
          '.sebco-acknowledge-dispatch-trigger',
          A.handleStaffAcknowledgeDispatchTrigger
        )
    }

    static initStaffActiveErrorEmailIds () {
      var con = $('#sebco-active-error-report-ids-con')
      var loader
      var loaderHide
      var loaderShow
      var tmpl
      var update

      if (con.length < 1) {
        /* RETURNS */
        return
      }

      loader = w
        .Handlebars
        .compile($('#' + con.data('loaderTemplate')).html())

      tmpl = w
        .Handlebars
        .compile($('#' + con.data('contentTemplate')).html())

      loaderHide = function () {
        con.find('.sebco-loader').remove()
      }

      loaderShow = function () {
        var def = $.Deferred()

        con.append(loader)
        def.resolve()

        return def.promise()
      }

      update = function () {
        loaderShow()
          .then(function () {
            return A.ajaxPromise({ url: con.data('endpoint') })
          })
          .then(function (o) {
            loaderHide()

            if (lo.isObjectLike(o) &&
                lo.isObjectLike(o.meta) &&
                Number.isFinite(o.meta.status) &&
                o.meta.status < 400 &&
                $.isArray(o.response)) {
              con.empty().append(tmpl({ emailIds: o.response }))
            } else {
              throw new Error()
            }
          })
          .fail(function (err) {
            debug(err)
            loaderHide()
          })
      }

      con.on(
        'change',
        '.checkbox input[type=checkbox]',
        function (e) {
          loaderShow()
          A.handleActiveErrorReportEnabledChange(e)
        }
      )

      con.on('datachange', update).trigger('datachange')
    }

    static initStaffActiveErrorReports () {
      var con = $('#sebco-table-active-error-report-con')
      var loader
      var loaderHide
      var loaderShow
      var tmpl
      var update

      if (con.length < 1) {
        /* RETURNS */
        return
      }

      loader = w
        .Handlebars
        .compile($('#' + con.data('loaderTemplate')).html())

      tmpl = w
        .Handlebars
        .compile($('#' + con.data('contentTemplate')).html())

      loaderHide = function () {
        con.find('.sebco-loader').remove()
      }

      loaderShow = function () {
        var def = $.Deferred()

        con.append(loader)
        def.resolve()

        return def.promise()
      }

      update = function () {
        loaderShow()
          .then(function () {
            return A.ajaxPromise({ url: con.data('endpoint') })
          })
          .then(function (o) {
            loaderHide()

            if (lo.isObjectLike(o) &&
                lo.isObjectLike(o.meta) &&
                Number.isFinite(o.meta.status) &&
                o.meta.status < 400 &&
                $.isArray(o.response)) {
              con.empty().append(tmpl({ reports: o.response }))
            } else {
              throw new Error()
            }
          })
          .fail(function (err) {
            debug(err)
            loaderHide()
          })
      }

      con.on(
        'change',
        '.checkbox input[type=checkbox]',
        function (e) {
          loaderShow()
          A.handleActiveErrorReportEnabledChange(e)
        }
      )

      con.on('datachange', update).trigger('datachange')
    }

    static initStaffBountyAddForm (v) {
      var fm = $(v)

      if (fm.length < 1) {
        /* RETURNS */
        return
      }

      fm
        .on(
          'change',
          '.sebco-bounty-add-form-type',
          A.handleStaffBountyAddFormTypeChange
        )
        .on(
          'datachange',
          '.sebco-bounty-add-form-target-lookup',
          A.handleStaffBountyAddFormTargetLookupDatachange
        )

      A.initTargetLookup(fm.find('.sebco-bounty-add-form-target-lookup'))
      fm.find('.sebco-bounty-add-form-type').trigger('change')
    }

    static initStaffBountyAddForms () {
      $('.sebco-bounty-add-form').each(function (ignore, v) {
        A.initStaffBountyAddForm(v)
      })
    }

    static initStaffCollectionReports () {
      if (d.querySelectorAll('.sebco-form-collection-report').length < 1) {
        return
      }

      $(d).on(
        'submit',
        '.sebco-form-collection-report',
        A.handleStaffCollectionReportSubmit
      )
    }

    static initStaffCommServerStatus () {
      var decorateAlive,
        decorateDead,
        decorateUnknown,
        updater

      decorateAlive = function (con) {
        con
          .removeClass(con.data('deadClass'))
          .removeClass(con.data('unknownClass'))
          .addClass(con.data('aliveClass'))
          .attr({
            title: con.data('aliveText')
          })
      }

      decorateDead = function (con) {
        con
          .removeClass(con.data('aliveClass'))
          .removeClass(con.data('unknownClass'))
          .addClass(con.data('deadClass'))
          .attr({
            title: con.data('deadText')
          })
      }

      decorateUnknown = function (con) {
        con
          .removeClass(con.data('aliveClass'))
          .removeClass(con.data('deadClass'))
          .addClass(con.data('unknownClass'))
          .attr({
            title: con.data('unknownText')
          })
      }

      updater = function () {
        var endpoint,
          widgets

        A.initStaffKioskConnectionStatusDestroy()
        widgets = $('.sebco-comm-server-status')

        widgets.each(function (k, v) {
          if (k === 0) {
            endpoint = $(v).data('endpoint')
          }
        })

        try {
          if (!lo.isString(endpoint)) {
            /* THROWS */
            throw new Error('Invalid endpoint.')
          }

          $.ajax({
            url: endpoint,
            dataType: 'json',
            error: decorateUnknown,
            success: function (o) {
              if (!lo.isObjectLike(o) ||
                  !lo.isObjectLike(o.meta) ||
                  !Number.isFinite(o.meta.status)) {
                widgets.each(function (ignore, v) {
                  decorateUnknown($(v))
                })

                /* RETURNS */
                return
              }

              if (o.meta.status >= 400) {
                widgets.each(function (ignore, v) {
                  decorateDead($(v))
                })

                /* RETURNS */
                return
              }

              widgets.each(function (ignore, v) {
                decorateAlive($(v))
              })
            },
            type: 'POST'
          })
        } catch (ignore) {
          // debug(ignore);
        }

        $('body:first').data(
          'commServerStatusTimer',
          w.setTimeout(updater, 30000)
        )
      }

      updater()
    }

    static initCurrentTimeAutoUpdate () {
      var fn

      fn = function () {
        try {
          A.currentTimeAutoUpdate()
        } catch (ignore) {}

        w.setTimeout(fn, 5000)
      }

      fn()
    }

    static initDemoToggleServiceRequest () {
      var trig = $('.sebco-demo-toggle-service-request')
      var ind

      if (trig.length < 1) {
        /* RETURNS */
        return
      }

      ind = trig.find('.sebco-indicator')

      trig.on('click', function () {
        $.ajax({
          url: trig.data('endpoint'),
          dataType: 'json',
          error: function (x, t, e) {
            debug([x, t, e])
          },
          success: function (o) {
            if (lo.isObjectLike(o) &&
                lo.isObjectLike(o.meta) &&
                Number.isFinite(o.meta.status) &&
                o.meta.status < 400 &&
                lo.isString(o.response)) {
              if (o.response === 'service-requested') {
                ind.addClass('sebco-engaged')
              } else {
                ind.removeClass('sebco-engaged')
              }

              $('.sebco-add-funds-widget-toggle')
                .trigger('click')

              setTimeout(function () {
                w.location.reload()
              }, 3000)
            }
          },
          type: 'POST'
        })
      })
    }

    static initDetergentWidget () {
      $(d)
        .on(
          'click',
          '.sebco-device-form-detergent',
          function (e) {
            var widget,
              icon,
              input,
              priceCon,
              val

            e.preventDefault()
            widget = $(e.currentTarget)
            icon = widget.find('.sebco-icon-sprite')

            input = widget
              .find('.sebco-device-form-detergent-count')

            priceCon = $('.sebco-device-form-price')
            val = A.parseInt(input.val())

            if (val === 1) {
              input.val(0)

              priceCon.data('detergentCount', 0)

              icon
                .addClass('sebco-icon-sprite-unchecked')
                .removeClass('sebco-icon-sprite-checked')
            } else {
              input.val(1)

              priceCon.data('detergentCount', 1)

              icon
                .addClass('sebco-icon-sprite-checked')
                .removeClass('sebco-icon-sprite-unchecked')
            }

            A.updatePrice(priceCon)
          }
        )
    }

    static initDeviceDeselect () {
      $(d)
        .on(
          'click',
          '.sebco-btn-device-selected',
          function (e) {
            var fm,
              link

            e.preventDefault()
            link = $(e.currentTarget)

            fm = $(d.createElement('form'))
              .attr({
                'accept-charset': 'UTF-8',
                action: link.attr('href'),
                method: 'post'
              })

            $('body').first().append(fm)
            fm.trigger('submit')
          }
        )
    }

    static initDeviceFeedbackUploadForm () {
      var addTrigger
      var addTriggerCon
      var con = $('.sebco-device-feedback-add-form-file-con')
      var fm
      var index
      var tmpl

      if (con.length < 1) {
        /* RETURNS */
        return
      }

      addTriggerCon = con.find('.sebco-add-trigger-con')
      fm = con.closest('form')

      if (addTriggerCon.length < 1) {
        /* RETURNS */
        return
      }

      addTrigger = con.find('.sebco-add-trigger')

      if (addTrigger.length < 1) {
        /* RETURNS */
        return
      }

      tmpl = $('#template-sebco-device-feedback-add-form-file')

      if (tmpl.length < 1) {
        /* RETURNS */
        return
      }

      tmpl = w.Handlebars.compile(tmpl.html())
      index = 0

      addTrigger.on('click', function (e) {
        var group

        e.preventDefault()
        addTrigger.trigger('blur')

        if (con.find('.sebco-upload').length >= 10) {
          /* RETURNS */
          return
        }

        group = $(tmpl({ index }))
          .on('click', '.sebco-remove', function (x) {
            var c,
              t,
              u

            x.preventDefault()

            u = group
              .find(
                '.sebco-staff-media-object-upload-form' +
                  '-file-display'
              )
              .val()

            if (!lo.isDefined(u) || (u === '')) {
              c = true
            } else {
              t = group
                .data('removeText')
                .replace(/%s/, u)

              c = w.confirm(t)
            }

            if (c) {
              group
                .slideUp()
                .promise()
                .then(function () {
                  group.remove()
                  group = null

                  if (con.find('.sebco-upload').length < 10) {
                    addTrigger.removeClass('disabled')
                  }
                })
            }
          })

        A.initStaffMediaObjectUploadForm(group)

        group
          .hide(0)
          .promise()
          .then(function () {
            index += 1

            group
              .insertBefore(addTriggerCon)
              .slideDown()

            if (con.find('.sebco-upload').length >= 10) {
              addTrigger.addClass('disabled')
            }
          })
      })

      fm.on('submit', function (e) {
        var v = fm
          .find('.sebco-device-feedback-add-form-commentx')
          .val()

        if ($.trim(v) === '') {
          e.preventDefault()
          w.alert('"Feedback" must not be empty.')

          /* RETURNS */
          return false
        }

        if (fm.data('photosRequired') === true &&
            fm.find('.sebco-upload').length < 1) {
          e.preventDefault()

          w.alert(
            '1 or more photo uploads are required to satisfy' +
              ' this bounty.'
          )

          /* RETURNS */
          return false
        }
      })

      if (fm.data('photosRequired') === true &&
          fm.find('.sebco-upload').length < 1) {
        addTrigger.trigger('click')
      }
    }

    static initDeviceServiceRequest () {
      var closeFn,
        confirmDialog,
        dialogOptions,
        formDialog,
        fm,
        id,
        other,
        otherCon,
        sel,
        winHeight,
        winWidth

      confirmDialog = $('#sebco-dialog-service-request-confirm')
      formDialog = $('#sebco-dialog-service-request-form')
      fm = formDialog.find('#sebco-service-request-form')

      /*
       * Bail if the dialog templates do not exist, in-page.
       */
      if (confirmDialog.length < 1 || formDialog.length < 1) {
        return
      }

      id = fm.find('#sebco-service-request-form-id')
      otherCon = fm.find('#sebco-service-request-form-other-con')
      other = otherCon.find('#sebco-service-request-form-other')
      sel = fm.find('#sebco-service-request-form-issue')

      closeFn = function (e) {
        e.preventDefault()
        $(e.currentTarget).closest('.sebco-dialog').dialog('close')
      }

      dialogOptions = {
        autoOpen: false,
        autoResize: true,
        dialogClass: 'sebco-dialog-con',
        modal: true,
        resizable: false
      }

      winHeight = A.win.height()

      if (winHeight >= 768) {
        dialogOptions.position = { at: 'center top+208' }
      }

      winWidth = A.win.width()

      if (winWidth >= 1200) {
        dialogOptions.width = 992
      } else if (winWidth >= 768) {
        dialogOptions.width = 480
      }

      confirmDialog
        .hide()
        .removeAttr('hidden')
        .dialog(dialogOptions)
        .on('click', '.sebco-btn-close, .sebco-close', closeFn)

      formDialog
        .hide()
        .removeAttr('hidden')
        .dialog(dialogOptions)
        .on('click', '.sebco-btn-close', closeFn)

      fm.on('submit', function (e) {
        e.preventDefault()

        $.ajax({
          url: fm.attr('action'),
          data: {
            id: id.val(),
            issue: sel.val(),
            other: other.val()
          },
          dataType: 'json',
          error: function (x, h, t) {
            debug([x, h, t])
            formDialog.dialog('close')

            confirmDialog
              .find('.sebco-message')
              .each(function (ignore, v) {
                var msg = $(v)

                if (msg.data('messageName') === 'default') {
                  msg.removeAttr('hidden')
                } else {
                  msg.attr('hidden', 'hidden')
                }
              })

            confirmDialog.dialog('open')
          },
          success: function (o) {
            var messageName = 'default'

            formDialog.dialog('close')

            if (lo.isObjectLike(o) &&
                lo.isObjectLike(o.meta) &&
                Number.isFinite(o.meta.status) &&
                o.meta.status < 400 &&
                lo.isObjectLike(o.response) &&
                lo.isString(o.response.showMessage) &&
                $.trim(o.response.showMessage) !== '') {
              messageName = o.response.showMessage
            }

            confirmDialog
              .find('.sebco-message')
              .each(function (ignore, v) {
                var msg = $(v)

                if (messageName === msg.data('messageName')) {
                  if (lo.isString(o.response.deviceName)) {
                    msg
                      .find('.sebco-device-name')
                      .text(o.response.deviceName)
                  }

                  msg.removeAttr('hidden')
                } else {
                  msg.attr('hidden', 'hidden')
                }
              })

            confirmDialog.dialog('open')
          },
          type: 'post'
        })
      })

      sel.on('change', function () {
        if (sel.val().toLowerCase() === 'other') {
          otherCon.slideDown(function () {
            formDialog.dialog('open')
            other.trigger('focus')
          })

          other.prop('disabled', false)
        } else {
          otherCon.slideUp()
          other.prop('disabled', true)
        }
      })

      $(d).on('click', '.sebco-requesting-service', function (e) {
        var link,
          opts,
          tmp,
          tmpl

        e.preventDefault()
        link = $(e.currentTarget)
        tmp = link.data('deviceId')

        if (!Number.isFinite(tmp)) {
          tmp = link.data('id')
        }

        id.val(tmp)

        if (link.hasClass('sebco-btn-device-washer') ||
            link.hasClass('sebco-device-washer-v2')) {
          opts = sel.data('serviceTypesWasher')
        } else {
          opts = sel.data('serviceTypesDryer')
        }

        sel.empty()
        tmpl = w.Handlebars.compile('<option>{{value}}</option>')

        $.each(opts, function (ignore, v) {
          sel.append($(tmpl({ value: v })))
        })

        sel.trigger('change')
        formDialog.dialog('open')
      })
    }

    static initDismissFlashData () {
      $(d)
        .on(
          'click',
          '.sebco-flash-data .close',
          A.handleFlashDataCloseClick
        )
    }

    static initDevicehealthExposeMonitormessage () {
      $(d)
        .on(
          'click',
          'tr.sebco-monitormessage-panel-trigger',
          function (e) {
            if ($(e.target).prop('tagName') !== 'A') {
              $(e.currentTarget)
                .next('tr.sebco-monitormessage-panel')
                .find('.sebco-monitormessage')
                .slideToggle()
            }
          }
        )
    }

    static initDevicehealthSelectLocation () {
      $(d)
        .on(
          'submit',
          '#sebco-devicehealth-select-location-form',
          function (e) {
            var fm,
              locationId

            e.preventDefault()
            fm = $(e.currentTarget)
            locationId = A.parseInt(fm.find('[name="id"]').val())

            if (!lo.isDefined(locationId) ||
                !Number.isFinite(locationId)) {
              locationId = 0
            }

            w.location.href = fm.attr('action') + '/' + locationId
          }
        )
    }

    static initEditDevice () {
      var fieldset = $('#sebco-deviceedit-device-form-fieldset-extended')
      var sourceField
      var targetField
      var targetFieldMidcycle

      /* Prevent double submit (#227) */
      $(d).on('submit', '.sebco-deviceedit-device-form', function (e) {
        $(e.currentTarget)
          .find('input[type="submit"], button[type="submit"]')
          .each(function (ignore, v) {
            var u = $(v)

            u.prop('disabled', true)

            /* Disablement is only temporary */
            A.waitPromise(60000)
              .then(function () {
                u.prop('disabled', false)
              })
          })
      })

      /*
       * Pulse machines.
       */
      if (fieldset.length > 0) {
        $(d)
          .on(
            'change',
            '#sebco-deviceedit-device-form-extended-cycle',
            function (e) {
              var checky = $(e.currentTarget)

              if (checky.is(':checked')) {
                fieldset.prop('disabled', false)
              } else {
                fieldset.prop('disabled', true)

                $(
                  '#sebco-deviceedit-device-form' +
                    '-midcycle-top-off-supported'
                ).val(0)
              }
            }
          )

        $('#sebco-deviceedit-device-form-extended-cycle')
          .trigger('change')

        $(d)
          .on(
            'change',
            '#sebco-deviceedit-device-form-machine-type',
            function (e) {
              var sel = $(e.currentTarget)
              var v = $(
                '#sebco-deviceedit-device-form' +
                  '-midcycle-top-off-supported'
              )

              if (sel.val() !== 'dryer') {
                v.val(0).prop('disabled', true)
              } else {
                v.prop('disabled', false)
              }
            }
          )

        $('#sebco-deviceedit-device-form-machine-type')
          .trigger('change')

        /* RETURNS */
        return
      }

      /*
       * Enabled dynamic detergent price enable/disablement.
       */
      $('#sebco-deviceedit-device-form-detergent')
        .each(function (ignore, el) {
          var jq = $(el)
          var target = $('#' + jq.data('targetId'))

          jq.on('change', function () {
            if (A.parseInt(jq.val()) === 1) {
              target.prop('disabled', false)
            } else {
              target.prop('disabled', true)
            }
          })

          jq.trigger('change')
        })

      /*
       * Check for Maytag washer.
       */
      sourceField = $('#sebco-deviceedit-device-form-extended-cycle')

      /*
       * Check for Maytag dryer.
       */
      if (sourceField.length < 1) {
        sourceField = $('#sebco-deviceedit-device-form-extended-cycle-style')
      }

      if (sourceField.length > 0) {
        targetField = $('#' + sourceField.data('targetId'))

        targetFieldMidcycle = $(
          '#sebco-deviceedit-device-form' +
            '-midcycle-top-off-supported'
        )

        sourceField.on('change', function () {
          var v = A.parseInt(sourceField.val())

          if (Number.isFinite(v)) {
            if (v === 1) {
              /*
               * Don't enable the top-off price field if this is a W-cloud coin
               * device. It must not be user-configurable, as the value is
               * locked to the device's `coin1` value.
               */
              if (targetField.attr('data-wcloud-coin') !== 'true') {
                targetField.prop('disabled', false)
              }

              targetFieldMidcycle.prop('disabled', false)
            } else {
              targetField.prop('disabled', true)

              targetFieldMidcycle
                .val(0)
                .prop('disabled', true)
            }
          }
        })

        sourceField.trigger('change')
      }

      /*
       * W-cloud device
       */
      if (d.querySelector('.sebco-deviceedit-device-form-payment-type')) {
        $(d).on(
          'change',
          '.sebco-deviceedit-device-form-payment-type',
          A.handleDeviceEditPaymentTypeChange
        )
      }

      Array.from(d.querySelectorAll('.sebco-deviceedit-device-form'))
        .filter(function (f) {
          return (
            f &&
            f.dataset &&
            f.dataset.machineType &&
            f.dataset.connectionType &&
            (A.CONNECTION_TYPE_WCLOUD === A.parseInt(f.dataset.connectionType))
          )
        })
        .forEach(function (f) {
          switch (f.dataset.machineType) {
            case 'dryer':
              lo.noop(new DeviceEditDryer(f))
              break
            case 'washer':
              lo.noop(new DeviceEditWasher(f))
              break
          }
        })

      /*
      if (d.querySelector('.sebco-deviceedit-device-form-coin-1')) {
        $(d).on(
          'change',
          '.sebco-deviceedit-device-form-coin-1',
          a.handleDeviceEditCoin1Change
        );

        $(d).on(
          'change',
          '.sebco-deviceedit-device-form-price',
          a.handleDeviceEditPriceChange
        );
      }
      */
    }

    static initFakeFinishPayLoading () {
      var selector = '#sebco-finish-pay-form .sebco-btn-finish-pay, ' +
        '#sebco-finish-pay-form-top .sebco-btn-finish-pay'

      $(d)
        .on(
          'click',
          selector,
          function () {
            try {
              A.showLoadingDialog()
            } catch (ignore) {}
          }
        )
    }

    static initFigureQuotePreview () {
      var fm = $('.sebco-use-figure-quote-preview')
      var con
      var preview
      var previewAuthor
      var previewText

      if (fm.length < 1) {
        /* RETURNS */
        return
      }

      con = $('#sebco-figure-quote-preview-con')
      preview = $($('#template-sebco-figure-quote-preview').html())
      previewAuthor = preview.find('.sebco-quote-author .sebco-text')
      previewText = preview.find('.sebco-quote-text .sebco-text')

      fm
        .find('.sebco-use-figure-quote-preview-author')
        .on('keyup', function (e) {
          previewAuthor.text($(e.currentTarget).val())
        })
        .trigger('keyup')

      fm
        .find('.sebco-use-figure-quote-preview-text')
        .on('keyup', function (e) {
          previewText.text($(e.currentTarget).val())
        })
        .trigger('keyup')

      con.append(preview)
    }

    static initForgotPasswordLink (v) {
      if (lo.isDefined(v.pathname)) {
        $(v).data('originalUrl', v.pathname)
          .on('click', A.handleForgotPasswordLinkClick)
      }
    }

    /*
     * Initializes "forgot password" email field pre-population. If a page
     * contains one or more links bearing the class
     * "sebco-forgot-password-link", an event handler is set for it that will
     * search the page for an email form field. If the form field is present and
     * contains user input, that value will be passed to the forgot password
     * page, and used to pre-populate the email form field on the forgot
     * password page.
     */
    static initForgotPasswordLinks () {
      $('a.sebco-forgot-password-link').each(function (ignore, v) {
        A.initForgotPasswordLink(v)
      })
    }

    /*
     * Initializes date formatting helper for input elements bearing the class
     * "sebco-input-cc-expires".
     */
    static initFormatterCCExpires () {
      $(d)
        .on(
          'keyup',
          '.sebco-input-cc-expires',
          function (e) {
            var input = $(e.currentTarget)
            var key = e.keyCode || e.charCode
            var txt = input.val()

            if (key !== 8 &&
                key !== 46 &&
                txt.match(/^[0-9][0-9]$/) !== null) {
              input.val(txt + '/')
            }
          }
        )
    }

    static initFullPageBackNav () {
      var body,
        url

      body = $('body:first')

      if (body.data('fullPageBackNav') !== true) {
        /* RETURNS */
        return
      }

      if (body.data('isSignout') === true) {
        url = '/'
      } else {
        url = $('.sebco-back-bar a:first, .sebco-bar-bar-2 a:first')
          .attr('href')
      }

      if (lo.isString(url) && $.trim(url) !== '') {
        body.on('click', function () {
          w.location.href = url
        })
      }
    }

    static initGenericUrlChanger () {
      if (!d.querySelectorAll('.sebco-generic-url-changer').length) {
        return
      }

      $(d).on(
        'change',
        '.sebco-generic-url-changer',
        A.handleGenericUrlChangerChange
      )
    }

    static initKeyfobHousekeepingSubmit () {
      $(d)
        .on(
          'change',
          '.sebco-housekeeping-toggle',
          function (e) {
            var fm,
              check

            check = $(e.currentTarget)
            fm = check.closest('form')

            if (fm.length > 0) {
              fm.trigger('submit')
            }
          }
        )
    }

    static initKeyfobScanTrigger () {
      var dia
      var fm = $('#sebco-kiosk-login-scan-form')
      var input = fm.find('#sebco-kiosk-login-scan-form-rfid')

      if (fm.length > 0) {
        dia = $($('#template-sebco-dialog-signing-in').html()).hide()
        $('body').first().append(dia)

        fm.on(
          'submit',
          function () {
            A.hideScanningDialog()
            A.showSigningInDialog()
            return true
          }
        )

        $('.sebco-keyfob-scan-trigger')
          .on(
            'click',
            function (e) {
              var t

              e.stopPropagation()
              e.preventDefault()
              A.queueScreenSaver()
              A.showScanningDialog()
              input.val('').trigger('focus')

              /*
               * Dismiss dialog after 30 seconds.
               */
              t = w.setTimeout(
                function () {
                  w.clearTimeout(t)
                  t = null
                  A.hideScanningDialog()
                },
                30000
              )
            }
          )
      }
    }

    static initKeyfobScanConstant () {
      var fm = $('#sebco-kiosk-login-scan-form')
      var input = fm.find('#sebco-kiosk-login-scan-form-rfid')

      if (fm.length > 0 && input.length > 0) {
        input.val('').trigger('focus')
      }
    }

    /**
     * @param {HTMLInputElement} v
     * @returns
     */
    static initKioskHardwareLookup (v) {
      const lookup = $(v)

      if (lookup.length < 1) {
        return
      }

      const url = v.dataset.lookupEndpoint
      const idField = d.querySelector(v.dataset.lookupIdField)
      const focusNext = $(v.dataset.lookupFocusNext)
      const submitOnSelect = lookup.data('lookupSubmitOnSelect')
      let appendTo = $(v.closest('.sebco-staff-wrapper'))

      if (appendTo.length < 1) {
        appendTo = $(v.closest('.sebco-customer-detail-wrapper'))
      }

      if (appendTo.length < 1) {
        appendTo = $(v.closest('.sebco-device-history-wrapper'))
      }

      lookup.autocomplete({
        appendTo,
        classes: { 'ui-autocomplete': 'sebco-autocomplete-results' },
        minLength: 1,
        position: {
          at: 'left bottom',
          collision: 'flipfit',
          my: 'left top'
        },
        select: (ignore, ui) => {
          if (idField) {
            idField.value = ui.item.id
          }

          v.value = ui.item.label

          if (focusNext.length > 0) {
            focusNext.trigger('focus')
          }

          if (submitOnSelect === true) {
            lookup.closest('form').trigger('submit')
          }

          return false
        },
        source: (request, response) => {
          let t = []

          A.ajaxPromise({ url, data: { terms: request.term } })
            .then(o => {
              if (Array.isArray(o.response)) {
                t = o.response.map(v => {
                  const value = `${v.name} / ${v.mac}`

                  return {
                    value,
                    label: value,
                    id: v.id
                  }
                })
              }

              response(t)
            })
            .fail(function (err) {
              if (lo.isDefined(err)) {
                debug(err)
              }

              response(t)
            })
        }
      })
    }

    static initKioskHardwareLookups () {
      d.querySelectorAll('.sebco-staff-kiosk-hardware-lookup')
        .forEach(v => A.initKioskHardwareLookup(v))
    }

    static initKioskLookup () {
      $('.sebco-staff-kiosk-lookup').each(function (ignore, v) {
        var appendTo
        var lookup = $(v)

        appendTo = lookup.closest('.sebco-staff-wrapper')

        if (appendTo.length < 1) {
          appendTo = lookup.closest('.sebco-customer-detail-wrapper')
        }

        if (appendTo.length < 1) {
          appendTo = lookup.closest('.sebco-device-history-wrapper')
        }

        lookup.autocomplete({
          appendTo,
          classes: { 'ui-autocomplete': 'sebco-autocomplete-results' },
          minLength: 1,
          position: {
            my: 'left top',
            at: 'left bottom',
            collision: 'flipfit'
          },
          select: function (ignore, ui) {
            var focusNext

            $(lookup.data('lookupIdField')).val(ui.item.id)
            lookup.val(ui.item.label)
            focusNext = $(lookup.data('lookupFocusNext'))

            if (focusNext.length > 0) {
              focusNext.trigger('focus')
            }

            if (lookup.data('lookupSubmitOnSelect') === true) {
              lookup.closest('form').trigger('submit')
            }

            return false
          },
          source: function (request, response) {
            $.ajax({
              url: lookup.data('lookupEndpoint'),
              data: { terms: request.term },
              dataType: 'json',
              error: function (x, t, e) {
                debug([x, t, e])
                response([])
              },
              success: function (o) {
                var kiosks = []

                if (
                  lo.isDefined(o) &&
                  lo.isDefined(o.response) &&
                  $.isArray(o.response)
                ) {
                  $.each(
                    o.response,
                    function (ignore, kiosk) {
                      var txt = kiosk.name

                      kiosks.push({
                        value: txt,
                        label: txt,
                        id: kiosk.id
                      })
                    }
                  )
                }

                response(kiosks)
              }
            })
          }
        })
      })
    }

    static initLatLongLookup () {
      $('.sebco-staff-lat-long-lookup').each(function (ignore, v) {
        var doMap,
          endpoint,
          key,
          latitudeField,
          longitudeField,
          mapCon,
          trig

        trig = $(v)
        endpoint = trig.data('endpoint')
        key = trig.data('googleBrowserKey')
        latitudeField = $(trig.data('latitudeField'))
        longitudeField = $(trig.data('longitudeField'))
        mapCon = $(trig.data('mapCon'))

        doMap = function (data) {
          var height,
            j,
            ll,
            o,
            t,
            tmp,
            width

          if (mapCon.length > 0) {
            mapCon.css({ minHeight: 1 })
            width = A.parseInt(mapCon.outerWidth())

            if (Number.isFinite(width)) {
              mapCon.slideUp()
              height = A.parseInt((width / 16) * 9)

              t = w.Handlebars.compile(
                $('#template-sebco-location-map-static').html()
              )

              ll = data.latitude + ',' + data.longitude

              o = {
                address: data.address,
                hrefParams: {
                  ll,
                  q: data.address,
                  z: 14
                },
                srcParams: {
                  center: ll,
                  format: 'jpg',
                  key,
                  markers: data.address,
                  scale: 2,
                  size: width + 'x' + height,
                  zoom: 15
                }
              }

              tmp = []

              $.each(o.hrefParams, function (k, v) {
                tmp.push(
                  encodeURIComponent(k) +
                    '=' +
                    encodeURIComponent(v)
                )
              })

              o.hrefParams = tmp.join('&')

              tmp = []

              $.each(o.srcParams, function (k, v) {
                tmp.push(
                  encodeURIComponent(k) +
                    '=' +
                    encodeURIComponent(v)
                )
              })

              o.srcParams = tmp.join('&')
              j = $(t(o))
              tmp = new Image()

              $(tmp).on('load', function () {
                mapCon.find('.sebco-location-map-static').remove()
                mapCon.append(j).slideDown()
              })

              tmp.src = j.find('img').attr('src')
            }
          }
        }

        trig.on('click', function (e) {
          var address

          e.preventDefault()

          address = $(trig.data('addressField')).val() +
            ', ' +
            $(trig.data('cityField')).val() +
            ', ' +
            $(trig.data('stateField')).val()

          $.ajax({
            url: endpoint,
            data: { address },
            dataType: 'json',
            error: function (x, t, e) {
              debug([x, t, e])
            },
            success: function (o) {
              if (lo.isObjectLike(o) &&
                  lo.isObjectLike(o.meta) &&
                  Number.isFinite(o.meta.status) &&
                  o.meta.status < 400) {
                latitudeField.val(o.response.latitude)
                longitudeField.val(o.response.longitude)
                doMap(o.response)
              }
            }
          })
        })
      })
    }

    static initLocationLookup () {
      $('.sebco-staff-location-lookup').each(function (ignore, v) {
        var appendTo
        var lookup = $(v)
        var fieldAddress = $(lookup.data('lookupFieldAddress'))
        var fieldCity = $(lookup.data('lookupFieldCity'))
        var fieldState = $(lookup.data('lookupFieldState'))
        var fieldZip = $(lookup.data('lookupFieldZip'))
        var nameOnly = lookup.data('lookupNameOnly')
        var organizationId = lookup.data('lookupOrganizationId')

        appendTo = lookup.closest('.sebco-staff-wrapper')

        if (appendTo.length < 1) {
          appendTo = lookup.closest('.sebco-customer-detail-wrapper')
        }

        if (appendTo.length < 1) {
          appendTo = lookup.closest('.sebco-technician-wrapper')
        }

        lookup.autocomplete({
          appendTo,
          classes: { 'ui-autocomplete': 'sebco-autocomplete-results' },
          minLength: 1,
          position: {
            my: 'left top',
            at: 'left bottom',
            collision: 'flipfit'
          },
          select: function (ignore, ui) {
            var focusNext

            $(lookup.data('lookupIdField')).val(ui.item.id)
            lookup.val(ui.item.label)

            if (fieldAddress.length > 0) {
              fieldAddress.val(ui.item.address)
            }

            if (fieldCity.length > 0) {
              fieldCity.val(ui.item.city)
            }

            if (fieldState.length > 0) {
              fieldState.val(ui.item.state)
            }

            if (fieldZip.length > 0) {
              fieldZip.val(ui.item.zip)
            }

            focusNext = $(lookup.data('lookupFocusNext'))

            if (focusNext.length > 0) {
              focusNext.trigger('focus')
            }

            if (lookup.data('lookupSubmitOnSelect') === true) {
              lookup.closest('form').trigger('submit')
            }

            return false
          },
          source: function (request, response) {
            var opts = { terms: request.term }

            if (Number.isFinite(organizationId)) {
              opts.organizationId = organizationId
            }

            $.ajax({
              url: lookup.data('lookupEndpoint'),
              data: opts,
              dataType: 'json',
              error: function (x, t, e) {
                debug([x, t, e])
                response([])
              },
              success: function (o) {
                var locations = []

                if (
                  lo.isDefined(o) &&
                  lo.isDefined(o.response) &&
                  $.isArray(o.response)
                ) {
                  $.each(
                    o.response,
                    function (ignore, loc) {
                      var txt = loc.name

                      if (nameOnly !== true) {
                        txt += ', ' +
                          loc.city +
                          ', ' +
                          loc.state +
                          ', ' +
                          loc.zip
                      }

                      locations.push({
                        address: loc.address,
                        city: loc.city,
                        id: loc.id,
                        label: txt,
                        state: loc.state,
                        value: txt,
                        zip: loc.zip
                      })
                    }
                  )
                }

                response(locations)
              }
            })
          }
        })
      })
    }

    static initMmpCustomerReportForm () {
      const fm = d.getElementsByClassName('mmp-customer-report-form')

      if (!fm.length) {
        return
      }

      const [type] =
        fm[0].getElementsByClassName('mmp-customer-report-form-type')

      if (type) {
        type.addEventListener('input', A.handleMmpCustomerTypeInput)
        type.dispatchEvent(new Event('input'))
      }
    }

    static initMmpLoadingForms () {
      const sel = '.mmp-loading-form'

      if (d.querySelector(sel)) {
        $(d).on('submit', sel, A.handleMmpLoadingFormSubmit)
      }
    }

    static initMmpSelectAllTriggers () {
      $(d).on(
        'input',
        '.mmp-select-all-trigger',
        A.handleMmpSelectAllTriggerInput
      )
    }

    static initMmpUserLookupForms () {
      if (!d.querySelector('.mmp-user-lookup-form')) {
        return
      }

      $(d).on(
        'submit',
        '.mmp-user-lookup-form',
        A.handleMmpUserLookupFormSubmit
      )
    }

    static initMonitorMessageTwisty () {
      $('.sebco-panel-monitormessage').each(function (ignore, v) {
        var panel = $(v)
        var heading = panel.find('.panel-heading')
        var tableCon = panel.find('.sebco-table-con')

        tableCon.hide()

        heading
          .on('click', function () {
            panel.toggleClass('sebco-open')

            if (panel.hasClass('sebco-open')) {
              tableCon.slideDown()
            } else {
              tableCon.slideUp()
            }
          })
      })
    }

    static initNav () {
      var con
      var submenu = $('#sebco-nav-payment-submenu')

      /*
       * Bail if submenu does not exist.
       */
      if (submenu.length < 1) {
        return
      }

      con = submenu.closest('li')
      submenu.css('display', 'none').removeClass('sebco-no-display')

      $(d)
        .on(
          'click',
          '.sebco-nav-payment-submenu-toggle',
          function (e) {
            e.preventDefault()

            if (con.hasClass('sebco-expanded')) {
              con.removeClass('sebco-expanded')
              submenu.slideUp('fast')
            } else {
              con.addClass('sebco-expanded')
              submenu.slideDown('fast')
            }
          }
        )
    }

    static initOnScreenKeyboard () {
      var firstAutofocus = null

      if (!$('body:first').data('isKiosk')) {
        return
      }

      $('input, textarea').each(function (ignore, v) {
        var input = $(v)
        var con = $('#sebco-keyboard-con')
        var options = {
          autoAccept: true,
          tabNavigation: true,
          usePreview: false
        }
        var type = input.attr('type')

        /*
         * May need to add touchmove (and touchstart) to the list of prevented
         * events. Test first.
         */
        $(d).on('wheel', '.ui-keyboard', function (e) {
          e.preventDefault()
        })

        if ((firstAutofocus === null) && input.is('[autofocus]')) {
          firstAutofocus = input
        }

        options.layout = input.data('keyboardLayout')

        if (!lo.isDefined(type)) {
          type = 'text'
        }

        if (!lo.isDefined(options.layout) || (options.layout === 'qwerty')) {
          options.layout = 'custom'
          options.appendTo = con
          options.position = false

          options.visible = function () {
            $('.sebco-page-main').first()
              .css('padding-bottom', con.height())

            A.centerVertical(input)
          }

          options.display = {
            accept: 'Done',
            bksp: '\u232B',
            default: 'ABC',
            enter: 'Return',
            meta1: '.?123',
            meta2: '#+=',
            next: 'Next'
          }

          options.customLayout = {
            default: [
              'q w e r t y u i o p {bksp}',
              'a s d f g h j k l {enter}',
              '{s} z x c v b n m @ . {s}',
              '{meta1} {space} _ - .com {accept} {next}'
            ],
            shift: [
              'Q W E R T Y U I O P {bksp}',
              'A S D F G H J K L {enter}',
              '{s} Z X C V B N M @ . {s}',
              '{meta1} {space} _ - .com {accept} {next}'
            ],
            meta1: [
              '1 2 3 4 5 6 7 8 9 0 {bksp}',
              '` | { } % ^ * / \' {enter}',
              '{meta2} $ & ~ # = + @ . {meta2}',
              '{default} {space} ! ? .com {accept} {next}'
            ],
            meta2: [
              '[ ] { } \u2039 \u203a ^ * " , {bksp}',
              '\\ | / < > $ \u00a3 \u00a5 \u2022 {enter}',
              '{meta1} \u20ac & ~ # = + @ . {meta1}',
              '{default} {space} ! ? .com {accept} {next}'
            ]
          }
        } else if (options.layout === 'num') {
          options.layout = 'custom'

          options.display = {
            accept: 'OK',
            bksp: '\u232B',
            next: 'Next'
          }

          options.customLayout = {
            default: [
              '1 2 3',
              '4 5 6',
              '7 8 9',
              '{bksp} 0 {next}'
            ]
          }
        }

        switch (type) {
          case 'email':
            /* FALLTHROUGH */
          case 'password':
            /* FALLTHROUGH */
          case 'tel':
            /* FALLTHROUGH */
          case 'text':
            if (options.layout !== false) {
              input.keyboard(options)
            }
            break
        }
      })

      if (
        (firstAutofocus !== null) &&
        lo.isFunction(firstAutofocus.getkeyboard)
      ) {
        firstAutofocus.trigger('focus')
      }
    }

    static initPaymentMethodFavorite () {
      $(d)
        .on(
          'click',
          '.sebco-favorite-widget',
          A.handlePaymentMethodFavorite
        )
    }

    static initPaymentMethodInputCleanup () {
      var fms = $('.sebco-add-card-form')

      if (fms.length < 1) {
        fms = $('.sebco-paymentmethod-edit-form')

        if (fms.length < 1) {
          /* RETURNS */
          return
        }
      }

      fms.each(function (ignore, fm) {
        $(fm).on('keyup', '.sebco-trim-input', function (e) {
          var t = $(e.currentTarget)
          var v = t.val()

          if (lo.isString(v)) {
            t.val($.trim(v))
          }
        })
      })
    }

    static initPostFinishPayDelay () {
      var content = $('.sebco-post-finish-pay-content')
      var count
      var left
      var loader = $('.sebco-post-finish-pay-loader')
      var navWidth
      var progressBar
      var progressBarCallback
      var progressBarDelay
      var progressText
      var tmpDemoPostFinishPayDelay
      var top

      if (loader.length > 0 && content.length > 0) {
        navWidth = $('.sebco-nav-col').outerWidth()

        if (!Number.isFinite(navWidth) || navWidth < 0) {
          navWidth = 0
        }

        left = (A.win.width() - navWidth) - loader.outerWidth()
        top = A.win.height() - loader.outerHeight()
        left = A.parseInt(left / 2) + navWidth
        top = A.parseInt(top / 2)

        if (!Number.isFinite(left) || left < 0) {
          left = 0
        }

        if (!Number.isFinite(top) || top < 0) {
          top = 0
        }

        tmpDemoPostFinishPayDelay = $('body:first')
          .data('demoPostFinishPayDelay')

        if (Number.isFinite(tmpDemoPostFinishPayDelay)) {
          A.postFinishPayDelay = tmpDemoPostFinishPayDelay
        }

        progressBar = loader.find('.progress-bar')
        progressText = loader.find('.sebco-progress-text')
        progressBarDelay = A.parseInt(A.postFinishPayDelay / 10)
        count = 0

        if (!Number.isFinite(progressBarDelay) || progressBarDelay < 400) {
          progressBarDelay = 400
        }

        progressBarCallback = function () {
          progressBar
            .attr('aria-valuenow', count)
            .css({ width: count + '%' })

          progressText.text(count)
          count += 10

          if (count < 100) {
            w.setTimeout(progressBarCallback, progressBarDelay)
          }
        }

        progressBarCallback()

        loader
          .css({ left, top })
          .css({ opacity: 1 })

        w.setTimeout(
          function () {
            loader.animate({ opacity: 0 }, function () {
              var t

              loader.remove()
              t = setTimeout(A.showAutoSignOutDialog, 10000)

              $('body:first')
                .data('timerShowAutoSignOutDialog', t)
            })

            content.animate({ opacity: 1 })
          },
          A.postFinishPayDelay
        )
      }
    }

    static initPreventKioskImageDrag () {
      $(d)
        .on(
          'dragstart',
          '[draggable="false"], [data-is-kiosk="true"] a',
          function (e) {
            e.preventDefault()
          }
        )
    }

    static initTargetLookup (v) {
      var appendTo
      var focusNext
      var idField
      var lookup = $(v)
      var params
      var submitOnSelect

      if (lookup.length < 1) {
        /* RETURNS */
        return
      }

      focusNext = $(lookup.data('lookupFocusNext'))
      idField = $(lookup.data('lookupIdField'))
      params = lookup.data('lookupParams')
      submitOnSelect = lookup.data('lookupSubmitOnSelect')
      appendTo = lookup.closest('.sebco-staff-wrapper')

      if (!lo.isObjectLike(params)) {
        params = {}
      }

      if (appendTo.length < 1) {
        appendTo = lookup.closest('.sebco-customer-detail-wrapper')
      }

      if (appendTo.length < 1) {
        appendTo = lookup.closest('.sebco-technician-wrapper')
      }

      lookup.autocomplete({
        appendTo,
        classes: { 'ui-autocomplete': 'sebco-autocomplete-results' },
        minLength: 1,
        position: {
          my: 'left top',
          at: 'left bottom',
          collision: 'flipfit'
        },
        select: function (ignore, ui) {
          idField.val(ui.item.id)
          lookup.val(ui.item.label)

          if (focusNext.length > 0) {
            focusNext.trigger('focus')
          }

          if (submitOnSelect === true) {
            lookup.closest('form').trigger('submit')
          }

          return false
        },
        source: function (request, response) {
          A
            .ajaxPromise({
              url: lookup.data('endpoint'),
              data: $.extend(params, { terms: request.term })
            })
            .then(function (o) {
              var t = []

              if (
                lo.isDefined(o) &&
                lo.isDefined(o.response) &&
                $.isArray(o.response)
              ) {
                $.each(
                  o.response,
                  function (ignore, v) {
                    t.push({
                      id: v.id,
                      label: v.name
                    })
                  }
                )
              }

              response(t)
            })
            .fail(function (err) {
              if (lo.isDefined(err)) {
                debug(err)
              }

              response([])
            })
        }
      })
    }

    static initTenantReferralPromo () {
      var tmpl = $('#template-sebco-tenant-referral-promo')

      if (tmpl.length > 0) {
        tmpl = w.Handlebars.compile(tmpl.html())
        $('.sebco-content-col').append(tmpl({}))
      }
    }

    static initRegistrationForm () {
      var fieldsets,
        fm,
        inputs,
        submits

      fm = $('#sebco-register-form')
      fieldsets = fm.find('.sebco-register-fieldset')
      inputs = fm.find('input[required="required"]')

      /*
       * Bail if the "oninput" event is not supported.
       */
      if ((inputs.length < 1) || !lo.isDefined(inputs.get(0).oninput)) {
        return
      }

      submits = fm.find('button[type="submit"]')
      submits.prop('disabled', true)

      inputs.on('input change', function () {
        var empties,
          i,
          fieldset

        empties = inputs.filter(function (ignore, v) {
          return $(v).val() === ''
        })

        if (empties.length < 1) {
          fieldsets.each(function (ignore, v) {
            $(v).prop('disabled', false)
          })

          submits.prop('disabled', false)
        } else {
          fieldset = empties.first().closest('fieldset')
          i = fieldset.data('registerFieldset')

          fieldsets.each(function (ignore, v) {
            var tmp = $(v)

            if (tmp.data('registerFieldset') > i) {
              tmp.prop('disabled', true)
            } else {
              tmp.prop('disabled', false)
            }
          })

          submits.prop('disabled', true)
        }
      })

      inputs.first().trigger('input')
    }

    static initRequireConfirm () {
      $(d)
        .on(
          'submit',
          'form.sebco-form-require-confirm',
          function (e) {
            var confirmed
            var fm = $(e.currentTarget)

            confirmed = w.confirm(fm.data('requireConfirmText'))

            if (confirmed) {
              if (fm.data('requireConfirmDisableSubmit') === true) {
                fm
                  .find('button[type="submit"]')
                  .prop('disabled', true)
              }

              /* RETURNS */
              return true
            }

            return false
          }
        )
    }

    static initResponsiveNavToggle () {
      $(d).on(
        'click',
        '.sebco-responsive-menu-trigger, .sebco-nav-col .sebco-btn-close',
        (e) => {
          e.preventDefault()
          A.toggleResponsiveNav()
        }
      )

      if (d.querySelectorAll('.mmp-responsive-menu-trigger').length > 0) {
        $(d).on('click', '.mmp-responsive-menu-trigger', (e) => {
          e.preventDefault()
          A.toggleResponsiveNavMmp()
        })
      }
    }

    static initRoomLookup () {
      $('.sebco-staff-room-lookup').each(function (ignore, v) {
        var appendTo
        var lookup = $(v)

        appendTo = lookup.closest('.sebco-staff-wrapper')

        if (appendTo.length < 1) {
          appendTo = lookup.closest('.sebco-customer-detail-wrapper')
        }

        if (appendTo.length < 1) {
          appendTo = lookup.closest('.sebco-device-history-wrapper')
        }

        lookup.autocomplete({
          appendTo,
          classes: { 'ui-autocomplete': 'sebco-autocomplete-results' },
          minLength: 1,
          position: {
            my: 'left top',
            at: 'left bottom',
            collision: 'flipfit'
          },
          select: function (ignore, ui) {
            var focusNext

            $(lookup.data('lookupIdField')).val(ui.item.id)
            lookup.val(ui.item.label)
            focusNext = $(lookup.data('lookupFocusNext'))

            if (focusNext.length > 0) {
              focusNext.trigger('focus')
            }

            if (lookup.data('lookupSubmitOnSelect') === true) {
              lookup.closest('form').trigger('submit')
            }

            return false
          },
          source: function (request, response) {
            $.ajax({
              url: lookup.data('lookupEndpoint'),
              data: { terms: request.term },
              dataType: 'json',
              error: function (x, t, e) {
                debug([x, t, e])
                response([])
              },
              success: function (o) {
                var rooms = []

                if (
                  lo.isDefined(o) &&
                  lo.isDefined(o.response) &&
                  $.isArray(o.response)
                ) {
                  $.each(
                    o.response,
                    function (ignore, room) {
                      var txt = room.name +
                        ', ' +
                        room.locationName

                      rooms.push({
                        value: txt,
                        label: txt,
                        id: room.id
                      })
                    }
                  )
                }

                response(rooms)
              }
            })
          }
        })
      })
    }

    /*
     * Creates a new screen saver jQuery instance, inserts it into the current
     * page, and queues it to be displayed after "a.screenSaverDelay"
     * milliseconds.
     */
    static initScreenSaver () {
      var screenSaver = A.createScreenSaver()

      if (screenSaver === null) {
        /* RETURNS */
        return
      }

      $('body').first().append(screenSaver)
      A.queueScreenSaver()
    }

    static initSendMobileAppLink () {
      var closeFn
      var con
      var links = $('.sebco-send-mobile-app-link')
      var tmpl

      if (links.length > 0) {
        links = null

        tmpl = w
          .Handlebars
          .compile($('#template-sebco-dialog-flash').html())

        closeFn = function () {
          con.fadeOut('fast', function () {
            try {
              con.dialog('close')
            } catch (ignore) {}

            try {
              con.dialog('destroy')
            } catch (ignore) {}

            try {
              con.remove()
            } catch (ignore) {}
          })
        }

        $(d).on('click', '.sebco-send-mobile-app-link', function (e) {
          var link = $(e.currentTarget)

          $.ajax({
            url: link.data('endpoint'),
            dataType: 'json',
            data: { platform: link.data('platform') },
            error: function () {
              var dialogOpts

              con = $(tmpl(link.data('failure')))
              con.hide()
              $('#sebco-content-col').append(con)

              dialogOpts = {
                dialogClass: 'sebco-dialog-flash-con'
              }

              if (A.win.width() >= 1200) {
                dialogOpts.width = 512
              }

              con
                .on('click', '.close', closeFn)
                .dialog(dialogOpts)
            },
            success: function (o) {
              var dialogOpts,
                re,
                tmplData

              if (lo.isObjectLike(o) &&
                  lo.isObjectLike(o.meta) &&
                  Number.isFinite(o.meta.status)) {
                if (o.meta.status >= 400) {
                  tmplData = link.data('failureEnhanced')
                } else {
                  tmplData = link.data('success')
                }

                re = /\{\{mobilephone\}\}/

                tmplData.message = tmplData
                  .message
                  .replace(re, o.response)
              } else {
                tmplData = link.data('failure')
              }

              con = $(tmpl(tmplData))
              con.hide()
              $('#sebco-content-col').append(con)

              dialogOpts = {
                dialogClass: 'sebco-dialog-flash-con'
              }

              if (A.win.width() >= 1200) {
                dialogOpts.width = 512
              }

              con
                .on('click', '.close', closeFn)
                .dialog(dialogOpts)
            },
            type: 'POST'
          })
        })
      }
    }

    static initSignoutForward () {
      var b = $('body:first')
      var delay
      var url

      if (b.data('isSignout') === true) {
        url = (b.data('isKiosk') === true)
          ? '/kiosk'
          : '/'

        delay = b.data('signoutForwardDelay')

        if (!Number.isFinite(delay)) {
          delay = 5000
        }

        w.setTimeout(
          function () {
            w.location.href = url
          },
          delay
        )
      }
    }

    static initSiteCode () {
      var fm = $('#sebco-register-form, #sebco-sitecode-form')
      var siteCode
      var locationId

      if (fm.length < 1) {
        return
      }

      siteCode = fm
        .find(
          '#sebco-register-form-sitecode,' +
            ' #sebco-sitecode-form-sitecode'
        )

      if (siteCode.length < 1) {
        return
      }

      locationId = fm
        .find(
          '#sebco-register-form-location-id,' +
            ' #sebco-sitecode-form-location-id'
        )

      if (locationId.length < 1) {
        return
      }

      siteCode.autocomplete({
        appendTo: siteCode.parent(),
        change: function () {
          var key = siteCode.val()
          var locations = siteCode.data('locations')

          if (lo.isObjectLike(locations) && lo.isDefined(locations[key])) {
            locationId.val(locations[key])
          } else {
            locationId.val('')
          }
        },
        minLength: 4,
        source: function (request, response) {
          A
            .ajaxPromise({
              data: { code: request.term },
              url: '/api/v1/sites'
            })
            .then(function (o) {
              var locations,
                locationNames

              if (
                lo.isDefined(o) &&
                lo.isDefined(o.response) &&
                $.isArray(o.response)
              ) {
                locations = {}
                locationNames = []

                $.each(o.response, function (ignore, v) {
                  if (lo.isDefined(v) && lo.isString(v.name)) {
                    locations[v.name] = v.id
                    locationNames.push(v.name)
                  }
                })

                siteCode.data('locations', locations)
                response(locationNames)
              }
            })
            .fail(function (err) {
              if (lo.isDefined(err)) {
                debug(err)
              }

              response([])
            })
        }
      })
    }

    static initSortableTable () {
      const table = window.document.querySelector('table.mmp-sortable-table')

      if (table instanceof HTMLTableElement) {
        /**
         * @typedef InputSort
         * @property {number} direction
         * @property {string} field
         */
        /** @type {InputSort[] | undefined} */
        let order

        try {
          order = JSON.parse(table.dataset.order)
        } catch (err) {
          this.warn(err)
        }

        if (Array.isArray(order)) {
          /** @type Record<string, number> */
          const orderMap = Object.fromEntries(
            order.filter(o => (
              lo.isString(o.field) && Number.isFinite(o.direction)
            )).map(o => [o.field, o.direction])
          )

          for (const o of table.querySelectorAll('thead a[data-field]')) {
            if (o instanceof HTMLElement) {
              const { dataset } = o

              dataset.direction = Number.isFinite(orderMap[dataset.field])
                ? orderMap[dataset.field].toString(10)
                : ''
              o.addEventListener('click', A.handleSortableTableClick)
            }
          }
        }
      }
    }

    static initStaffActivityHeatmap () {
      var con = $('#sebco-activity-heatmap-con')
      var locationSelector

      if (con.length < 1) {
        /* RETURNS */
        return
      }

      locationSelector = $(
        '#sebco-staff-select-activity-heatmap-location-form-location'
      )

      locationSelector
        .on(
          'change',
          function () {
            var locationId = locationSelector.val()
            var url = locationSelector.data('urlTemplate')

            locationId = locationSelector.val()

            if (lo.isString(locationId)) {
              locationId = locationId.trim()
            }

            if (locationId !== '') {
              url += '/' + encodeURIComponent(locationId)
            }

            w.location.pathname = A.sanitizeUrl(url)
          }
        )

      $.ajax({
        url: con.data('endpoint'),
        data: {
          locationId: con.data('locationId')
        },
        dataType: 'json',
        success: function (o) {
          var days = ['Su', 'M', 'Tu', 'W', 'Th', 'F', 'Sa']
          var meridiem
          var i = 0
          var row
          var rowLabel
          var tmpl

          tmpl = w
            .Handlebars
            .compile($('#template-sebco-activity-heatmap').html())

          if (!lo.isObjectLike(o) || !lo.isObjectLike(o.response)) {
            /* RETURNS */
            return
          }

          o = o.response
          o.days.rows = []

          while (i < 24) {
            o.days.rows.push([])
            i += 1
          }

          o.days.high = 0

          o.days.forEach(function (v) {
            var tmp = new Date(v.timestamp)

            v.dateStr = days[tmp.getDay()] +
              '<br />' +
              (1 + tmp.getMonth()) +
              '/' +
              tmp.getDate()

            v.total = 0

            v.hours.forEach(function (ht) {
              if (Number.isFinite(ht) && ht > 0) {
                v.total += ht
              }
            })

            if (v.total >= 1000000) {
              v.totalStr = (v.total / 1000000)
              v.totalStr = v.totalStr.toFixed(1) + 'M'
            } else if (v.total >= 1000) {
              v.totalStr = (v.total / 1000)
              v.totalStr = v.totalStr.toFixed(1) + 'K'
            } else {
              v.totalStr = v.total
              v.totalStr += ''
            }
          })

          o.days.rows.forEach(function (ignore, i) {
            row = []
            rowLabel = i

            if (rowLabel >= 12) {
              rowLabel -= 12
              meridiem = 'p'
            } else {
              meridiem = 'a'
            }

            if (rowLabel === 0) {
              rowLabel = 12
            }

            row.rowLabel = rowLabel + meridiem

            o.days.forEach(function (v) {
              var val = v.hours[i]

              if (val > o.days.high) {
                o.days.high = val
              }

              row.push(
                val > -1
                  ? { val, title: val + ' machine starts' }
                  : { val: null, title: 'No data.' }
              )
            })

            o.days.rows[i] = row
          })

          o.days.rows.forEach(function (row) {
            row.forEach(function (v) {
              if (v.val !== null) {
                v.opac = (v.val / o.days.high)

                if (!Number.isFinite(v.opac)) {
                  v.opac = 0.15
                }

                v.opac = v.opac.toFixed(2)
              }
            })
          })

          // con = $('#heatmap-con').append(tmpl(o));

          con
            .empty()
            .append(tmpl(o))
            .find('tbody td')
            .first()
            .each(function (ignore, v) {
              var cell = $(v)
              var h = cell.height()

              con
                .find('.sebco-activity-heatmap-point')
                .each(function (ignore, v) {
                  var el = $(v)
                  var min = 4
                  var nh
                  var no
                  var opts = {}

                  if (Number.isFinite(el.data('val'))) {
                    nh = A.parseInt(h * el.data('opac'))

                    if (nh < min) {
                      nh = min
                    }

                    no = el.data('opac')

                    if (!Number.isFinite(no)) {
                      no = parseFloat(no)
                    }

                    if (!Number.isFinite(no)) {
                      no = 0.15
                    }

                    if (no < 0.15) {
                      no = 0.15
                    }

                    opts = {
                      height: nh,
                      lineHeight: h,
                      opacity: no,
                      verticalAlign: 'middle',
                      width: nh
                    }

                    if (opts.opacity <= 0.15) {
                      opts.opacity = 0.15
                    }
                  } else {
                    opts = {
                      backgroundColor: '#CCC',
                      height: min,
                      lineHeight: h,
                      opacity: 0.67,
                      verticalAlign: 'middle',
                      width: min
                    }
                  }

                  w.setTimeout(function () {
                    el.css(opts)
                  }, A.parseInt(Math.random() * 900))
                })
            })
        }
      })
    }

    static initStaffAddFundsForm () {
      var amountField
      var amountFieldCon
      var chargeOptions
      var creditOptions
      var creditTypeField
      var creditTypeFieldCon
      var fm = $('#sebco-staff-add-funds-form')
      var prevVal = 0

      if (fm.length < 1) {
        /* RETURNS */
        return
      }

      amountField = fm.find('#sebco-staff-add-funds-form-amount')

      amountFieldCon = amountField.parent()

      creditTypeField = fm
        .find('#sebco-staff-add-funds-form-credit-type')

      creditTypeFieldCon = creditTypeField.parent()

      chargeOptions = creditTypeField
        .find('[data-credit-type="charge"]')

      creditOptions = creditTypeField
        .find('[data-credit-type="credit"]')

      amountField
        .on(
          'change',
          function () {
            var val = parseFloat(amountField.val())

            if (!Number.isFinite(val)) {
              val = 0
              amountField.val(val)
            }

            if (val < 0) {
              amountFieldCon.addClass('has-error')
              creditTypeFieldCon.addClass('has-error')
              creditOptions.prop('disabled', true)
              chargeOptions.prop('disabled', false)
            } else {
              amountFieldCon.removeClass('has-error')
              creditTypeFieldCon.removeClass('has-error')
              chargeOptions.prop('disabled', true)
              creditOptions.prop('disabled', false)
            }

            if (val < 0 && prevVal >= 0) {
              creditTypeField.prop('selectedIndex', -1)
              chargeOptions.first().prop('selected', true)
            } else if (val >= 0 && prevVal < 0) {
              creditTypeField.prop('selectedIndex', -1)
              creditOptions.first().prop('selected', true)
            }

            prevVal = val
          }
        )
        .trigger('change')
    }

    static initStaffCountBadges () {
      Array.from(d.querySelectorAll('.sebco-staff-count-badge[data-endpoint]'))
        .filter(function (q) {
          return (
            lo.isDefined(q.dataset) &&
            lo.isDefined(q.dataset.endpoint) &&
            lo.isDefined(q.dataset.name)
          )
        })
        .map(function (q) {
          return {
            endpoint: q.dataset.endpoint,
            name: q.dataset.name
          }
        })
        .filter(function (o) {
          return (lo.isString(o.endpoint) && lo.isString(o.name))
        })
        .map(function (o) {
          o.endpoint = o.endpoint.trim()
          o.name = o.name.trim().toLowerCase().replace('/[^a-z0-9]/g', '')

          return o
        })
        .filter(function (o) {
          return ((o.endpoint !== '') && (o.name !== ''))
        })
        .filter(function (o) {
          return lo.isUndefined(A.countBadgeEndpoints[o.name])
        })
        .forEach(function (o) {
          A.waitPromise(60000)
            .then(function () {
              A.updateStaffCountBadges(o.endpoint, o.name)
            })
        })
    }

    static initStaffCreateKioskForm (fm) {
      if (!(fm instanceof $) || fm.length < 1) {
        /* RETURNS */
        return
      }

      fm
        .find('#sebco-staff-create-kiosk-form-room-lookup')
        .on(
          'autocompleteselect',
          function (ignore, ui) {
            var sourceText = ''
            var targetText

            if (lo.isObjectLike(ui) &&
                lo.isObjectLike(ui.item) &&
                lo.isString(ui.item.value)) {
              sourceText = $.trim(ui.item.value)
            }

            if (sourceText !== '') {
              targetText = 'Kiosk 1, ' + sourceText

              fm
                .find('#sebco-staff-create-kiosk-form-name')
                .val(targetText)

              /*
               * iPhone workaround 2016-10-19: the call to trigger('focus') is
               * ignored without the setTimeout delay.
               */
              A.waitPromise(50)
                .then(function () {
                  fm
                    .find('#sebco-staff-create-kiosk-form-mac-address')
                    .trigger('focus')
                })
            }
          }
        )

      fm
        .find('.sebco-staff-form-recent-macs')
        .on(
          'change',
          function (e) {
            var sel = $(e.currentTarget)
            var val = sel.val()

            if (lo.isString(val) && val !== '') {
              $(sel.data('updateId'))
                .val(val)
                .trigger('focus')
            }
          }
        )
    }

    static initStaffCreateKioskForms () {
      var fm = $(
        '#sebco-staff-create-kiosk-form,#sebco-kioskhardware-add-form'
      )

      if (fm.length < 1) {
        /* RETURNS */
        return
      }

      fm.each(function (ignore, v) {
        A.initStaffCreateKioskForm($(v))
      })
    }

    static initStaffCurrencyValidatorResetCountsForm () {
      $('.sebco-staff-currency-validator-reset-form')
        .each(function (ignore, v) {
          var fm = $(v)

          fm
            .find('button[type="submit"]')
            .on('click', function () {
              var confirmed = w.confirm(
                'Reset counters for "' +
                  fm.data('kioskName') +
                  '"?'
              )

              if (confirmed) {
                /* RETURNS */
                return true
              }

              return false
            })
        })
    }

    static initStaffDeviceEventsTable () {
      var tr = $('.sebco-staff-device-events-table tbody tr')

      if (tr.length < 1) {
        /* RETURNS */
        return
      }

      tr.each(function (i, v) {
        var myTime,
          nextTime

        if (lo.isDefined(tr[i + 1])) {
          myTime = $(v).find('.sebco-auto-delta')
          nextTime = $(tr[i + 1]).find('.sebco-auto-delta')
          A.updateDeviceEventsTimeDelta(myTime, nextTime)
        }
      })

      $(d)
        .on(
          'click',
          '.sebco-has-dupe .sebco-dupe-indicator',
          A.handleDeviceEventDupeIndicatorClick
        )
    }

    static initStaffDeviceeditSetstatusForm () {
      var com
      var sel = d.getElementById(
        'sebco-staff-deviceedit-setstatus-form-reason'
      )

      if ((sel === null) || !(sel instanceof w.HTMLSelectElement)) {
        /* RETURNS */
        return
      }

      com = d.getElementById(
        'sebco-staff-deviceedit-setstatus-form-commentx'
      )

      if (com === null) {
        /* RETURNS */
        return
      }

      $(sel).on('change', function () {
        var t

        if (Number.isFinite(sel.selectedIndex) && (sel.selectedIndex > -1)) {
          t = sel.options[sel.selectedIndex]
            .getAttribute('data-require-comment')
          com.disabled = !!(t === 'false')
        }
      }).trigger('change')
    }

    static initStaffDeviceStatistics () {
      var fm
      var con = $('#sebco-staff-device-statistics-table-con')
      var tmpl

      if (con.length < 1) {
        /* RETURNS */
        return
      }

      fm = $('#sebco-staff-device-stats-form')

      if (fm.length > 0) {
        fm
          .find('select[name="locationId"]')
          .on(
            'change',
            function (e) {
              var s = $(e.currentTarget)
              var locationId = A.parseInt(s.val())
              var roomSelect = fm.find('[name="roomId"]')

              if (locationId === 0) {
                roomSelect
                  .find('option')
                  .prop('disabled', false)
                  .prop('hidden', false)
              } else {
                roomSelect
                  .find('option')
                  .each(function (k, v) {
                    var opt = $(v)
                    var t = opt.data('locationId')

                    if (
                      (k === 0) ||
                      !lo.isDefined(t) ||
                      (locationId === A.parseInt(t))
                    ) {
                      opt
                        .prop('disabled', false)
                        .prop('hidden', false)
                    } else {
                      opt
                        .prop('disabled', true)
                        .prop('hidden', true)

                      if (k === roomSelect
                        .prop('selectedIndex')) {
                        roomSelect
                          .prop('selectedIndex', 0)
                      }
                    }
                  })
              }
            }
          )
          .trigger('change')
      }

      tmpl = $('#template-sebco-staff-device-statistics-table').html()
      tmpl = w.Handlebars.compile(tmpl)

      A
        .ajaxPromise({
          url: con.data('endpoint'),
          dataType: 'json',
          data: {
            endTimestamp: con.data('endTimestamp'),
            field1: con.data('field1'),
            field2: con.data('field2'),
            locationId: con.data('locationId'),
            page: con.data('page'),
            roomId: con.data('roomId')
          }
        })
        .then(function (o) {
          con
            .empty()
            .append(tmpl(o.response))
        })
    }

    static initStaffVirtualStackTriggers () {
      var trig = $(
        '#sebco-staff-create-virtual-stack-form-trigger, ' +
          '#sebco-staff-destroy-virtual-stack-form-trigger'
      )

      if (trig.length < 1) {
        /* RETURNS */
        return
      }

      trig.each(function (ignore, v) {
        var fm = $('#' + trig.data('targetId'))
        var me = $(v)

        if (fm.length < 1) {
          /* RETURNS */
          return
        }

        me.on('click', function (e) {
          e.preventDefault()

          if (w.confirm('Are you sure?')) {
            fm.trigger('submit')
          }
        })
      })
    }

    static initStaffDashboardPanel (el) {
      var endpoint,
        interval,
        panel,
        template,
        timeoutId,
        updateCount,
        updater

      if (!lo.isDefined(el) || !lo.isObjectLike(el)) {
        /* RETURNS */
        return
      }

      panel = $(el)
      endpoint = panel.data('endpoint')
      updateCount = panel.data('updateCount') === true

      if (!lo.isString(endpoint)) {
        /* RETURNS */
        return
      }

      template = $(panel.data('templateId'))

      if (template.length < 1) {
        /* RETURNS */
        return
      }

      template = w.Handlebars.compile(template.html())
      interval = 60000

      updater = function () {
        try {
          // debug('Dashboard panel updating from ' + endpoint);

          A
            .ajaxPromise({
              url: endpoint,
              dataType: 'json'
            })
            .then(function (o) {
              var def,
                old,
                tmp

              if (!lo.isObjectLike(o) ||
                  !lo.isObjectLike(o.meta) ||
                  !Number.isFinite(o.meta.status)) {
                def = $.Deferred()
                def.reject(new Error('Invalid JSON response.'))

                /* RETURNS */
                return def.promise()
              }

              if (o.meta.status >= 400) {
                tmp = o.meta.status

                if (lo.isString(o.meta.message)) {
                  tmp += ' ' + o.meta.message
                }

                def = $.Deferred()
                def.reject(new Error(tmp))

                /* RETURNS */
                return def.promise()
              }

              // debug(o);
              tmp = $(template(o))
              old = panel.find('.sebco-replace-me')
              tmp.css('width', old.outerWidth())
              panel.append($('#sebco-bullpen'))
              $('#sebco-bullpen').append(tmp)
              tmp.data('targetHeight', tmp.outerHeight())
              tmp.css('height', old.outerHeight())
              old.replaceWith(tmp)

              if (updateCount && $.isArray(o.response)) {
                panel
                  .find('.sebco-count:first')
                  .text(o.response.length)
              }

              tmp
                .animate(
                  { height: tmp.data('targetHeight') },
                  function () {
                    tmp.css({
                      height: '',
                      width: ''
                    })
                  }
                )
            })
            .fail(function (err) {
              // debug(err);
              lo.noop(err)
            })
        } catch (ignore) {}

        try {
          w.clearTimeout(timeoutId)
        } catch (ignore) {}

        timeoutId = w.setTimeout(updater, interval)
      }

      updater()
    }

    static initStaffDashboardPanels () {
      var panels = $('.sebco-staff-dashboard-panel')
      var bullpen

      if (panels.length < 1) {
        /* RETURNS */
        return
      }

      bullpen = $('#sebco-bullpen')

      if (bullpen.length < 1) {
        bullpen = $('<div id="sebco-bullpen"></div>')
          .css({
            left: -16384,
            position: 'absolute',
            top: 0
          })

        $('.sebco-page-main:first').append(bullpen)
      }

      panels
        .each(function (ignore, el) {
          A.initStaffDashboardPanel(el)
        })
    }

    static initStaffDatePickers () {
      $('.sebco-staff-date-picker').each(function (ignore, v) {
        var dp = $(v)

        dp.datepicker()
      })
    }

    static initStaffDeviceSetupMessageStatuses () {
      if ($('.sebco-setup-message-indicator').length < 1) {
        /* RETURNS */
        return
      }

      A.waitPromise(15000).then(A.updateDeviceSetupMessageStatuses)
    }

    static initStaffDormantCustomerForm () {
      var checkboxes
      var checkSelectAll
      var fm = $('#sebco-staff-dormant-customer-form')

      if (fm.length < 1) {
        /* RETURNS */
        return
      }

      checkSelectAll = true

      checkboxes = fm
        .find('.sebco-staff-dormant-customer-form-send')
        .on(
          'change',
          function (e) {
            if (!$(e.currentTarget).is(':checked')) {
              $('.sebco-select-all-trigger')
                .prop('checked', false)
            }
          }
        )

      checkboxes.each(function (ignore, v) {
        if (!$(v).is(':checked')) {
          checkSelectAll = false
        }
      })

      if (checkboxes.length < 1) {
        checkSelectAll = false
      }

      if (checkSelectAll) {
        fm
          .find('.sebco-select-all-trigger')
          .prop('checked', true)
      }

      fm
        .on(
          'change',
          '.sebco-select-all-trigger',
          function (e) {
            if ($(e.currentTarget).is(':checked')) {
              checkboxes.prop('checked', true)
            } else {
              checkboxes.prop('checked', false)
            }
          }
        )
    }

    static initStaffFindLogViewerOutput () {
      var newHeight
      var textarea = $('.sebco-log-viewer-output-form-output')
      var textareaOuterHeight
      var windowInnerHeight

      if (textarea.length < 1) {
        /* RETURNS */
        return
      }

      textareaOuterHeight = textarea.outerHeight()
      textarea.css({ height: textareaOuterHeight })
      windowInnerHeight = A.win.innerHeight()

      newHeight = A
        .parseInt((windowInnerHeight - textarea.offset().top) - 20)

      if (Number.isFinite(newHeight)) {
        textarea.css({ height: newHeight })
      }
    }

    static initStaffKioskConnectionStatus () {
      var decorateAlive,
        decorateDead,
        decorateDegraded,
        decorateUnknown,
        updater

      decorateAlive = function (con) {
        con
          .removeClass(con.data('deadClass'))
          .removeClass(con.data('degradedClass'))
          .removeClass(con.data('unknownClass'))
          .addClass(con.data('aliveClass'))
          .attr({
            title: con.data('aliveText')
          })
      }

      decorateDead = function (con) {
        con
          .removeClass(con.data('aliveClass'))
          .removeClass(con.data('degradedClass'))
          .removeClass(con.data('unknownClass'))
          .addClass(con.data('deadClass'))
          .attr({
            title: con.data('deadText')
          })
      }

      decorateDegraded = function (con) {
        con
          .removeClass(con.data('aliveClass'))
          .removeClass(con.data('deadClass'))
          .removeClass(con.data('unknownClass'))
          .addClass(con.data('degradedClass'))
          .attr({
            title: con.data('degradedText')
          })
      }

      decorateUnknown = function (con) {
        con
          .removeClass(con.data('aliveClass'))
          .removeClass(con.data('deadClass'))
          .removeClass(con.data('degradedClass'))
          .addClass(con.data('unknownClass'))
          .attr({
            title: con.data('unknownText')
          })
      }

      updater = function () {
        var endpoint,
          kioskHardwareIds,
          widgets

        A.initStaffKioskConnectionStatusDestroy()
        widgets = $('.sebco-kiosk-connection-status')
        kioskHardwareIds = []

        widgets.each(function (k, v) {
          var widget = $(v)
          var kioskHardwareId = widget.data('kioskHardwareId')

          if (!kioskHardwareIds.includes(kioskHardwareId)) {
            kioskHardwareIds.push(
              (Number.isFinite(kioskHardwareId))
                ? kioskHardwareId
                : null
            )
          }

          if (k === 0) {
            endpoint = widget.data('endpoint')
          }
        })

        try {
          if (!lo.isString(endpoint)) {
            /* THROWS */
            throw new Error('Invalid endpoint.')
          }

          $.ajax({
            url: endpoint,
            data: {
              ids: JSON.stringify(kioskHardwareIds)
            },
            dataType: 'json',
            error: decorateUnknown,
            success: function (o) {
              if (!lo.isObjectLike(o) ||
                  !lo.isObjectLike(o.meta) ||
                  !Number.isFinite(o.meta.status)) {
                widgets.each(function (ignore, v) {
                  decorateUnknown($(v))
                })

                /* RETURNS */
                return
              }

              if (o.meta.status >= 400 ||
                  !lo.isObjectLike(o.response)) {
                widgets.each(function (ignore, v) {
                  decorateUnknown($(v))
                })

                /* RETURNS */
                return
              }

              widgets.each(function (ignore, v) {
                var key,
                  online,
                  widget

                widget = $(v)
                key = 'kiosk-hardware-id-' + widget.data('kioskHardwareId')
                online = o.response[key]

                if (Number.isFinite(online)) {
                  if (online === A.kioskConnectionOnline) {
                    decorateAlive(widget)
                  } else if (online === A.kioskConnectionDegraded) {
                    decorateDegraded(widget)
                  } else {
                    decorateDead(widget)
                  }
                } else {
                  decorateUnknown(widget)
                }
              })
            },
            type: 'POST'
          })
        } catch (ignore) {

          // debug(ignore);
        }

        $('body:first').data(
          'kioskConnectionStatusTimer',
          w.setTimeout(updater, 30000)
        )
      }

      updater()
    }

    static initStaffKioskConnectionStatusDestroy () {
      w.clearTimeout($('body:first').data('kioskConnectionStatusTimer'))
    }

    static initStaffKioskDisconnects () {
      var con = $('#sebco-kiosk-disconnects-con')

      if (con.length < 1) {
        /* RETURNS */
        return
      }

      $('#sebco-kiosk-disconnects-change-increment-form')
        .on(
          'submit',
          function (e) {
            e.preventDefault()
          }
        )
        .find('[name="increment"]')
        .on(
          'change',
          function (e) {
            var sel = $(e.currentTarget)
            var t

            con.data('increment', sel.val())

            t = con.data('baseUrl') +
              '/' +
              encodeURIComponent(con.data('increment')) +
              '/' +
              encodeURIComponent(
                con.data('endDate').substring(0, 10)
              )

            w.location.pathname = A.sanitizeUrl(t)
          }
        )
    }

    static initStaffKioskRebootCountRangeSelect () {
      var con,
        url

      con = $(
        '#sebco-staff-kiosk-reboot-count-select-form-range-lookup'
      )

      if (con.length < 1) {
        /* RETURNS */
        return
      }

      con
        .closest('form')
        .on('submit', function (e) {
          e.preventDefault()
        })

      url = con.data('urlTemplate')

      con.on('change', function () {
        var t = url + '/' + con.val()

        w.location.pathname = A.sanitizeUrl(t)
      })
    }

    static initStaffKioskSoftwareUpdate () {
      var con,
        conWidth,
        fieldOutput,
        progFailed,
        progInitializing,
        progSuccess,
        progUpdating

      con = $('.sebco-kiosk-software-update-status-con')

      if (con.length < 1) {
        /* RETURNS */
        return
      }

      progInitializing = $('#sebco-progress-initializing')
      conWidth = progInitializing.width()

      con.css({
        maxWidth: conWidth,
        width: conWidth
      })

      fieldOutput = $('#sebco-kiosk-software-update-status-form-output')
      progFailed = $('#sebco-progress-failed')
      progSuccess = $('#sebco-progress-success')
      progUpdating = $('#sebco-progress-updating')

      $.each(
        [
          fieldOutput,
          progFailed,
          progSuccess,
          progUpdating
        ],
        function (ignore, v) {
          v.hide().removeAttr('hidden')
        }
      )

      progInitializing.hide()
      progUpdating.show()

      $.ajax({
        url: con.data('endpoint'),
        dataType: 'json',
        error: function (x, t, e) {
          progUpdating.hide()
          progFailed.show()

          fieldOutput
            .val('Error: ' + JSON.stringify([x, t, e], null, ' '))
            .slideDown()
        },
        method: 'POST',
        success: function (o) {
          var buf

          debug(o)
          progUpdating.hide()

          if (!lo.isObjectLike(o) ||
              !lo.isObjectLike(o.meta) ||
              !Number.isFinite(o.meta.status) ||
              !lo.isObjectLike(o.response)) {
            progFailed.show()

            fieldOutput
              .val('Error: received malformed output from cloud site.')
              .slideDown()

            /* RETURNS */
            return
          }

          if (o.meta.status >= 600) {
            progFailed.show()
            buf = ''

            if (lo.isString(o.response.cause)) {
              buf += 'Cause: ' + o.response.cause + '\n'
            } else {
              buf += 'Cause: unknown.\n'
            }

            if (lo.isString(o.response.output) &&
                $.trim(o.response.output) !== '') {
              buf += 'Details: ' + o.response.output
            }

            fieldOutput
              .val(buf)
              .slideDown()

            /* RETURNS */
            return
          }

          if (o.meta.status >= 400) {
            progFailed.show()

            buf = ''

            if (lo.isString(o.response.cause)) {
              buf += 'Cause: ' + o.response.cause + '\n'
            } else {
              buf += 'Cause: unknown.\n'
            }

            if (lo.isString(o.response.output) &&
                $.trim(o.response.output) !== '') {
              buf += 'Details: ' + o.response.output
            }

            fieldOutput
              .val(buf)
              .slideDown()

            /* RETURNS */
            return
          }

          if (o.meta.status === 200 &&
              Number.isFinite(o.response.status) &&
              o.response.status === 0) {
            progSuccess.show()

            fieldOutput
              .val(o.response.output)
              .slideDown()

            /* RETURNS */
            return
          }

          progFailed.show()

          fieldOutput
            .val(JSON.stringify(o, null, ' '))
            .slideDown()
        },
        timeout: 3600000
      })
    }

    static initStaffKioskSoftwareUpdateForm () {
      if ($('.sebco-staff-kiosk-software-update-form').length > 0) {
        $(d)
          .on(
            'click',
            '.sebco-staff-kiosk-software-update-form [type="submit"]',
            function (e) {
              var con,
                fm

              e.preventDefault()

              fm = $(e.currentTarget)
                .closest(
                  '.sebco-staff-kiosk-software-update-form'
                )

              $('.sebco-dialog-confirm')
                .each(function (ignore, v) {
                  var el = $(v)

                  try {
                    el.dialog('close')
                  } catch (ignore) {}

                  try {
                    el.dialog('destroy')
                  } catch (ignore) {}

                  try {
                    el.remove()
                  } catch (ignore) {}
                })

              con = $(
                $(
                  '#template-sebco-staff-kiosk-software-update-dialog-confirm'
                ).html()
              )

              con
                .find('.sebco-kiosk-name')
                .text(fm.data('kioskName'))

              $('.sebco-staff-wrapper').prepend(con)

              con
                .removeAttr('hidden')
                .find('.sebco-btn-cancel')
                .on(
                  'click',
                  function (e) {
                    e.preventDefault()

                    try {
                      con
                        .dialog('close')
                        .remove()
                        .dialog('destroy')
                    } catch (ignore) {}
                  }
                )
                .end()
                .find('.sebco-btn-confirm')
                .on(
                  'click',
                  function (e) {
                    e.preventDefault()

                    try {
                      con
                        .dialog('close')
                        .remove()
                        .dialog('destroy')
                    } catch (ignore) {}

                    fm.trigger('submit')
                  }
                )
                .end()
                .dialog({
                  dialogClass: 'sebco-dialog-confirm-con'
                })
                .animate({ opacity: 1 }, 200)
            }
          )
      }
    }

    static initStaffMachineStartsBarChart () {
      var con,
        scale,
        tmplChart,
        tmplError

      con = $('.sebco-machine-starts-bar-chart-con')

      if (con.length < 1) {
        /* RETURNS */
        return
      }

      scale = con.data('scale')

      if (!Number.isFinite(scale)) {
        scale = 300
      }

      tmplChart = w
        .Handlebars
        .compile(
          $('#template-sebco-machine-starts-bar-chart-table')
            .html()
        )

      tmplError = w
        .Handlebars
        .compile(
          $('#template-sebco-machine-starts-bar-chart-table-error')
            .html()
        )

      $.ajax({
        url: con.data('endpoint'),
        data: {
          locationId: con.data('locationId')
        },
        dataType: 'json',
        error: function (x, t, e) {
          debug([x, t, e])

          con
            .slideUp(function () {
              con
                .empty()
                .append(
                  tmplError({ message: t + '\n' + e })
                )
                .slideDown()
            })
        },
        success: function (o) {
          if (!lo.isObjectLike(o) ||
              !lo.isObjectLike(o.meta) ||
              !Number.isFinite(o.meta.status) ||
              o.meta.status >= 400) {
            debug(o)

            con
              .slideUp(function () {
                con
                  .empty()
                  .append(
                    tmplError({
                      message: 'Could not load data.'
                    })
                  )
                  .slideDown()
              })

            /* RETURNS */
            return
          }

          con
            .slideUp(function () {
              var days = o.response.days

              A.prepareStaffMachineStartsData(scale, days)

              con
                .empty()
                .append(tmplChart(o.response))
                .slideDown()
            })
        }
      })
    }

    static initStaffMediaObjectUploadForm (v) {
      var fileDisplayInput,
        fileInput,
        fileLabel,
        fm

      fm = $(v)

      fileDisplayInput = fm
        .find('.sebco-staff-media-object-upload-form-file-display')

      fileLabel = fm
        .find('.sebco-staff-media-object-upload-form-file-label')

      fileDisplayInput.on('click', function (e) {
        e.preventDefault()
        fileLabel.trigger('click')
      })

      fileInput = fm
        .find('.sebco-staff-media-object-upload-form-file')

      fileInput.on('change', function () {
        fileDisplayInput.val(fileInput.val())
      })
    }

    static initStaffMediaObjectUploadForms () {
      $('.sebco-staff-media-object-upload-form')
        .each(function (ignore, v) {
          A.initStaffMediaObjectUploadForm(v)
        })
    }

    static initStaffLocationMap () {
      const con = document.getElementById('sebco-staff-location-map-con')

      if (!con) {
        return
      }

      const key = con.dataset.googleBrowserKey

      if (!lo.isString(key) || key.trim() === '') {
        return
      }

      const center = JSON.parse(con.dataset.center)

      if (
        !lo.isObjectLike(center) ||
        lo.isUndefined(center.lat) ||
        !Number.isFinite(center.lat) ||
        lo.isUndefined(center.lng) ||
        !Number.isFinite(center.lng)
      ) {
        return
      }

      const updateMap = () => {
        const markers = JSON.parse(con.dataset.markers)
        const { Animation, Map, Marker, event } = w.google.maps
        const { DROP: animation } = Animation

        con.innerHTML = ''

        const map = new Map(con, { center, zoom: 8 })

        for (const v of markers) {
          const { position, title } = v
          const url = A.sanitizeUrl(v.url)
          const marker = new Marker({ animation, map, position, title, url })

          event.addListener(marker, 'click', () => {
            w.location.pathname = url
          })
        }
      }

      const loadMapApi = () => {
        const mapCallbackname = A.generateCallbackName()

        w[mapCallbackname] = updateMap

        $.ajax({
          url: 'https://maps.googleapis.com/maps/api/js?key=' +
            key +
            '&v=3.26&callback=' +
            mapCallbackname,
          complete: () => {
            if (w[mapCallbackname]) {
              delete w[mapCallbackname]
            }
          },
          dataType: 'script'
        })
      }

      const jqel = $(con)

      const resizeCon = () => {
        const pad = (A.win.innerWidth() > 768) ? 30 : 20
        let height = A.parseInt((A.win.innerHeight() - jqel.offset().top) - pad)

        if (!Number.isFinite(height) || height < 1) {
          height = 1
        }

        jqel.animate({ height }, loadMapApi)
      }

      resizeCon()
      A.windowResizeComplete(resizeCon)
    }

    static initStaffLocationSelectForm () {
      var fm,
        sel

      fm = $('.sebco-staff-location-select-form')
      sel = fm.find('.sebco-staff-location-select-form-id')

      if (fm.length < 1 || sel.length < 1) {
        /* RETURNS */
        return
      }

      fm.on('submit', function (e) {
        e.preventDefault()
      })

      sel.on('change', function () {
        var currentLocationId = fm.data('currentLocationId')
        var newLocationId = sel.val()

        if (A.parseInt(currentLocationId) ===
            A.parseInt(newLocationId)) {
          /* RETURNS */
          return
        }

        w.setTimeout(
          function () {
            var t = fm.data('urlTemplate') + '/' + newLocationId

            w.location.pathname = A.sanitizeUrl(t)
          },
          200
        )
      })
    }

    static initStaffLocationStats () {
      var table = $('.sebco-location-statistics-table')
      var rangeSelect = $(
        '#sebco-staff-select-location-statistics-range-form-range'
      )

      if (table.length < 1 || rangeSelect.length < 1) {
        /* RETURNS */
        return
      }

      rangeSelect.on('change', function (e) {
        var t

        table.data('range', $(e.currentTarget).val())
        t = table.data('urlTemplate') + '/' + table.data('range')
        w.location.pathname = A.sanitizeUrl(t)
      })
    }

    static initStaffMicroEvents () {
      var con,
        replaceContent,
        tmplLoader,
        tmplTable,
        update

      con = $('#sebco-staff-micro-event-heatmap-con')

      if (con.length < 1) {
        /* RETURNS */
        return
      }

      tmplLoader = w
        .Handlebars
        .compile(
          $('#template-sebco-staff-loader').html()
        )

      tmplTable = w
        .Handlebars
        .compile(
          $('#template-sebco-staff-micro-event-heatmap-table').html()
        )

      replaceContent = function (oldContent, newContent) {
        var def = $.Deferred()

        if (oldContent.hasClass('sebco-tmp-loader') &&
            newContent.hasClass('sebco-tmp-loader')) {
          def.resolve()

          /* RETURNS */
          return def.promise()
        }

        oldContent
          .slideUp(function () {
            oldContent.replaceWith(newContent)

            newContent
              .slideDown(function () {
                def.resolve()
              })
          })

        return def.promise()
      }

      update = function () {
        var loader = $(tmplLoader({})).hide()

        replaceContent(con.find('.sebco-replace-me'), loader)
          .then(function () {
            return $
              .ajax({
                url: con.data('endpoint') +
                  '/' +
                  con.data('currentMicroEventType') +
                  '/' +
                  con.data('currentLocationId') +
                  '/' +
                  con.data('currentPage') +
                  '/' +
                  con.data('currentStartDate'),
                dataType: 'json'
              })
          })
          .then(function (o) {
            var microEventType
            var newContent
            var oldContent = con.find('.sebco-replace-me')
            var prevButton

            if (lo.isObjectLike(o) && lo.isObjectLike(o.response)) {
              o.response.extraClasses = ''
              microEventType = con.data('currentMicroEventType')

              if (microEventType === 'devicecheckfail') {
                o.response.extraClasses += ' sebco-hide-maytag-backoff'
              } else if (microEventType === 'maytagbackoff') {
                o.response.extraClasses += ' sebco-hide-device-check-fail'
              }

              prevButton = $('#sebco-staff-micro-events-prev')

              if (lo.isBoolean(o.response.enablePrev) &&
                  o.response.enablePrev === true) {
                prevButton
                  .removeClass(
                    'sebco-btn-pagination-disabled'
                  )
              } else {
                prevButton
                  .addClass('sebco-btn-pagination-disabled')
              }
            }

            newContent = $(tmplTable(o.response)).hide()
            replaceContent(oldContent, newContent)
          })
          .fail(function (x, t, e) {
            debug([x, t, e])
          })
      }

      $(d)
        .on(
          'change',
          '#sebco-staff-micro-event-type-select-form-id',
          function (e) {
            var newVal = $(e.currentTarget).val()
            var oldVal = con.data('currentMicroEventType')
            var url

            if (newVal !== oldVal) {
              con
                .attr('data-current-micro-event-type', newVal)
                .data('currentMicroEventType', newVal)

              url = con.data('urlTemplate') +
                '/' +
                newVal +
                '/' +
                con.data('currentOrganizationId') +
                '/' +
                con.data('currentLocationId') +
                '/1'

              w.location.pathname = A.sanitizeUrl(url)
            }
          }
        )
        .on(
          'change',
          '#sebco-micro-event-location-select-form-id',
          function (e) {
            var newVal = $(e.currentTarget).val()
            var oldVal = con.data('currentLocationId')
            var url

            newVal = A.parseInt(newVal)
            oldVal = A.parseInt(oldVal)

            if (newVal !== oldVal) {
              con
                .attr('data-current-location-id', newVal)
                .data('currentLocationId', newVal)

              url = con.data('urlTemplate') +
                '/' +
                con.data('currentMicroEventType') +
                '/' +
                con.data('currentOrganizationId') +
                '/' +
                newVal +
                '/1'

              w.location.pathname = A.sanitizeUrl(url)
            }
          }
        )

      update()
    }

    static initStaffProblemDevicesView () {
      var q = d.getElementById('sebco-staff-problem-device-con')
      var t

      if (q === null) {
        /* RETURNS */
        return
      }

      t = d.getElementById('template-sebco-staff-problem-device-con')

      if (t === null) {
        /* RETURNS */
        return
      }

      q = $(q)

      A.ajaxPromise({ url: q.data('endpoint') })
        .then(A.normalizeJsonResponse)
        .then(function (o) {
          var u
          var utdh = q.data('deviceHistoryViewUrl')
          var utpa = q.data('testPaymentAddUrl')
          var utpd = q.data('testPaymentDevicelistUrl')

          $.each(o.response, function (ignore, x) {
            if (!lo.isDefined(x.data) && !$.isArray(x.data)) {
              /* RETURNS */
              return
            }

            $.each(x.data, function (ignore, v) {
              v.deviceHistoryViewUrl = utdh.replace(/\{\{id\}\}/, v.id)
              v.testPaymentAddUrl = utpa.replace(/\{\{id\}\}/, v.id)
              v.testPaymentDevicelistUrl = utpd.replace(/\{\{id\}\}/, v.id)

              v.extraClasses = (Number.isFinite(v.tier) && (v.tier !== 1))
                ? 'text-success'
                : 'text-danger'

              u = new Date(1000 * v.last_monitor_message)
              v.last_monitor_message = u.toLocaleString()
              v.lastMonitorMessageDtf = u.toISOString()

              if (lo.isDefined(v.heatmap)) {
                v.heatmap = JSON.stringify(v.heatmap)
              }

              if (
                lo.isObjectLike(v.lastTest) &&
                lo.isDefined(v.lastTest.created) &&
                Number.isFinite(v.lastTest.created)
              ) {
                u = new Date(1000 * v.lastTest.created)
                v.lastTest.dateTime = u.toLocaleString()
                v.lastTest.dateTimeDtf = u.toISOString()
              }
            })
          })

          t = w.Handlebars
            .compile(t.innerText)({ problemDevices: o.response })

          q.empty().append(t)
          w.sebco.initProblemDevicesHeatmaps()
        })
        .catch(debug)
    }

    static initStaffRebootKioskForm () {
      if ($('.sebco-staff-reboot-kiosk-form').length > 0) {
        $(d)
          .on(
            'click',
            '.sebco-staff-reboot-kiosk-form [type="submit"]',
            function (e) {
              var con,
                fm

              e.preventDefault()

              fm = $(e.currentTarget)
                .closest('.sebco-staff-reboot-kiosk-form')

              $('.sebco-dialog-confirm')
                .each(function (ignore, v) {
                  var el

                  el = $(v)

                  try {
                    el.dialog('close')
                  } catch (ignore) {}

                  try {
                    el.dialog('destroy')
                  } catch (ignore) {}

                  try {
                    el.remove()
                  } catch (ignore) {}
                })

              con = $(
                $(
                  '#template-sebco-staff-reboot-kiosk-dialog-confirm'
                ).html()
              )

              con
                .find('.sebco-kiosk-name')
                .text(fm.data('kioskName'))

              $('.sebco-staff-wrapper').prepend(con)

              con
                .removeAttr('hidden')
                .find('.sebco-btn-cancel')
                .on(
                  'click',
                  function (e) {
                    e.preventDefault()

                    try {
                      con
                        .dialog('close')
                        .remove()
                        .dialog('destroy')
                    } catch (ignore) {}
                  }
                )
                .end()
                .find('.sebco-btn-confirm')
                .on(
                  'click',
                  function (e) {
                    e.preventDefault()

                    try {
                      con
                        .dialog('close')
                        .remove()
                        .dialog('destroy')
                    } catch (ignore) {}

                    fm.trigger('submit')
                  }
                )
                .end()
                .dialog({
                  dialogClass: 'sebco-dialog-confirm-con'
                })
                .animate({ opacity: 1 }, 200)
            }
          )
      }
    }

    static initStaffRecalculateCustomerBalanceView () {
      var balance
      var balanceDisplay
      var fm = $('#sebco-staff-recalculate-customer-balance-form')
      var recalcButton
      var saveButton

      if (fm.length < 1) {
        /* RETURNS */
        return
      }

      balance = $(
        '#sebco-staff-recalculate-customer-balance-form-new-balance'
      )

      balanceDisplay = $(
        '#sebco-staff-recalculate-customer-balance-form-new-balance-display'
      )

      recalcButton = $(
        '#sebco-staff-recalculate-customer-balance-form-recalculate'
      )

      saveButton = $(
        '#sebco-staff-save-recalculated-customer-balance-form [name="save"]'
      )

      recalcButton
        .prop('disabled', false)
        .on(
          'click',
          function (e) {
            e.preventDefault()
            fm.trigger('submit')
          }
        )

      saveButton.prop('disabled', true)

      fm
        .on(
          'submit',
          function (e) {
            e.preventDefault()
            recalcButton.prop('disabled', true)
            balanceDisplay.addClass('sebco-loading')

            A
              .ajaxPromise({
                dataType: 'json',
                data: {
                  customerId: fm.data('customerId')
                },
                type: 'post',
                url: fm.data('endpoint')
              })
              .then(function (o) {
                balanceDisplay.removeClass('sebco-loading')

                if (!lo.isObjectLike(o) ||
                    !lo.isObjectLike(o.meta) ||
                    !Number.isFinite(o.meta.status) ||
                    o.meta.status >= 400) {
                  recalcButton.prop('disabled', false)

                  /* RETURNS */
                  return
                }

                balance.val(o.response.toFixed(2))
                balanceDisplay.val(o.response.toFixed(2))
                saveButton.prop('disabled', false)
              })
              .fail(function (err) {
                balanceDisplay.removeClass('sebco-loading')
                recalcButton.prop('disabled', false)
                debug(err)
              })
          }
        )
    }

    static initStaffReportDateChanger () {
      var t = d.querySelectorAll('.sebco-form-report-date-changer')

      if (t.length < 1) {
        return
      }

      $(d).on(
        'submit',
        '.sebco-form-report-date-changer',
        A.handleStaffReportDateChangerSubmit
      )

      t.forEach(function (u) {
        (u.dataset.autoSubmit === 'true') && $(u).trigger('submit')
      })
    }

    static initStaffResetCustomerPasswordFormTrigger () {
      $('.sebco-staff-reset-customer-password-form-trigger')
        .each(function (ignore, v) {
          var con,
            fm,
            trig

          trig = $(v)
          fm = $('#' + trig.data('formId'))

          trig.on('click', function (e) {
            e.preventDefault()

            $('.sebco-dialog-confirm')
              .each(function (ignore, v) {
                var el = $(v)

                try {
                  el.dialog('close')
                } catch (ignore) {}

                try {
                  el.dialog('destroy')
                } catch (ignore) {}

                try {
                  el.remove()
                } catch (ignore) {}
              })

            con = $(
              $(
                '#template-sebco-staff-reset-customer-password-dialog-confirm'
              ).html()
            )

            $('.sebco-staff-wrapper').prepend(con)

            con
              .removeAttr('hidden')
              .find('.sebco-btn-cancel')
              .on(
                'click',
                function (e) {
                  e.preventDefault()

                  try {
                    con
                      .dialog('close')
                      .remove()
                      .dialog('destroy')
                  } catch (ignore) {}
                }
              )
              .end()
              .find('.sebco-btn-confirm')
              .on(
                'click',
                function (e) {
                  e.preventDefault()
                  fm.trigger('submit')
                }
              )
              .end()
              .dialog({
                dialogClass: 'sebco-dialog-confirm-con'
              })
              .animate({ opacity: 1 }, 200)
          })
        })
    }

    static initStaffRoomVideo () {
      var cons = $('.sebco-staff-video-feed-con')
      var sizer

      if (cons.length < 1) {
        /* RETURNS */
        return
      }

      sizer = function (con) {
        var height,
          multiple,
          width

        con.css({ minHeight: 1 })
        width = con.outerWidth()

        if (width >= 768) {
          multiple = 0.5625
        } else {
          multiple = 0.75
        }

        height = A.parseInt(width * multiple)

        return { height, width }
      }

      cons
        .each(function (ignore, v) {
          var con,
            iframe,
            size,
            url

          con = $(v)
          size = sizer(con)
          url = con.data('embedUrl')

          if (url.indexOf('video.nest.com/live/') !== -1) {
            url = url.replace(/\/live/, '/embedded/live')
          }

          iframe = $(d.createElement('iframe'))
            .attr({
              allowfullscreen: 'allowfullscreen',
              frameborder: 0,
              height: size.height,
              src: A.sanitizeUrl(url),
              type: 'text/html',
              width: size.width
            })

          con.animate({ height: size.height }, function () {
            con.empty().append(iframe)
          })

          A.windowResizeComplete(A.initStaffRoomVideo)
        })
    }

    static initStaffSelectRoomLinks () {
      $('.sebco-staff-location-room-redirect-trigger')
        .each(function (ignore, v) {
          var trig = $(v)

          trig.on('click', function (e) {
            e.preventDefault()
            $('#' + trig.data('formId')).submit()
          })
        })
    }

    static initStaffServiceModeForm () {
      var fms = $('.sebco-staff-deviceedit-setservicemode-form')

      if (fms.length < 1) {
        /* RETURNS */
        return
      }

      fms.each(function (ignore, v) {
        var fm

        fm = $(v).on('submit', function () {
          return w.confirm(fm.data('confirmText'))
        })
      })
    }

    static initStaffSetupRoomMode () {
      var opts = {}

      opts.con = $('.sebco-setup-room-devices-con')

      if (opts.con.length < 1) {
        /* RETURNS */
        return
      }

      opts.endpoint = opts.con.data('endpoint')
      opts.loadingCon = opts.con.find('.sebco-setup-room-loading-con')

      opts.disabledCon = opts
        .con
        .find('.sebco-setup-room-disabled-devices-con')
        .hide()
        .removeAttr('hidden')

      opts.enabledCon = opts
        .con
        .find('.sebco-setup-room-enabled-devices-con')
        .hide()
        .removeAttr('hidden')

      opts.disabledTmpl = $('#template-sebco-setup-room-device-disabled')
      opts.disabledTmpl = w.Handlebars.compile(opts.disabledTmpl.html())
      opts.enabledTmpl = $('#template-sebco-setup-room-device-enabled')
      opts.enabledTmpl = w.Handlebars.compile(opts.enabledTmpl.html())
      opts.errorTmpl = $('#template-sebco-setup-room-error')
      opts.errorTmpl = w.Handlebars.compile(opts.errorTmpl.html())
      opts.noDevicesTmpl = $('#template-sebco-setup-room-no-devices')

      opts.noDevicesTmpl = w
        .Handlebars
        .compile(opts.noDevicesTmpl.html())

      A.staffSetupRoomRefreshRoom(opts)
    }

    static initStaffStatistics () {
      var con = $('#sebco-statistics-con')

      if (con.length < 1) {
        /* RETURNS */
        return
      }

      $('#sebco-statistics-change-increment-form')
        .on(
          'submit',
          function (e) {
            e.preventDefault()
          }
        )
        .find('[name="increment"]')
        .on(
          'change',
          function (e) {
            var sel = $(e.currentTarget)
            var t

            con.data('increment', sel.val())

            t = con.data('baseUrl') +
              '/' +
              encodeURIComponent(con.data('increment')) +
              '/' +
              encodeURIComponent(
                con.data('endDate').substring(0, 10)
              ) +
              '/' +
              encodeURIComponent(con.data('locationId'))

            w.location.pathname = A.sanitizeUrl(t)
          }
        )

      $('#sebco-statistics-change-location-form')
        .on(
          'submit',
          function (e) {
            e.preventDefault()
          }
        )
        .find('[name="locationId"]')
        .on(
          'change',
          function (e) {
            var sel = $(e.currentTarget)
            var t

            con.data('locationId', A.parseInt(sel.val()))

            t = con.data('baseUrl') +
              '/' +
              encodeURIComponent(con.data('increment')) +
              '/' +
              encodeURIComponent(
                con.data('endDate').substring(0, 10)
              ) +
              '/' +
              encodeURIComponent(con.data('locationId'))

            w.location.pathname = A.sanitizeUrl(t)
          }
        )

      $.ajax({
        url: con.data('endpoint'),
        data: {
          endDate: con.data('endDate'),
          increment: con.data('increment'),
          locationId: con.data('locationId')
        },
        dataType: 'json',
        error: function (x, t, e) {
          debug([x, t, e])
        },
        success: function (o) {
          var tmpl

          tmpl = w
            .Handlebars
            .compile($('#template-sebco-statistics-table').html())

          con
            .slideUp(function () {
              con
                .empty()
                .append(tmpl(o))

              con.slideDown()
            })
        }
      })
    }

    static initStaffStreetViewCon (el) {
      var address,
        k,
        opts,
        q,
        tmpl

      q = (el instanceof $)
        ? el
        : $(el)

      k = q.data('apiKey')

      if (!lo.isString(k) || $.trim(k) === '') {
        /* RETURNS */
        return
      }

      if (!lo.isString(q.attr('id'))) {
        q.attr('id', A.generateId())
      }

      if (!lo.isString(q.attr('data-id'))) {
        q.attr('data-id', q.attr('id'))
      }

      if (!lo.isString(q.data('id'))) {
        q.data('id', q.attr('id'))
      }

      address = q.data('address')

      opts = {
        alt: address,
        queryValue: encodeURIComponent(address),
        width: A.parseInt(q.outerWidth())
      }

      opts.height = A.parseInt(opts.width * 0.75)

      opts.src = 'https://maps.googleapis.com/maps/api/streetview' +
        '?location=' +
        opts.queryValue +
        '&key=' +
        encodeURIComponent(k) +
        '&size=' +
        encodeURIComponent(2 * opts.width) +
        'x' +
        encodeURIComponent(2 * opts.height)

      tmpl = w
        .Handlebars
        .compile($('#template-sebco-staff-street-view').html())

      A.win.on('resizeend', function () {
        var t = A.parseInt(A.win.outerWidth())

        if (t !== q.data('lastWidth')) {
          q.data('lastWidth', t)
          opts.width = A.parseInt(q.outerWidth())
          opts.height = A.parseInt(opts.width * 0.75)
          q.animate({ height: opts.height }, 700, 'easeOutQuad')
        }
      })

      q.empty()
        .animate({ height: opts.height }, 700, 'easeOutQuad')
        .promise()
        .then(function () {
          q.append(tmpl(opts))
        })
    }

    // initStaffStreetViewCon: function (el) {
    //
    //     var k,
    //         q,
    //         s;
    //
    //     q = (el instanceof $)
    //         ? el
    //         : $(el);
    //
    //     k = q.data("apiKey");
    //
    //     if (!lo.isString(k) || "" === $.trim(k)) {
    //
    //         /* RETURNS */
    //         return;
    //     }
    //
    //     if (!lo.isString(q.attr("id"))) {
    //
    //         q.attr("id", a.generateId());
    //     }
    //
    //     if (!lo.isString(q.attr("data-id"))) {
    //
    //         q.attr("data-id", q.attr("id"));
    //     }
    //
    //     if (!lo.isString(q.data("id"))) {
    //
    //         q.data("id", q.attr("id"));
    //     }
    //
    //     a.win.on("resizeend", function () {
    //
    //         var height,
    //             width = a.parseInt(a.win.outerWidth());
    //
    //         if (width !== q.data("lastWidth")) {
    //
    //             q.data("lastWidth", width);
    //             height = a.parseInt(q.outerWidth() * 0.75);
    //             q.data("lastHeight", height);
    //             q.animate({height: height}, 700, "easeOutQuad");
    //         }
    //     });
    //
    //     a.promiseResolved()
    //         .then(function () {
    //
    //             var u;
    //
    //             if (true !== a.googleMapsApiLoaded) {
    //
    //                 u = "https://maps.googleapis.com/maps/api/js?key=" +
    //                         k;
    //
    //         /* RETURNS */
    //                 return a.ajaxPromiseScript(u);
    //             }
    //
    //             u = $.Deferred();
    //             u.resolve();
    //
    //             return u.promise();
    //         })
    //         .then(function () {
    //
    //             var height,
    //                 width;
    //
    //             a.googleMapsApiLoaded = true;
    //             q.empty();
    //             width = a.parseInt(q.outerWidth());
    //             q.data("lastWidth", width);
    //             height = a.parseInt(width * 0.75);
    //             q.data("lastHeight", height);
    //
    //             return q
    //                 .animate({height: height}, 700, "easeOutQuad")
    //                 .promise();
    //         })
    //         .then(function () {
    //
    //             var t = {
    //                 lat: parseFloat(q.data("latitude")),
    //                 lng: parseFloat(q.data("longitude"))
    //             };
    //
    //             console.log(t);
    //
    //             s = new w.google.maps.StreetViewPanorama(
    //                 q.get(0),
    //                 {
    //                     position: t,
    //                     visible: true
    //                 }
    //             );
    //
    //             lo.noop(s);
    //         });
    // },

    static initStaffStreetViewCons () {
      $('.sebco-staff-street-view-con')
        .each(function (ignore, v) {
          A.initStaffStreetViewCon(v)
        })
    }

    static initStaffSupportedBillTypesCon (con, tmpl) {
      A
        .ajaxPromise({
          dataType: 'json',
          timeout: 5000,
          url: con.data('endpoint')
        })
        .then(A.normalizeJsonResponse)
        .then(function (o) {
          var def,
            t

          if (!lo.isObjectLike(o.response)) {
            def = $.Deferred()
            t = new Error('InternalServerError')
            t.message = 'InternalServerError'
            def.reject(t)

            /* RETURNS */
            return def.promise()
          }

          return o.response
        })
        .then(function (o) {
          con.empty().append($(tmpl(o)))

          return A.waitPromise(100)
        })
        .fail(function (err) {
          var t

          if (err) {
            debug(err)
          }

          t = $(
            '<p><em>Could not load supported bill types from' +
              ' remote kiosk.</em></p>'
          )

          con.empty().append(t)
        })
        .always(function () {
          con.slideDown()
        })
    }

    static initStaffSupportedBillTypesCons () {
      var cons = $('.sebco-supported-bill-types-con')
      var tmpl

      if (cons.length < 1) {
        /* RETURNS */
        return
      }

      tmpl = w
        .Handlebars
        .compile($('#template-sebco-supported-bill-types').html())

      cons.each(function (ignore, v) {
        A.initStaffSupportedBillTypesCon($(v), tmpl)
      })
    }

    static initStaffUnifiedSearch (v) {
      var appendTo,
        endpoint,
        // focusNext,
        // idField,
        inst,
        lookup,
        renderItem,
        resizeMenu
        // submitOnSelect,
        // typeField;

      lookup = $(v)

      if (lookup.length < 1) {
        /* RETURNS */
        return
      }

      endpoint = lookup.data('endpoint')
      // focusNext = $(lookup.data("focusNext"));
      // idField = $(lookup.data("idField"));
      // submitOnSelect = lookup.data("submitOnSelect");
      // typeField = $(lookup.data("typeField"));
      appendTo = lookup.closest('form')

      if (appendTo.length < 1) {
        appendTo = lookup.closest('.sebco-customer-detail-wrapper')
      }

      if (appendTo.length < 1) {
        appendTo = lookup.closest('.sebco-device-history-wrapper')
      }

      if (appendTo.length < 1) {
        appendTo = lookup.closest('.sebco-staff-wrapper')
      }

      lookup.autocomplete({
        appendTo,
        classes: {
          'ui-autocomplete': 'sebco-autocomplete-results' +
            ' sebco-staff-unified-search-results '
        },
        focus: function (e) {
          e.preventDefault()
        },
        minLength: 1,
        position: {
          my: 'left top',
          at: 'left bottom',
          collision: 'none',
          of: lookup.closest('form')
        },
        select: function (e) {
          var t = e
          var u

          e.preventDefault()

          while (lo.isObjectLike(t.originalEvent)) {
            t = t.originalEvent

            if (
              lo.isString(t.type) &&
              (t.type === 'click') &&
              lo.isDefined(t.target)
            ) {
              u = $(t.target)

              if (u.hasClass('sebco-action-post')) {
                e.currentTarget = t.target
                A.handleUnifiedSearchActionPostClick(e)

                /* RETURNS */
                return
              }

              if (u.hasClass('sebco-action')) {
                w.location.href = u.attr('href')

                /* RETURNS */
                return
              }
            }
          }

          e.currentTarget = $(e.currentTarget)
            .find('.ui-state-active')
            .first()

          A.handleUnifiedSearchItemClick(e)
        },
        source: function (request, response) {
          var t = []

          A
            .ajaxPromise({
              url: endpoint,
              data: {
                exclude: lookup.data('exclude'),
                include: lookup.data('include'),
                q: request.term
              }
            })
            .then(function (o) {
              if (
                lo.isDefined(o) &&
                lo.isDefined(o.response) &&
                $.isArray(o.response)
              ) {
                $.each(o.response, function (ignore, v) {
                  var u = v.display_name

                  t.push({
                    actions: v.actions,
                    label: u,
                    id: v.entity_id,
                    type: v.typex,
                    value: u
                  })
                })
              }

              response(t)
            })
            .fail(function (err) {
              if (lo.isDefined(err)) {
                debug(err)
              }

              response(t)
            })
        }
      })

      /* Appease jslint */
      renderItem = '_renderItem'
      resizeMenu = '_resizeMenu'
      inst = lookup.autocomplete('instance')
      inst[renderItem] = A.handleUnifiedSearchRenderItem

      inst[resizeMenu] = function () {
        this.menu.element.outerWidth(appendTo.outerWidth())
      }
    }

    static initStaffUnifiedSearches () {
      var t = $(
        '.sebco-staff-unified-search-form' +
          ', .sebco-staff-unified-search-results-con'
      )

      if (t.length > 0) {
        $(d)
          .on(
            'click',
            '.sebco-staff-unified-search-results' +
              ' .sebco-action-post',
            A.handleUnifiedSearchActionPostClick
          )
      }

      $(d)
        .on(
          'click',
          '.sebco-staff-unified-search-results' +
            ' .sebco-staff-unified-search-item a,' +
            ' .sebco-staff-unified-search-results' +
            ' .sebco-staff-unified-search-result a',
          A.handleUnifiedSearchLinkClick
        )
        .on(
          'click',
          '.sebco-staff-unified-search-results' +
            ' .sebco-staff-unified-search-item,' +
            ' .sebco-staff-unified-search-results' +
            ' .sebco-staff-unified-search-result',
          A.handleUnifiedSearchItemClick
        )

      t = null

      $('.sebco-staff-unified-search-form input[name=q]')
        .each(function (ignore, v) {
          A.initStaffUnifiedSearch(v)
        })
    }

    static initStaffUserStats () {
      var table = $('.sebco-user-statistics-table')

      var locationSelect = $(
        '#sebco-staff-select-user-statistics-location-form-location-id'
      )
      var rangeSelect = $(
        '#sebco-staff-select-user-statistics-range-form-range'
      )

      if (table.length < 1 ||
          locationSelect.length < 1 ||
          rangeSelect.length < 1) {
        /* RETURNS */
        return
      }

      locationSelect.on('change', function (e) {
        var t

        table.data('locationId', $(e.currentTarget).val())

        t = table.data('urlTemplate') +
          '/' +
          table.data('locationId') +
          '/' +
          table.data('range')

        w.location.pathname = A.sanitizeUrl(t)
      })

      rangeSelect.on('change', function (e) {
        var t

        table.data('range', $(e.currentTarget).val())

        t = table.data('urlTemplate') +
          '/' +
          table.data('locationId') +
          '/' +
          table.data('range')

        w.location.pathname = A.sanitizeUrl(t)
      })
    }

    static initStaffUnreliableDevices () {
      var con = $('#sebco-unreliable-devices-con')
      var fn
      var tmplDevices
      var tmplEmpty
      var tmplError
      var tmplLoading

      if (con.length < 1) {
        /* RETURNS */
        return
      }

      tmplDevices = w
        .Handlebars
        .compile($('#template-sebco-unreliable-devices-table').html())

      tmplEmpty = w
        .Handlebars
        .compile($('#template-sebco-unreliable-devices-empty').html())

      tmplError = w
        .Handlebars
        .compile($('#template-sebco-unreliable-devices-error').html())

      tmplLoading = w
        .Handlebars
        .compile(
          $('#template-sebco-unreliable-devices-loading').html()
        )

      fn = function (page) {
        if (!Number.isFinite(page)) {
          page = 0
        }

        $.ajax({
          url: con.data('endpoint') + '/' + page,
          error: function (x, t, e) {
            debug([x, t, e])

            con.slideUp(function () {
              A.initStaffKioskConnectionStatusDestroy()

              con
                .empty()
                .append(tmplError({}))

              con.slideDown()
            })
          },
          success: function (o) {
            if (!lo.isObjectLike(o) ||
                !lo.isObjectLike(o.meta) ||
                !Number.isFinite(o.meta.status)) {
              con.slideUp(function () {
                A.initStaffKioskConnectionStatusDestroy()

                con
                  .empty()
                  .append(tmplError({}))

                con.slideDown()
              })

              /* RETURNS */
              return
            }

            if (o.meta.status >= 400) {
              con.slideUp(function () {
                A.initStaffKioskConnectionStatusDestroy()

                con
                  .empty()
                  .append(tmplError({}))

                con.slideDown()
              })

              /* RETURNS */
              return
            }

            if (o.response === null) {
              con.slideUp(function () {
                A.initStaffKioskConnectionStatusDestroy()

                con
                  .empty()
                  .append(tmplEmpty({}))

                con.slideDown()
              })

              /* RETURNS */
              return
            }

            con.slideUp(function () {
              A.initStaffKioskConnectionStatusDestroy()

              con
                .empty()
                .append(tmplDevices(o.response))

              A.initStaffKioskConnectionStatus()
              con.slideDown('slow')
            })
          }
        })
      }

      con
        .on(
          'click',
          '[data-load-page]',
          function (e) {
            con.slideUp(function () {
              A.initStaffKioskConnectionStatusDestroy()

              con
                .empty()
                .append(tmplLoading({}))

              con.slideDown(function () {
                fn($(e.currentTarget).data('loadPage'))
              })
            })
          }
        )

      fn(1)
    }

    static initStaffZipBuildingMapCon (el) {
      var k,
        m,
        q

      q = (el instanceof $)
        ? el
        : $(el)

      k = q.data('apiKey')

      if (!lo.isString(k) || $.trim(k) === '') {
        /* RETURNS */
        return
      }

      if (!lo.isString(q.attr('id'))) {
        q.attr('id', A.generateId())
      }

      if (!lo.isString(q.attr('data-id'))) {
        q.attr('data-id', q.attr('id'))
      }

      if (!lo.isString(q.data('id'))) {
        q.data('id', q.attr('id'))
      }

      A.win.on('resizeend', function () {
        var height
        var width = A.parseInt(A.win.outerWidth())

        if (width !== q.data('lastWidth')) {
          q.data('lastWidth', width)
          height = A.parseInt(q.outerWidth() * 0.4)
          q.data('lastHeight', height)
          q.animate({ height }, 700, 'easeOutQuad')
        }
      })

      A.promiseResolved()
        .then(function () {
          var u

          if (A.googleMapsApiLoaded !== true) {
            u = 'https://maps.googleapis.com/maps/api/js?key=' + k

            /* RETURNS */
            return A.ajaxPromiseScript(u)
          }

          u = $.Deferred()
          u.resolve()

          return u.promise()
        })
        .then(function () {
          var height,
            width

          A.googleMapsApiLoaded = true
          q.empty()
          width = A.parseInt(q.outerWidth())
          q.data('lastWidth', width)
          height = A.parseInt(width * 0.4)
          q.data('lastHeight', height)

          return q
            .animate({ height }, 700, 'easeOutQuad')
            .promise()
        })
        .then(function () {
          m = new w.google.maps.Map(
            q.get(0),
            {
              center: {
                lat: q.data('latitude'),
                lng: q.data('longitude')
              },
              zoom: q.data('zoom')
            }
          )

          return A.populateZipBuildingMap(q, m)
        })
    }

    static initStaffZipBuildingMapCons () {
      $('.sebco-staff-zip-building-map-con')
        .each(function (ignore, v) {
          A.initStaffZipBuildingMapCon(v)
        })
    }

    static initSuperCycleToggle () {
      $(d)
        .on(
          'click',
          '.sebco-device-form-washer .sebco-btn-remove',
          function (e) {
            var btn,
              durationCon,
              extendedCycleCount,
              extendedCycleCountInput,
              fm,
              priceCon

            e.preventDefault()
            btn = $(e.currentTarget)
            fm = btn.closest('.sebco-device-form')
            priceCon = fm.find('.sebco-device-form-price')
            durationCon = fm.find('.sebco-device-form-cycle-duration')

            extendedCycleCountInput =
              fm.find('input.sebco-extended-cycle-count')

            extendedCycleCount = A.parseInt(extendedCycleCountInput.val())

            if (!Number.isFinite(extendedCycleCount)) {
              extendedCycleCount = 0
            }

            if (extendedCycleCount === 0) {
              extendedCycleCount = 1
              btn.text(btn.data('textRemove'))
            } else if (extendedCycleCount === 1) {
              extendedCycleCount = 0
              btn.text(btn.data('textAdd'))
            }

            extendedCycleCountInput.val(extendedCycleCount)
            priceCon.data('extendedCycleCount', extendedCycleCount)

            durationCon
              .data('extendedCycleCount', extendedCycleCount)

            A.updatePrice(priceCon)
            A.updateDuration(durationCon)
          }
        )
    }

    static initTopOffControls () {
      $(d)
        .on(
          'click',
          '.sebco-device-form-top-off-controls button',
          function (e) {
            var btn,
              con,
              extendedCycleCount,
              extendedCycleCountInput,
              extendedDuration,
              extendedPrice,
              fm,
              maxExtendedCycleCount,
              minDuration,
              minPrice,
              newDuration,
              newPrice,
              topOffSubtotal

            e.preventDefault()
            btn = $(e.currentTarget)

            con = btn
              .closest('.sebco-device-form-top-off-controls')

            fm = con.closest('.sebco-device-form')
            minPrice = parseFloat(con.data('minimumPrice'))
            minDuration = A.parseInt(con.data('minimumDuration'))
            extendedDuration = A.parseInt(con.data('extendedDuration'))
            extendedPrice = parseFloat(con.data('extendedPrice'))
            maxExtendedCycleCount = con.data('maximumExtendedCycleCount')

            extendedCycleCountInput =
              fm.find('input.sebco-extended-cycle-count')

            extendedCycleCount = A.parseInt(extendedCycleCountInput.val())

            if (!Number.isFinite(extendedCycleCount)) {
              extendedCycleCount = 0
            }

            if (btn.attr('name') === 'add') {
              extendedCycleCount += 1

              if (extendedCycleCount > maxExtendedCycleCount) {
                extendedCycleCount = maxExtendedCycleCount
              }
            } else {
              extendedCycleCount -= 1

              if (extendedCycleCount < 0) {
                extendedCycleCount = 0
              }
            }

            extendedCycleCountInput.val(extendedCycleCount)

            topOffSubtotal = (extendedCycleCount * extendedPrice)
            newPrice = minPrice + topOffSubtotal

            if (newPrice < minPrice) {
              newPrice = minPrice
            }

            newDuration = minDuration + (extendedCycleCount * extendedDuration)

            if (newDuration < minDuration) {
              newDuration = minDuration
            }

            if (newPrice < 1) {
              fm
                .find('.sebco-device-form-price .sebco-value')
                .text(
                  newPrice
                    .toFixed(2)
                    .toString()
                    .replace(/^0\./, '') + '¢'
                )

              fm
                .find('.sebco-device-form-price sup')
                .attr('hidden', 'hidden')
            } else {
              fm
                .find('.sebco-device-form-price .sebco-value')
                .text(newPrice.toPrecision(3))

              fm
                .find('.sebco-device-form-price sup')
                .removeAttr('hidden')
            }

            fm
              .find('.sebco-device-form-cycle-duration .value')
              .text(newDuration)
          }
        )
    }

    static initTwisty () {
      $(d)
        .on(
          'click',
          '.sebco-twisty',
          function (e) {
            var con,
              trigger

            e.preventDefault()
            trigger = $(e.currentTarget)
            con = $('#' + trigger.data('targetId'))

            if (con.attr('hidden') === 'hidden') {
              con.hide().removeAttr('hidden')
            }

            if (con.length > 0) {
              if (trigger.hasClass('sebco-twisty-open')) {
                trigger.removeClass('sebco-twisty-open')
                con.slideUp({ duration: 200 })
              } else {
                trigger.addClass('sebco-twisty-open')
                con.slideDown({ duration: 200 })
              }
            }
          }
        )
    }

    static initTimezones () {
      var t

      if (!w.Intl) {
        return
      }

      try {
        t = w.Intl.DateTimeFormat().resolvedOptions().timeZone
      } catch (ignore) {}

      if (!t) {
        return
      }

      d.querySelectorAll('.sebco-tz')
        .forEach(function (v) {
          if (v.hasAttribute('data-tz')) {
            v.setAttribute('data-tz', t)
            return
          }

          if (
            lo.isUndefined(v.value) ||
            (v.value === '') ||
            (v.value === null)
          ) {
            v.value = t
          }
        })
    }

    static initUserLookup (v) {
      var lookup = $(v)
      var orgId = lookup.data('lookupOrganizationId')
      var parent

      parent = lookup.closest('.sebco-staff-wrapper')

      if (parent.length < 1) {
        parent = lookup.closest('.sebco-staff-dashboard-wrapper')
      }

      if (parent.length < 1) {
        parent = lookup.closest('.sebco-customer-detail-wrapper')
      }

      if (parent.length < 1) {
        parent = lookup.closest('.sebco-staff-functional-dialog')
      }

      lookup.autocomplete({
        appendTo: parent,
        classes: { 'ui-autocomplete': 'sebco-autocomplete-results' },
        minLength: 3,
        position: {
          my: 'left top',
          at: 'left bottom',
          collision: 'flipfit'
        },
        select: function (ignore, ui) {
          var bal,
            focusNext

          if (lo.isDefined(ui.item.balance)) {
            bal = parseFloat(ui.item.balance)

            if (Number.isFinite(bal)) {
              $(lookup.data('lookupBalanceField'))
                .val(bal.toFixed(2).toString())
            }
          }

          $(lookup.data('lookupIdField')).val(ui.item.id)
          lookup.val(ui.item.label)
          focusNext = $(lookup.data('lookupFocusNext'))

          if (focusNext.length > 0) {
            focusNext.trigger('focus')
          }

          if (lookup.data('lookupSubmitOnSelect') === true) {
            lookup.closest('form').trigger('submit')
          }

          return false
        },
        source: function (request, response) {
          var data

          lookup.addClass('sebco-lookup-in-progress')
          data = { terms: request.term }

          if (lo.isDefined(orgId)) {
            data.organizationId = orgId
          }

          $.ajax({
            url: lookup.data('lookupEndpoint'),
            data,
            complete: function () {
              lookup.removeClass('sebco-lookup-in-progress')
            },
            error: function (x, t, e) {
              debug([x, t, e])
              response([])
            },
            success: function (o) {
              var users = []

              if (
                lo.isDefined(o) &&
                lo.isDefined(o.response) &&
                $.isArray(o.response)
              ) {
                $.each(
                  o.response,
                  function (ignore, user) {
                    var txt = user.name.replace(/\n/, ' ')

                    if (lo.isDefined(user.email)) {
                      txt += ', ' + user.email
                    }

                    if (lo.isDefined(user.locationName)) {
                      txt += ', ' + user.locationName
                    }

                    if (lo.isDefined(user.apartmentName)) {
                      txt += ', ' + user.apartmentName
                    }

                    users.push({
                      value: txt,
                      label: txt,
                      id: user.id,
                      balance: user.balance
                    })
                  }
                )
              }

              response(users)
            }
          })
        }
      })
    }

    static initUserLookups () {
      $('.sebco-staff-user-lookup').each(function (ignore, v) {
        A.initUserLookup(v)
      })
    }

    /**
     * @param {HTMLFormElement} el
     * @private
     */
    static initWCloudDeviceEditForm (el) {
      const { machineType } = el.dataset
      const recordType = parseInt(el.dataset.recordType, 10)

      switch (machineType) {
        case 'washer':
          lo.noop()
          break
        case 'dryer':
          switch (recordType) {
            case 52:
              lo.noop(new WCloudDeviceEditFormDryer52(el))
              break
          }

          break
      }
    }

    static initWCloudDeviceEditForms () {
      Array.from(d.querySelectorAll('.sebco-form-wcloud-edit'))
        .forEach(A.initWCloudDeviceEditForm)
    }

    /**
     * @param {HTMLFormElement} el
     * @private
     */
    static initWCloudDeviceSelectionForm (el) {
      const { machineType } = el.dataset
      const recordType = parseInt(el.dataset.recordType, 10)

      switch (machineType) {
        case 'washer':
          switch (recordType) {
            case 48:
            case 52:
              lo.noop(new WCloudDeviceSelectionFormWasher52(el))
              return
          }

          throw new RangeError(`Unsupported record type ${recordType}`)
        case 'dryer':
          switch (recordType) {
            case 52:
              lo.noop(new WCloudDeviceSelectionFormDryer52(el))
              return
          }

          throw new RangeError(`Unsupported record type ${recordType}`)
      }

      throw new RangeError(`Unsupported machine type ${machineType}`)
    }

    static initWCloudDeviceSelectionForms () {
      Array.from(d.querySelectorAll('.sebco-form-wcloud-device'))
        .forEach(A.initWCloudDeviceSelectionForm)
    }

    static initWindowResizeEnd () {
      var win = A.win

      win.on('resize', function (e) {
        w.clearTimeout(A.windowResizeEndTimer)
        A.windowResizeEndTimer = null

        A.windowResizeEndTimer = w.setTimeout(
          function () {
            win.trigger('resizeend', e)
          },
          400
        )
      })
    }

    static initZipLookup () {
      $('.sebco-staff-zip-lookup').each(function (ignore, v) {
        var lookup = $(v)
        var wrapper = lookup.closest('.sebco-staff-wrapper')

        if (wrapper.length < 1) {
          wrapper = lookup.closest('.sebco-customer-detail-wrapper')
        }

        lookup.autocomplete({
          appendTo: wrapper,
          classes: { 'ui-autocomplete': 'sebco-autocomplete-results' },
          minLength: 3,
          position: {
            my: 'left top',
            at: 'left bottom',
            collision: 'flipfit'
          },
          select: function (ignore, ui) {
            var focusNext

            $(lookup.data('lookupCityField')).val(ui.item.city)
            $(lookup.data('lookupStateField')).val(ui.item.state)
            lookup.val(ui.item.value)
            focusNext = $(lookup.data('lookupFocusNext'))

            if (focusNext.length > 0) {
              focusNext.trigger('focus')
            }

            if (lookup.data('lookupSubmitOnSelect') === true) {
              lookup.closest('form').trigger('submit')
            }

            return false
          },
          source: function (request, response) {
            $.ajax({
              url: lookup.data('lookupEndpoint'),
              data: { terms: request.term },
              error: function (x, t, e) {
                debug([x, t, e])
                response([])
              },
              success: function (o) {
                var zips = []

                if (
                  lo.isDefined(o) &&
                  lo.isDefined(o.response) &&
                  $.isArray(o.response)
                ) {
                  $.each(
                    o.response,
                    function (ignore, zip) {
                      zips.push({
                        value: zip.zip,
                        label: zip.name,
                        city: zip.city,
                        state: zip.state
                      })
                    }
                  )
                }

                response(zips)
              }
            })
          }
        })
      })
    }

    /*
     * Queues the screen saver to be displayed after the number of milliseconds
     * specified in a.screenSaverDelay has elapsed. If the screen saver is
     * already queued, the previous queue request is cancelled. If no screen
     * saver production exists in the page, does nothing.
     *
     * Note: there is only meant to be a single screen saver production in a
     * page. If more than one is found in the page, the first is kept, but
     * additional screen savers are removed.
     */
    static queueScreenSaver () {
      var screenSaver = $('.sebco-screen-saver')

      screenSaver.each(function (i, v) {
        var tmp = $(v)
        var t = tmp.data('timerId')

        w.clearTimeout(t)
        t = null

        if (i > 0) {
          tmp.remove()
        }
      })

      screenSaver
        .data(
          'timerId',
          w.setTimeout(A.showScreenSaver, A.screenSaverDelay)
        )
    }

    /*
     * If a meta refresh tag exists, execute at 1.1 * the value given in tag (in
     * case refreshes are over-aggressively blocked).
     */
    static metaRefresh () {
      var meta = $('meta[http-equiv]').filter(function (ignore, v) {
        return $.trim($(v).attr('http-equiv')).toLowerCase() === 'refresh'
      })
      var o

      if (meta.length > 0) {
        o = A.parseMetaRefresh(meta.first().attr('content'))

        if (lo.isString(o.url) && o.url !== '') {
          setTimeout(
            function () {
              w.location.href = o.url
            },
            A.parseInt((1.1 * o.delay) * 1000)
          )
        }
      }
    }

    static normalizeJsonResponse (o) {
      var def

      if (!lo.isObjectLike(o)) {
        o = {}
      }

      if (!lo.isObjectLike(o.meta)) {
        o.meta = {}
      }

      if (!Number.isFinite(o.meta.status) || !lo.isString(o.meta.message)) {
        o.meta.status = 500
        o.meta.message = 'Internal Server Error'
      }

      if (!lo.isDefined(o.response)) {
        o.response = null
      }

      def = $.Deferred()
      def.resolve(o)

      return def.promise()
    }

    static parseInt (n) {
      return parseInt(n, 10)
    }

    /*
     * Takes a meta refresh tag's content value, and returns an object
     * containing "delay" (number) and "url" (string) properties.  If the
     * "delay" can not be parsed, its value will be 0.  If the "url" can not
     * parsed, its value will be an empty string.
     */
    static parseMetaRefresh (val) {
      var o = { delay: 0, url: '' }
      var tmp
      var tokens

      if (lo.isString(val)) {
        tokens = $.trim(val).split(';')

        if (tokens.length > 0) {
          tmp = A.parseInt(tokens.shift())

          o.delay = isNaN(tmp)
            ? 0
            : tmp

          if (tokens.length > 0) {
            tmp = $.trim(tokens.join(';'))
              .replace(/^;?\\s*url\\s*=\\s*(.*)/i, '$1')

            if (/^\u0027/.test(tmp)) {
              tmp = tmp.replace(/^\u0027([^\u0027]+)\u0027.*/, '$1')
            } else if (/^\u0022/.test(tmp)) {
              tmp = tmp.replace(/^\u0022([^\u0022]+)\u0022.*/, '$1')
            }

            o.url = $.trim(tmp)
          }
        }
      }

      return o
    }

    static populateZipBuildingMap (container, map, page) {
      var opts = { url: container.data('endpoint') }

      if (Number.isFinite(page)) {
        opts.data = { page }
      }

      return A
        .ajaxPromise(opts)
        .then(A.normalizeJsonResponse)
        .then(function (o) {
          var n

          if ($.isArray(o.response.buildings) &&
              (o.response.buildings.length > 0)) {
            A
              .populateZipBuildingMapWithMarkers(
                container,
                map,
                o.response.buildings
              )
          }

          if (Number.isFinite(o.response.page)) {
            n = o.response.page

            if (Number.isFinite(o.response.pages) &&
                (n < o.response.pages) &&
                (n < 50)) {
              A.populateZipBuildingMap(container, map, 1 + n)
            }
          }
        })
    }

    static populateZipBuildingMapWithMarker (container, map, marker) {
      var p,
        t

      t = marker.address +
        ', ' +
        marker.city +
        ', ' +
        marker.statex +
        ' ' +
        marker.zip

      if (marker.latitude === null || marker.longitude === null) {
        p = A
          .findPositionByAddress(container.data('endpointLatLong'), t)
          .then(function (u) {
            if (u !== null) {
              marker.latitude = u.latitude
              marker.longitude = u.longitude
            }

            A
              .ajaxPromise({
                data: {
                  id: marker.id,
                  latitude: u.latitude,
                  longitude: u.longitude
                },
                type: 'POST',
                url: container.data('endpointUpdateLatLong')
              })
              .fail(lo.noop)
          })
      } else {
        p = A.promiseResolved()
      }

      p
        .then(function () {
          var c,
            k

          container.data('markerIds')['id-' + marker.id] = true

          if (marker.latitude !== null) {
            if (marker.amenities_laundry_room === 1 &&
                marker.building_policy_wd_allowed === 0) {
              c = '#2CA02C'
            } else if (marker.amenities_laundry_room === 1 &&
                marker.building_policy_wd_allowed === 1) {
              c = '#FFBB78'
            } else if (marker.amenities_laundry_room === 1 &&
                marker.building_policy_wd_allowed === null) {
              c = '#FF7F0E'
            } else if (marker.amenities_laundry_room === null) {
              c = '#6A3D9A'
            } else {
              c = '#1F77B4'
            }

            k = new w.google.maps.Marker({
              icon: {
                fillColor: c,
                fillOpacity: 0.8,
                path: w.google.maps.SymbolPath.CIRCLE,
                scale: 4,
                strokeOpacity: 0,
                strokeWeight: 0
              },
              map,
              position: {
                lat: marker.latitude,
                lng: marker.longitude
              },
              title: t
            })

            w
              .google
              .maps
              .event
              .addListener(k, 'click', function () {
                var n = Number.parseInt(container.data('organizationId'), 10)
                var x = A
                  .sanitizeUrl('/building/view/' + n + '/' + marker.id)

                w.open(x, '_blank')
                // w.location.pathname = a
                //     .sanitizeUrl(
                //         "/building/view/" + marker.id
                //     );
              })

            lo.noop(k)
          }
        })
    }

    static populateZipBuildingMapWithMarkers (container, map, markers) {
      var delay = 0
      var increment = 1

      $.each(markers, function (ignore, v) {
        var k = 'id-' + v.id

        if (container.data('markerIds')[k] !== true) {
          container.data('markerIds')[k] = true

          A.waitPromise(delay)
            .then(function () {
              A
                .populateZipBuildingMapWithMarker(
                  container,
                  map,
                  v
                )
            })

          delay += increment
        }
      })
    }

    static preCacheImages () {
      var i = 0
      var j
      var urls = [
        '/img/ic_timer_white_48dp.png',
        '/img/icon-arrow-up-72.png'
      ]

      j = urls.length

      while (i < j) {
        try {
          (new Image()).src = urls[i]
        } catch (ignore) {}

        i += 1
      }
    }

    static prepareStaffMachineStartsData (scale, days) {
      var high = 0
      var ppu

      $.each(days, function (ignore, v) {
        if (v.values.total > high) {
          high = v.values.total
        }
      })

      ppu = (high > 0)
        ? (scale / high)
        : 0

      $.each(days, function (ignore, v) {
        var total,
          x

        total = (lo.isDefined(v.values) && lo.isDefined(v.values.total))
          ? v.values.total
          : 0

        if (total >= 1000000) {
          v.totalStr = (total / 1000000)
          v.totalStr = v.totalStr.toFixed(1) + 'M'
        } else if (total >= 1000) {
          v.totalStr = (total / 1000)
          v.totalStr = v.totalStr.toFixed(1) + 'K'
        } else {
          v.totalStr = total
          v.totalStr += ''
        }

        $.each(v.values, function (ik, iv) {
          if (ik === 'total' && iv === high && high !== 0) {
            v.heights.total = scale + 'px'
          } else {
            x = (ppu * iv)
            v.heights[ik] = x.toFixed(5) + 'px'
          }
        })
      })

      return days
    }

    static promiseResolved (o) {
      var def = $.Deferred()

      def.resolve(
        lo.isDefined(o)
          ? o
          : null
      )

      return def.promise()
    }

    /**
     * @param {string} message
     * @param {string | null} title
     * @param {string | null} extraClasses
     */
    static putFlash (
      message,
      title = null,
      extraClasses = null
    ) {
      const flash = A.buildFlash(message, title, extraClasses)

      $('.sebco-local-flash-con').append(flash)
      flash.slideDown('fast')
    }

    static refreshDeviceStatuses (con) {
      var initialDeviceStatuses,
        roomId

      if (
        !lo.isDefined(con) ||
        !lo.isDefined(con.data) ||
        !lo.isFunction(con.data)
      ) {
        /* RETURNS */
        return
      }

      initialDeviceStatuses = con.data('initialDeviceStatuses')

      if (!lo.isObjectLike(initialDeviceStatuses)) {
        /* RETURNS */
        return
      }

      roomId = con.data('roomId')

      if (!Number.isFinite(roomId)) {
        /* RETURNS */
        return
      }

      $.ajax({
        url: '/api/v1/devicestatuses',
        data: { roomId },
        dataType: 'json',
        success: function (o) {
          var statusesEqual = true

          if (
            lo.isDefined(o) &&
            lo.isDefined(o.meta) &&
            Number.isFinite(o.meta.status) &&
            (o.meta.status < 400)
          ) {
            statusesEqual = A.deviceStatusesEqual(
              initialDeviceStatuses,
              o.response
            )
          }

          if (!statusesEqual) {
            w.location.reload()
          }
        },
        type: 'POST'
      })

      A.waitPromise(15000)
        .then(function () {
          A.refreshDeviceStatuses(con)
        })
    }

    static sanitizeAttr (s) {
      if (lo.isString(s)) {
        return $
          .trim(s)
          .replace(/[^a-zA-Z0-9]/g, function (c) {
            return '&#x' +
              c.charCodeAt(0).toString(16).toUpperCase() +
              ';'
          })
      }

      return null
    }

    static sanitizeUrl (s) {
      var r,
        t

      if (!lo.isString(s)) {
        /* RETURNS */
        return 'about:blank'
      }

      r = /^(http:|https:|mailto:|tel:|\.\/|\/)/gim
      t = $.trim(s)

      if (!r.test(t)) {
        /* RETURNS */
        return 'about:blank'
      }

      return t
        .replace(/[\0'"\\\b\f\n\r\v{}|^~[\]`<> ]/gm, '')
    }

    static showAutoSignOutDialog () {
      var cancel
      var con = $('.sebco-dialog-auto-sign-out')
      var decrementSeconds
      var defaultDelay
      var secondsCon
      var signOut
      var signOutTimer

      if ($('body:first').data('isKioskLogin') === true &&
          con.length > 0) {
        defaultDelay = 30
        secondsCon = con.find('.sebco-seconds')
        signOut = con.find('.sebco-btn-sign-out')

        cancel = function (e) {
          e.preventDefault()
          e.stopPropagation()

          try {
            w.clearTimeout(signOutTimer)
          } catch (ignore) {}

          try {
            con
              .dialog('close')
              .remove()
              .dialog('destroy')
          } catch (ignore) {}

          w.location.href = $(e.currentTarget).get(0).href
        }

        decrementSeconds = function () {
          var seconds

          try {
            seconds = secondsCon.data('seconds')

            if (!Number.isFinite(seconds)) {
              seconds = defaultDelay
            }

            if (seconds > 0) {
              seconds -= 1

              secondsCon
                .data('seconds', seconds)
                .text(seconds)
            } else if (seconds === 0) {
              w.location.href = signOut.attr('href')
            }
          } catch (ignore) {}

          signOutTimer = setTimeout(decrementSeconds, 1000)
          $('body:first').data('timerSignOutTimer', signOutTimer)
        }

        con
          .removeAttr('hidden')
          .find('.sebco-btn-cancel')
          .on('click', cancel)
          .end()
          .dialog({ dialogClass: 'sebco-dialog-auto-sign-out-con' })
          .animate({ opacity: 1 }, function () {
            decrementSeconds()
          })
      }
    }

    static showLoadingDialog (fn) {
      var dia

      dia = $('.sebco-dialog-loading-con')

      if (dia.length < 1) {
        dia = $($('#template-sebco-dialog-loading').html()).hide()
        $('body').first().append(dia)

        dia.dialog({
          autoOpen: false,
          dialogClass: 'sebco-dialog-loading-con',
          height: 128,
          modal: true,
          resizable: false,
          width: 256
        })
      }

      dia.dialog('open')

      if (lo.isFunction(fn)) {
        fn()
      }
    }

    static showScanningDialog (fn) {
      var dia

      dia = $('#sebco-dialog-scanning')

      if (dia.length < 1) {
        dia = $($('#template-sebco-dialog-scanning').html()).hide()
        $('body').first().append(dia)
      }

      dia.dialog({
        autoOpen: false,
        dialogClass: 'sebco-dialog-scanning-con',
        height: 128,
        modal: true,
        resizable: false,
        width: 256
      })

      dia.dialog('open')

      if (lo.isFunction(fn)) {
        fn()
      }
    }

    static showSigningInDialog (fn) {
      var dia

      dia = $('#sebco-dialog-signing-in')

      if (dia.length < 1) {
        dia = $($('#template-sebco-dialog-signing-in').html()).hide()
        $('body').first().append(dia)
      }

      dia.dialog({
        autoOpen: false,
        dialogClass: 'sebco-dialog-signing-in-con',
        height: 128,
        modal: true,
        resizable: false,
        width: 256
      })

      dia.dialog('open')

      if (lo.isFunction(fn)) {
        fn()
      }
    }

    /*
     * Shows the screen saver. If no screen saver production exists in the page,
     * does nothing.
     *
     * Note: there is only meant to be a single screen saver production in a
     * page. If more than one is found in the page, the first is kept, but
     * additional screen savers are removed.
     */
    static showScreenSaver () {
      var screenSaver = $('.sebco-screen-saver')
      var handleFadeInEnd
      var slideCount
      var slides
      var rotate

      if (screenSaver.length < 1) {
        return
      }

      if (screenSaver.length > 1) {
        screenSaver.each(function (i, v) {
          if (i > 0) {
            $(v).remove()
          }
        })
      }

      screenSaver.data('currentIndex', 0)
      slides = screenSaver.find('.sebco-slide')
      slideCount = slides.length

      slides.each(function (i, v) {
        var tmp = $(v)

        if (i > 0) {
          tmp.css({
            display: 'none',
            opacity: 0
          })
        } else {
          tmp.css({
            display: 'block',
            opacity: 1
          })
        }
      })

      handleFadeInEnd = function () {
        var currentIndex = screenSaver.data('currentIndex')
        var currentSlide = $(slides[currentIndex])
        var t

        if (currentSlide.hasClass('sebco-slide-video')) {
          t = currentSlide.find('video').first()

          if (t.length < 1) {
            screenSaver
              .data(
                'slideTimerId',
                w.setTimeout(rotate, A.screenSaverRotateDelay)
              )

            /* RETURNS */
            return
          }

          t.one('ended', function () {
            screenSaver
              .data('slideTimerId', w.setTimeout(rotate, 100))
          })

          t.get(0)
            .play()
            .catch(function (err) {
              if (lo.isDefined(err)) {
                debug(err)
              }

              screenSaver
                .data(
                  'slideTimerId',
                  w.setTimeout(rotate, 3000)
                )
            })
        } else {
          screenSaver
            .data(
              'slideTimerId',
              w.setTimeout(rotate, A.screenSaverRotateDelay)
            )
        }
      }

      rotate = function () {
        var currentIndex,
          currentSlide,
          nextIndex,
          nextSlide,
          t

        t = screenSaver.data('slideTimerId')

        screenSaver
          .removeData('slideTimerId')
          .removeAttr('slide-timer-id')

        w.clearTimeout(t)
        t = null

        if (slideCount < 2) {
          return
        }

        currentIndex = screenSaver.data('currentIndex')
        nextIndex = currentIndex + 1

        if (nextIndex >= slideCount) {
          nextIndex = 0
        }

        screenSaver.data('currentIndex', nextIndex)
        currentSlide = $(slides[currentIndex])

        currentSlide.animate({ opacity: 0 }, 1000, function () {
          currentSlide
            .css({ display: 'none' })
            .find('video')
            .each(function (ignore, v) {
              v.currentTime = 0
            })
        })

        nextSlide = $(slides[nextIndex])

        if (nextSlide.hasClass('sebco-slide-video')) {
          nextSlide
            .find('video')
            .attr({
              height: A.parseInt(A.win.outerHeight()),
              width: A.parseInt(A.win.outerWidth())
            })
        }

        nextSlide
          .css({ display: 'block' })
          .animate({ opacity: 1 }, 1000, handleFadeInEnd)
      }

      screenSaver
        .data('isVisible', true)
        .css({ display: 'block', opacity: 0 })
        .animate({ opacity: 1 }, 1000, handleFadeInEnd)

      $('#sebco-kiosk-login-scan-form-rfid').trigger('focus')
    }

    static staffSetupRoomRefreshRoom (opts) {
      var timer

      $.ajax({
        url: opts.endpoint,
        error: function (x) {
          var data = { title: 'API Connection Error' }

          if (
            lo.isObjectLike(x) &&
            lo.isDefined(x.status) &&
            lo.isDefined(x.statusText)
          ) {
            data.description = x.status + ' ' + x.statusText
          }

          opts
            .loadingCon
            .empty()
            .append(opts.errorTmpl(data))

          opts.loadingCon.show()
          opts.enabledCon.hide()
          opts.disabledCon.hide()
        },
        success: function (o) {
          var clientWidth,
            maxBlocks,
            setupEnabledDevices,
            setupDisabledDevices

          if (!lo.isObjectLike(o) ||
              !lo.isObjectLike(o.meta) ||
              !Number.isFinite(o.meta.status) ||
              o.meta.status >= 400 ||
              !lo.isObjectLike(o.response) ||
              !$.isArray(o.response.devices) ||
              o.response.devices.length < 1) {
            opts
              .loadingCon
              .empty()
              .append(opts.noDevicesTmpl({}))

            opts.loadingCon.show()
            opts.enabledCon.hide()
            opts.disabledCon.hide()

            /* RETURNS */
            return
          }

          opts.loadingCon.hide()
          setupEnabledDevices = []
          setupDisabledDevices = []
          clientWidth = A.parseInt(A.win.width())

          if (!Number.isFinite(clientWidth)) {
            clientWidth = 767
          }

          if (clientWidth >= 1920) {
            maxBlocks = 20
          } else if (clientWidth >= 1200) {
            maxBlocks = 12
          } else if (clientWidth >= 992) {
            maxBlocks = 9
          } else if (clientWidth >= 768) {
            maxBlocks = 8
          } else if (clientWidth >= 375) {
            maxBlocks = 7
          } else {
            maxBlocks = 6
          }

          $.each(o.response.devices, function (ignore, v) {
            var setupEnabled = 0
            var start

            if (!$.isArray(v.setup_data)) {
              v.setup_data = []
            }

            if (v.setup_data.length > maxBlocks) {
              start = v.setup_data.length - maxBlocks

              if (start < 0) {
                start = 0
              }

              v.setup_data = v.setup_data.slice(start)
            }

            v.setup_data.forEach(function (x) {
              var date,
                hours,
                meridiem,
                minutes

              date = new Date(x.event_timestamp)
              hours = date.getHours()

              meridiem = (hours >= 12)
                ? 'P'
                : 'A'

              hours = (hours >= 12)
                ? (hours - 12)
                : hours

              hours = (hours === 0)
                ? 12
                : hours

              minutes = date.getMinutes()

              minutes = (minutes < 10)
                ? ('0' + minutes)
                : minutes

              x.date = (1 + date.getMonth()) +
                '/' +
                date.getDate() +
                '<br />' +
                hours +
                ':' +
                minutes +
                meridiem

              x.isoDate = date.toISOString()
            })

            if (lo.isDefined(v.setup_mode)) {
              setupEnabled = A.parseInt(v.setup_mode)
            }

            if (Number.isFinite(setupEnabled) && setupEnabled > 0) {
              setupEnabledDevices.push(v)
            } else {
              setupDisabledDevices.push(v)
            }
          })

          $.each(setupEnabledDevices, function (ignore, v) {
            var device

            device = $(
              '#sebco-setup-room-device-mac-' + v.mac_address
            )

            if (device.length < 1) {
              device = opts.enabledTmpl({ device: v })
            } else {
              device.replaceWith(opts.enabledTmpl({ device: v }))

              device = $(
                '#sebco-setup-room-device-mac-' + v.mac_address
              )
            }

            opts.enabledCon.append(device)
          })

          $.each(setupDisabledDevices, function (ignore, v) {
            var device

            device = $(
              '#sebco-setup-room-device-mac-' + v.mac_address
            )

            if (device.length < 1) {
              device = opts.disabledTmpl({ device: v })
            } else {
              device.replaceWith(opts.disabledTmpl({ device: v }))

              device = $(
                '#sebco-setup-room-device-mac-' + v.mac_address
              )
            }

            opts.disabledCon.append(device)
          })

          opts.loadingCon.hide()
          opts.enabledCon.show()
          opts.disabledCon.show()
        }
      })

      timer = w.setTimeout(
        function () {
          w.clearTimeout(timer)
          timer = null
          A.staffSetupRoomRefreshRoom(opts)
        },
        10000
      )
    }

    static toggleResponsiveNav () {
      $('.sebco-top-row').toggleClass('sebco-show-responsive-nav')
    }

    static toggleResponsiveNavMmp () {
      var el = d.querySelector('.sebco-top-row')

      if (el) {
        el.classList.toggle('mmp-show-responsive-nav')
      }
    }

    static updateDeviceEventsTimeDelta (later, earlier) {
      var t,
        u,
        v,
        x,
        y

      if (
        lo.isDefined(later) &&
        lo.isString(later.attr('datetime')) &&
        lo.isDefined(earlier) &&
        lo.isString(earlier.attr('datetime'))
      ) {
        t = new Date(later.attr('datetime'))
        u = new Date(earlier.attr('datetime'))
        v = t.getTime() - u.getTime()

        if (v >= 604800000) {
          x = A.parseInt(v / 604800000)

          x += (x === 1)
            ? ' week'
            : ' weeks'
        } else if (v >= 86400000) {
          x = A.parseInt(v / 86400000)

          x += (x === 1)
            ? ' day'
            : ' days'
        } else if (v >= 3600000) {
          x = A.parseInt(v / 3600000)

          x += (x === 1)
            ? ' hr'
            : ' hrs'
        } else if (v >= 60000) {
          y = (v / 60000)
          x = parseFloat(y.toFixed(1)) + ' min'
        } else {
          y = (v / 1000)
          x = parseFloat(y.toFixed(1)) + ' sec'
        }

        later
          .attr('title', v + ' milliseconds')
          .text(x)
      }
    }

    static updateDeviceSetupMessageStatuses () {
      var devices
      var ids = []

      devices = $('.sebco-setup-message-indicator')

      if (devices.length > 0) {
        devices.each(function (ignore, v) {
          ids.push($(v).data('deviceId'))
        })

        A
          .ajaxPromise({
            url: '/api/v1/device/setupmessage/statuses',
            cache: false,
            data: { ids: JSON.stringify(ids) },
            dataType: 'json',
            type: 'post'
          })
          .then(function (o) {
            var data

            if (!lo.isObjectLike(o) ||
                !lo.isObjectLike(o.meta) ||
                !Number.isFinite(o.meta.status) ||
                o.meta.status >= 400 ||
                !lo.isObjectLike(o.response)) {
              /* RETURNS */
              return
            }

            data = o.response

            devices.each(function (ignore, v) {
              var classesAdd,
                classesRemove,
                key,
                label,
                status,
                target

              v = $(v)
              key = 'device-id-' + v.data('deviceId')

              if (lo.isObjectLike(data[key])) {
                status = data[key]

                label = lo.isString(status.label)
                  ? status.label
                  : ''

                if (status.setupMessageInProgress === true) {
                  classesAdd = 'sebco-setup-message-indicator-active'
                  classesRemove = 'sebco-setup-message-indicator-inactive'
                } else {
                  classesAdd = 'sebco-setup-message-indicator-inactive'
                  classesRemove = 'sebco-setup-message-indicator-active'
                }

                v
                  .addClass(classesAdd)
                  .removeClass(classesRemove)
                  .attr('title', label)
                  .find('.sr-only')
                  .text(label)

                target = v.data('labelId')

                if (lo.isDefined(target)) {
                  $('#' + target).text(label)
                }
              }
            })
          })
          .fail(function (err) {
            lo.noop(err)
          })
      }

      A.waitPromise(15000).then(A.updateDeviceSetupMessageStatuses)
    }

    static updateDuration (jq) {
      var currentCycleDuration = A.parseInt(
        jq.data('currentCycleDuration')
      )
      var extendedCycleCount = A.parseInt(
        jq.data('extendedCycleCount')
      )
      var extendedCycleDuration = A.parseInt(
        jq.data('extendedCycleDuration')
      )
      var cycleDuration = A.parseInt(jq.data('cycleDuration'))

      cycleDuration += (extendedCycleCount * extendedCycleDuration)

      if (cycleDuration !== currentCycleDuration) {
        jq.data('currentCycleDuration', cycleDuration)
        jq.find('.value').text(cycleDuration)
      }
    }

    static updatePrice (jq) {
      var currentPrice = parseFloat(jq.data('currentPrice'))
      var detergentCount = A.parseInt(jq.data('detergentCount'))
      var detergentPrice = parseFloat(jq.data('detergentPrice'))
      var extendedCycleCount = A.parseInt(
        jq.data('extendedCycleCount')
      )
      var extendedCyclePrice = parseFloat(jq.data('extendedCyclePrice'))
      var price = parseFloat(jq.data('price'))
      var tmp

      price += (detergentCount * detergentPrice) +
        (extendedCycleCount * extendedCyclePrice)

      if (price !== currentPrice) {
        jq.data('currentPrice', price)

        if (price < 1) {
          jq.find('sup').attr('hidden', 'hidden')

          tmp = price
            .toFixed(2)
            .toString()
            .replace(/^0\./, '') + '¢'
        } else {
          jq.find('sup').removeAttr('hidden')
          tmp = price.toPrecision(3)
        }

        jq.find('.sebco-value').text(tmp)
      }
    }

    static updateStaffCountBadges (endpoint, name) {
      var selector

      if (!lo.isString(name)) {
        throw new TypeError('`name` is not a string')
      }

      selector = '.sebco-staff-count-badge[data-name="' +
        name.trim().toLowerCase().replace('/[^a-z0-9]/g', '') +
        '"]'

      A.ajaxPromise({ url: endpoint })
        .then(function (o) {
          var n

          if (!lo.isObjectLike(o) || !Number.isFinite(o.response)) {
            return
          }

          n = o.response

          Array.from(d.querySelectorAll(selector))
            .filter(function (v) {
              return (n !== parseInt(v.dataset.value, 10))
            })
            .forEach(function (v) {
              v.innerText = v.dataset.value = n
            })
        })
        .fail(debug)
        .always(function () {
          A.waitPromise(60000)
            .then(function () {
              A.updateStaffCountBadges(endpoint, name)
            })
        })
    }

    static waitPromise (n) {
      var def = $.Deferred()
      var timerId

      timerId = w.setTimeout(
        function () {
          w.clearTimeout(timerId)
          timerId = null
          def.resolve()
        },
        Number.isFinite(n)
          ? n
          : 1000
      )

      return def.promise()
    }

    static windowResizeComplete (fn) {
      var def = $.Deferred()
      var timerId

      if (!lo.isFunction(fn)) {
        def.reject()
      } else {
        A.win.on('resize', function () {
          w.clearTimeout(timerId)
          timerId = null

          timerId = w.setTimeout(function () {
            w.clearTimeout(timerId)
            timerId = null
            fn()
            def.resolve()
          }, 400)
        })
      }

      return def.promise()
    }

    static zeroPad (n, length) {
      var buf = ''
      var len

      if (Number.isFinite(n)) {
        buf += n

        len = (Number.isFinite(length) && length > 0)
          ? length
          : 2

        while (buf.length < len) {
          buf = '0' + buf
        }
      }

      return buf
    }
  }

  /**
   * @const
   * @private
   * @static
   */
  A.CONNECTION_TYPE_BLE_ONLY = 1

  /**
   * @const
   * @private
   * @static
   */
  A.CONNECTION_TYPE_WCLOUD = 3

  /**
   * @const
   * @private
   * @static
   */
  A.CONNECTION_TYPE_WIFI_AND_BLE = 2

  /**
   * @const
   * @private
   * @static
   */
  A.CONNECTION_TYPE_WIFI_ONLY = 0

  /** @const */
  A.PAYMENT_TYPE_COIN_DROP_AND_DEBIT = 0

  /** @const */
  A.PAYMENT_TYPE_COIN_DROP_ONLY = 1

  /** @const */
  A.PAYMENT_TYPE_COIN_SLIDE_AND_DEBIT = 2

  /** @const */
  A.PAYMENT_TYPE_DEBIT_ONLY = 3

  /** @const */
  A.PAYMENT_TYPE_ENHANCED_DEBIT = 4

  A.animateParamsFadeOut = {
    opacity: 0
  }

  A.animateParamsSlideUp = {
    borderBottom: 0,
    borderTop: 0,
    height: 0,
    marginBottom: 0,
    marginTop: 0,
    minHeight: 0,
    paddingBottom: 0,
    paddingTop: 0
  }

  A.countBadgeEndpoints = {}
  A.kioskConnectionDegraded = 1
  A.kioskConnectionOffline = 0
  A.kioskConnectionOnline = 2
  A.postFinishPayDelay = 8000
  A.screenSaverDelay = 300000
  // A.screenSaverDelay = 10000
  A.screenSaverRotateDelay = 10000
  A.signoutUrl = null
  A.windowResizeEndTimer = null

  $(A.init)
}(window))

/*
 * Local Variables:
 * coding: utf-8-unix
 * indent-tabs-mode: nil
 * tab-width: 4
 * End:
 */
